// Credit plans: the Change plan dialog, the Billing page, the paused dialog and
// the personal limit notice (D74).
//
// Namespace `billing`; owner ui/billing. Plans have no names: a plan is the
// credits it adds every month. Never name what a credit costs Poneva or a
// margin. Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
// `{codeNote}` is empty or a leading space plus `codeNote`/`codeNoteUntil`, so
// it sits directly after `{price}`. In the timing texts `{credits}` is already
// `creditsCount` ("1,000 credits"); elsewhere it is a bare number.
// `{percent}` is a formatted percentage with its sign ("20%", "20 %").
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  // Change plan, step one
  dialogTitle: 'Change plan',
  dialogTitleNew: 'Choose a plan',
  dialogSub:
    'Every month your plan adds its credits to the balance your whole team shares. Unused credits roll over and never expire, and you can spend them while a plan is active.',
  close: 'Close',
  kickerUpgrade: 'Upgrade',
  kickerDowngrade: 'Downgrade',
  currentPlanTag: 'Current plan',
  currentPin: 'Current',
  creditsEveryMonth: 'credits every month',
  captionCurrent: 'Your current plan.',
  captionMore: '{count} more credits each month than your current plan.',
  captionFewer: '{count} fewer credits each month than your current plan.',
  captionRecent: 'Your team used {count} credits in the last 30 days.',
  scaleEnd: '{price} a month',
  sliderLabel: 'Plan',
  sliderValue: '{credits} credits a month, {price} excl. VAT',
  priceLabel: 'Price per month',
  exclVat: 'excl. VAT',
  exclVatForMonths: 'excl. VAT, for {months} months',
  exclVatForOneMonth: 'excl. VAT, for 1 month',
  exclVatUntil: 'excl. VAT, until {date}',
  perCreditLabel: 'Price per credit',
  volumeLabel: 'Volume discount',
  volumeNone: 'None',
  volumeNote: 'off the smallest plan’s {price} per credit',
  volumeBase: 'The smallest plan sets the base price per credit',
  vatLabel: 'Incl. 25% VAT',
  perMonth: 'per month',
  vatReverseLabel: 'VAT',
  vatReverseValue: 'Reverse charge',
  codeAdd: 'Add discount code',
  codePlaceholder: 'Discount code',
  codeApply: 'Apply',
  codeChecking: 'Checking…',
  codeNotFound: 'We couldn’t find that code. Check the spelling and try again.',
  codeRemove: 'Remove code',
  codeForMonths: '{percent} off for {months} months',
  codeForOneMonth: '{percent} off for 1 month',
  codeForGood: '{percent} off for good',
  codeUntil: '{percent} off until {date}',
  codeCarries: '{percent} off until {date}, carries over',
  codeCarriesForGood: '{percent} off for good, carries over',
  ctaSwitch: 'Switch to {credits} credits',
  ctaUseCode: 'Use code on this plan',
  ctaCurrent: 'Your current plan',
  ctaCheckout: 'Continue to payment',
  ctaSaving: 'Saving…',
  ctaOpening: 'Opening payment…',
  sideFoot: 'Billed monthly by card',
  // Change plan, step two
  back: 'All plans',
  timingTitle: 'When should the new plan start?',
  timingGroup: 'When the new plan starts',
  summaryInclVat: '{price} a month incl. VAT',
  summaryExclVat: '{price} a month excl. VAT',
  summaryOff: '{percent} off',
  nowTitle: 'Start now',
  renewalTitle: 'At renewal, {date}',
  today: 'Today',
  fromThenOn: 'From then on',
  onDate: 'On {date}',
  codeNote: '({percent} off)',
  codeNoteUntil: '({percent} off until {date})',
  nowToday: 'The new plan starts. You pay {price}{codeNote} and get {credits} right away.',
  nowTodayOnTop:
    'The new plan starts. You pay {price}{codeNote} and get {credits} right away, on top of the {balance} you have ({total} in total).',
  nowThen:
    '{credits} and {price} every month after that. Your current plan ends today and is not charged again.',
  nowThenUntil:
    '{credits} and {price} every month until {date}, then {fullPrice}. Your current plan ends today and is not charged again.',
  renewalToday:
    'Nothing changes. You keep your current plan and your {balance} credits, and pay nothing.',
  renewalTodayPaused:
    'Nothing changes. You keep your current plan and your {balance} credits, and pay nothing. Usage stays paused until then.',
  renewalThen: 'The new plan starts. You pay {price}{codeNote} and get {credits} then, not before.',
  footSe: 'Amounts include 25% VAT. We charge the card on your Billing page.',
  footUnknown: 'Amounts include 25% Swedish VAT. VAT is added at checkout where it applies.',
  footReverse:
    'Amounts exclude VAT, which is reverse charged. We charge the card on your Billing page.',
  cancel: 'Cancel',
  confirmNow: 'Pay {price} and switch',
  confirmRenewal: 'Schedule switch to {credits} credits',
  paying: 'Paying…',
  scheduling: 'Scheduling…',
  // Results and errors
  toastSwitched: 'Your new plan is active. {credits} credits were added to your balance.',
  toastScheduled: 'Your new plan starts on {date}. Nothing changes until then.',
  toastCode: '{percent} off applies from {date}, for {months} months.',
  toastCodeOneMonth: '{percent} off applies from {date}, for 1 month.',
  toastCodeForGood: '{percent} off applies from {date}.',
  errorDeclined:
    'Your card was declined. Update it under Payment method and invoices, then try again.',
  errorDiscountActive: 'A discount is already active. It carries over to the new plan.',
  errorComplimentary: 'Your plan is free with a code, so a new plan can only start at renewal.',
  errorCodeNotAccepted: 'That code can’t be used for this plan.',
  codeAlreadyUsed: 'Your company has already used this code.',
  errorGeneric: 'We couldn’t change the plan. Please try again.',
  errorEditing: 'Finish or cancel your earlier question edit before opening payment.',
  errorAttachments:
    'Your draft has attached files. Remove them before opening payment. Your question text is saved, and you can attach the files again afterwards.',
  errorStorage:
    'Your browser could not save your draft. Copy your question somewhere safe and allow session storage before opening payment.',
  planPerMonth: '{credits} credits / month',
  creditsCount: '{count} credits',
  // Billing page
  billingTitle: 'Billing',
  billingIntro: 'Your company’s plan, credits and payment.',
  planLabel: 'Plan',
  noPlan: 'No plan',
  planPaymentFailed: 'Payment failed',
  planEndedOn: 'Ended on {date}',
  planEnded: 'Ended',
  codeNotPercent: 'This code takes an amount off. Only percentage codes work here.',
  balanceLabel: 'Credits left',
  nextLabel: 'Next',
  renewsWith: '+{credits} credits on {date}',
  switchesTo: 'Switches to {credits} credits / month on {date}',
  endsOn: 'Ends on {date}',
  discountLabel: 'Discount',
  creditsNeverExpire: 'Credits never expire.',
  paymentFailed:
    'Your last payment didn’t go through. Update the card under Payment method and invoices.',
  seeUsage: 'See usage',
  changePlan: 'Change plan',
  choosePlan: 'Choose a plan',
  portal: 'Payment method and invoices',
  opening: 'Opening…',
  readOnly: 'The company owner manages the plan and payment.',
  nativeOnly: 'Plans and payment are managed on the web.',
  loading: 'Loading billing…',
  retry: 'Try again',
  unavailable:
    'Billing is temporarily unavailable. Please try again. Your history and settings are still available.',
  portalError: 'Payment settings could not be opened. Please try again.',
  returnPending:
    'Waiting for payment confirmation. Your credits appear once the payment is confirmed.',
  returnDelayed: 'Payment confirmation is taking longer than expected. Check again shortly.',
  returnCanceled: 'Checkout was cancelled. Nothing was charged.',
  // Paused dialog
  pausedPlanTitle: 'Choose a plan to continue',
  pausedPlanOwner: 'Poneva runs on monthly credit plans. Pick a plan to keep asking questions.',
  pausedPlanMember:
    'Your company has no active plan. The company owner chooses one in Settings under Usage.',
  pausedPastDue:
    'Your last payment didn’t go through, so the plan is paused. Update the card under Payment method and invoices.',
  pausedPastDueMember:
    'The company’s last payment didn’t go through, so the plan is paused. The company owner can update the card.',
  pausedCreditsTitle: 'You’ve used all your credits',
  pausedCreditsBody:
    'Upgrade now, or wait until {date} at {time} when your plan adds {credits} credits.',
  pausedCreditsMember:
    'Ask the company owner to upgrade, or wait until {date} at {time} when your plan adds {credits} credits.',
  pausedCreditsNoRenewal: 'Choose a larger plan to continue.',
  pausedLimitTitle: 'You’ve reached your monthly limit',
  pausedLimitBody:
    'You have used your monthly limit of {limit} credits. It resets on {date} or when the company owner raises it.',
  pausedLimitNoDate:
    'You have used your monthly limit of {limit} credits. The company owner can raise it.',
  upgradeNow: 'Upgrade now',
  // Personal limit notice (80%)
  limitNotice: '{left} of your {limit} monthly credits left. Your limit resets on {date}.',
  limitNoticeNoDate: '{left} of your {limit} monthly credits left.',
};
export type BillingCopyKey = keyof typeof en;
type Copy = Record<BillingCopyKey, string>;

const sv: Copy = {
  dialogTitle: 'Byt plan',
  dialogTitleNew: 'Välj en plan',
  dialogSub:
    'Varje månad lägger planen till sina krediter i saldot som hela teamet delar. Krediter som inte används sparas och förfaller aldrig, och du kan använda dem så länge en plan är aktiv.',
  close: 'Stäng',
  kickerUpgrade: 'Uppgradering',
  kickerDowngrade: 'Nedgradering',
  currentPlanTag: 'Nuvarande plan',
  currentPin: 'Nuvarande',
  creditsEveryMonth: 'krediter varje månad',
  captionCurrent: 'Din nuvarande plan.',
  captionMore: '{count} fler krediter per månad än din nuvarande plan.',
  captionFewer: '{count} färre krediter per månad än din nuvarande plan.',
  captionRecent: 'Ditt team använde {count} krediter de senaste 30 dagarna.',
  scaleEnd: '{price} i månaden',
  sliderLabel: 'Plan',
  sliderValue: '{credits} krediter i månaden, {price} exkl. moms',
  priceLabel: 'Pris per månad',
  exclVat: 'exkl. moms',
  exclVatForMonths: 'exkl. moms, i {months} månader',
  exclVatForOneMonth: 'exkl. moms, i 1 månad',
  exclVatUntil: 'exkl. moms, till {date}',
  perCreditLabel: 'Pris per kredit',
  volumeLabel: 'Volymrabatt',
  volumeNone: 'Ingen',
  volumeNote: 'jämfört med den minsta planens {price} per kredit',
  volumeBase: 'Den minsta planen anger grundpriset per kredit',
  vatLabel: 'Inkl. 25 % moms',
  perMonth: 'per månad',
  vatReverseLabel: 'Moms',
  vatReverseValue: 'Omvänd skattskyldighet',
  codeAdd: 'Lägg till rabattkod',
  codePlaceholder: 'Rabattkod',
  codeApply: 'Använd',
  codeChecking: 'Kontrollerar…',
  codeNotFound: 'Vi hittade inte den koden. Kontrollera stavningen och försök igen.',
  codeRemove: 'Ta bort koden',
  codeForMonths: '{percent} rabatt i {months} månader',
  codeForOneMonth: '{percent} rabatt i 1 månad',
  codeForGood: '{percent} rabatt för alltid',
  codeUntil: '{percent} rabatt till {date}',
  codeCarries: '{percent} rabatt till {date}, följer med',
  codeCarriesForGood: '{percent} rabatt för alltid, följer med',
  ctaSwitch: 'Byt till {credits} krediter',
  ctaUseCode: 'Använd koden på den här planen',
  ctaCurrent: 'Din nuvarande plan',
  ctaCheckout: 'Gå vidare till betalning',
  ctaSaving: 'Sparar…',
  ctaOpening: 'Öppnar betalningen…',
  sideFoot: 'Faktureras månadsvis med kort',
  back: 'Alla planer',
  timingTitle: 'När ska den nya planen börja?',
  timingGroup: 'När den nya planen börjar',
  summaryInclVat: '{price} i månaden inkl. moms',
  summaryExclVat: '{price} i månaden exkl. moms',
  summaryOff: '{percent} rabatt',
  nowTitle: 'Börja nu',
  renewalTitle: 'Vid förnyelsen, {date}',
  today: 'I dag',
  fromThenOn: 'Därefter',
  onDate: 'Den {date}',
  codeNote: '({percent} rabatt)',
  codeNoteUntil: '({percent} rabatt till {date})',
  nowToday: 'Den nya planen börjar. Du betalar {price}{codeNote} och får {credits} direkt.',
  nowTodayOnTop:
    'Den nya planen börjar. Du betalar {price}{codeNote} och får {credits} direkt, utöver de {balance} du redan har ({total} totalt).',
  nowThen:
    '{credits} och {price} varje månad därefter. Din nuvarande plan slutar i dag och debiteras inte igen.',
  nowThenUntil:
    '{credits} och {price} varje månad till {date}, sedan {fullPrice}. Din nuvarande plan slutar i dag och debiteras inte igen.',
  renewalToday:
    'Inget ändras. Du behåller din nuvarande plan och dina {balance} krediter och betalar ingenting.',
  renewalTodayPaused:
    'Inget ändras. Du behåller din nuvarande plan och dina {balance} krediter och betalar ingenting. Användningen är pausad till dess.',
  renewalThen:
    'Den nya planen börjar. Du betalar {price}{codeNote} och får {credits} då, inte tidigare.',
  footSe: 'Beloppen är inklusive 25 % moms. Vi debiterar kortet som anges på faktureringssidan.',
  footUnknown: 'Beloppen är inklusive 25 % svensk moms. Moms läggs till i kassan där den gäller.',
  footReverse:
    'Beloppen är exklusive moms, som redovisas med omvänd skattskyldighet. Vi debiterar kortet som anges på faktureringssidan.',
  cancel: 'Avbryt',
  confirmNow: 'Betala {price} och byt',
  confirmRenewal: 'Schemalägg byte till {credits} krediter',
  paying: 'Betalar…',
  scheduling: 'Schemalägger…',
  toastSwitched: 'Din nya plan är aktiv. {credits} krediter har lagts till i saldot.',
  toastScheduled: 'Din nya plan börjar den {date}. Inget ändras innan dess.',
  toastCode: '{percent} rabatt gäller från {date}, i {months} månader.',
  toastCodeOneMonth: '{percent} rabatt gäller från {date}, i 1 månad.',
  toastCodeForGood: '{percent} rabatt gäller från {date}.',
  errorDeclined:
    'Kortet nekades. Uppdatera det under Betalningsmetod och fakturor och försök sedan igen.',
  errorDiscountActive: 'En rabatt är redan aktiv. Den följer med till den nya planen.',
  errorComplimentary:
    'Planen är gratis tack vare en kod, så en ny plan kan bara börja vid förnyelsen.',
  errorCodeNotAccepted: 'Den koden kan inte användas för den här planen.',
  codeAlreadyUsed: 'Ditt företag har redan använt den här koden.',
  errorGeneric: 'Vi kunde inte byta plan. Försök igen.',
  errorEditing:
    'Avsluta eller avbryt redigeringen av din tidigare fråga innan du öppnar betalningen.',
  errorAttachments:
    'Ditt utkast har bifogade filer. Ta bort dem innan du öppnar betalningen. Frågetexten sparas och du kan bifoga filerna igen efteråt.',
  errorStorage:
    'Din webbläsare kunde inte spara utkastet. Kopiera frågan till en säker plats och tillåt sessionslagring innan du öppnar betalningen.',
  planPerMonth: '{credits} krediter/månad',
  creditsCount: '{count} krediter',
  billingTitle: 'Fakturering',
  billingIntro: 'Företagets plan, krediter och betalning.',
  planLabel: 'Plan',
  noPlan: 'Ingen plan',
  planPaymentFailed: 'Betalningen misslyckades',
  planEndedOn: 'Slutade den {date}',
  planEnded: 'Avslutad',
  codeNotPercent: 'Den här koden drar av ett belopp. Bara procentkoder fungerar här.',
  balanceLabel: 'Krediter kvar',
  nextLabel: 'Nästa',
  renewsWith: '+{credits} krediter den {date}',
  switchesTo: 'Byter till {credits} krediter/månad den {date}',
  endsOn: 'Slutar den {date}',
  discountLabel: 'Rabatt',
  creditsNeverExpire: 'Krediterna förfaller aldrig.',
  paymentFailed:
    'Den senaste betalningen gick inte igenom. Uppdatera kortet under Betalningsmetod och fakturor.',
  seeUsage: 'Visa användning',
  changePlan: 'Byt plan',
  choosePlan: 'Välj en plan',
  portal: 'Betalningsmetod och fakturor',
  opening: 'Öppnar…',
  readOnly: 'Företagets ägare hanterar planen och betalningen.',
  nativeOnly: 'Planer och betalning hanteras på webben.',
  loading: 'Läser in faktureringen…',
  retry: 'Försök igen',
  unavailable:
    'Faktureringen är tillfälligt otillgänglig. Försök igen. Historik och inställningar är fortfarande tillgängliga.',
  portalError: 'Betalningsinställningarna kunde inte öppnas. Försök igen.',
  returnPending:
    'Väntar på att betalningen bekräftas. Krediterna visas när betalningen är bekräftad.',
  returnDelayed: 'Betalningsbekräftelsen tar längre tid än väntat. Kontrollera igen om en stund.',
  returnCanceled: 'Betalningen avbröts. Inget har debiterats.',
  pausedPlanTitle: 'Välj en plan för att fortsätta',
  pausedPlanOwner:
    'Poneva använder månadsplaner med krediter. Välj en plan för att fortsätta ställa frågor.',
  pausedPlanMember:
    'Företaget har ingen aktiv plan. Företagets ägare väljer en i Inställningar under Användning.',
  pausedPastDue:
    'Den senaste betalningen gick inte igenom, så planen är pausad. Uppdatera kortet under Betalningsmetod och fakturor.',
  pausedPastDueMember:
    'Företagets senaste betalning gick inte igenom, så planen är pausad. Företagets ägare kan uppdatera kortet.',
  pausedCreditsTitle: 'Du har använt alla krediter',
  pausedCreditsBody:
    'Uppgradera nu, eller vänta till {date} kl. {time} när planen lägger till {credits} krediter.',
  pausedCreditsMember:
    'Be företagets ägare att uppgradera, eller vänta till {date} kl. {time} när planen lägger till {credits} krediter.',
  pausedCreditsNoRenewal: 'Välj en större plan för att fortsätta.',
  pausedLimitTitle: 'Du har nått din månadsgräns',
  pausedLimitBody:
    'Du har använt din månadsgräns på {limit} krediter. Den nollställs den {date} eller när företagets ägare höjer den.',
  pausedLimitNoDate:
    'Du har använt din månadsgräns på {limit} krediter. Företagets ägare kan höja den.',
  upgradeNow: 'Uppgradera nu',
  limitNotice:
    '{left} av dina {limit} krediter för månaden är kvar. Gränsen nollställs den {date}.',
  limitNoticeNoDate: '{left} av dina {limit} krediter för månaden är kvar.',
};

const da: Copy = {
  dialogTitle: 'Skift plan',
  dialogTitleNew: 'Vælg en plan',
  dialogSub:
    'Hver måned lægger din plan sine kreditter til den saldo, som hele dit team deler. Ubrugte kreditter overføres og udløber aldrig, og du kan bruge dem, så længe en plan er aktiv.',
  close: 'Luk',
  kickerUpgrade: 'Opgradering',
  kickerDowngrade: 'Nedgradering',
  currentPlanTag: 'Nuværende plan',
  currentPin: 'Nuværende',
  creditsEveryMonth: 'kreditter hver måned',
  captionCurrent: 'Din nuværende plan.',
  captionMore: '{count} flere kreditter om måneden end din nuværende plan.',
  captionFewer: '{count} færre kreditter om måneden end din nuværende plan.',
  captionRecent: 'Dit team brugte {count} kreditter de seneste 30 dage.',
  scaleEnd: '{price} om måneden',
  sliderLabel: 'Plan',
  sliderValue: '{credits} kreditter om måneden, {price} ekskl. moms',
  priceLabel: 'Pris pr. måned',
  exclVat: 'ekskl. moms',
  exclVatForMonths: 'ekskl. moms, i {months} måneder',
  exclVatForOneMonth: 'ekskl. moms, i 1 måned',
  exclVatUntil: 'ekskl. moms, indtil {date}',
  perCreditLabel: 'Pris pr. kredit',
  volumeLabel: 'Mængderabat',
  volumeNone: 'Ingen',
  volumeNote: 'i forhold til den mindste plans {price} pr. kredit',
  volumeBase: 'Den mindste plan fastsætter grundprisen pr. kredit',
  vatLabel: 'Inkl. 25 % moms',
  perMonth: 'om måneden',
  vatReverseLabel: 'Moms',
  vatReverseValue: 'Omvendt betalingspligt',
  codeAdd: 'Tilføj rabatkode',
  codePlaceholder: 'Rabatkode',
  codeApply: 'Anvend',
  codeChecking: 'Kontrollerer…',
  codeNotFound: 'Vi kunne ikke finde den kode. Tjek stavningen, og prøv igen.',
  codeRemove: 'Fjern kode',
  codeForMonths: '{percent} rabat i {months} måneder',
  codeForOneMonth: '{percent} rabat i 1 måned',
  codeForGood: '{percent} rabat permanent',
  codeUntil: '{percent} rabat indtil {date}',
  codeCarries: '{percent} rabat indtil {date}, overføres',
  codeCarriesForGood: '{percent} rabat permanent, overføres',
  ctaSwitch: 'Skift til {credits} kreditter',
  ctaUseCode: 'Brug koden på denne plan',
  ctaCurrent: 'Din nuværende plan',
  ctaCheckout: 'Fortsæt til betaling',
  ctaSaving: 'Gemmer…',
  ctaOpening: 'Åbner betaling…',
  sideFoot: 'Faktureres månedligt via kort',
  back: 'Alle planer',
  timingTitle: 'Hvornår skal den nye plan starte?',
  timingGroup: 'Hvornår den nye plan starter',
  summaryInclVat: '{price} om måneden inkl. moms',
  summaryExclVat: '{price} om måneden ekskl. moms',
  summaryOff: '{percent} rabat',
  nowTitle: 'Start nu',
  renewalTitle: 'Ved fornyelse, {date}',
  today: 'I dag',
  fromThenOn: 'Derefter',
  onDate: 'Den {date}',
  codeNote: '({percent} rabat)',
  codeNoteUntil: '({percent} rabat indtil {date})',
  nowToday: 'Den nye plan starter. Du betaler {price}{codeNote} og får {credits} med det samme.',
  nowTodayOnTop:
    'Den nye plan starter. Du betaler {price}{codeNote} og får {credits} med det samme, oven i de {balance}, du har ({total} i alt).',
  nowThen:
    '{credits} og {price} hver måned efterfølgende. Din nuværende plan slutter i dag og opkræves ikke igen.',
  nowThenUntil:
    '{credits} og {price} hver måned indtil {date}, derefter {fullPrice}. Din nuværende plan slutter i dag og opkræves ikke igen.',
  renewalToday:
    'Intet ændres. Du beholder din nuværende plan og dine {balance} kreditter og betaler ingenting.',
  renewalTodayPaused:
    'Intet ændres. Du beholder din nuværende plan og dine {balance} kreditter og betaler ingenting. Forbruget er sat på pause indtil da.',
  renewalThen:
    'Den nye plan starter. Du betaler {price}{codeNote} og får {credits} på det tidspunkt, ikke før.',
  footSe:
    'Beløbene er inkl. 25 % moms. Vi opkræver betalingen på det kort, der står på din faktureringsside.',
  footUnknown:
    'Beløbene er inkl. 25 % svensk moms. Moms lægges til ved betalingen, hvor det er relevant.',
  footReverse:
    'Beløbene er ekskl. moms, som afregnes efter reglerne om omvendt betalingspligt. Vi opkræver betalingen på det kort, der står på din faktureringsside.',
  cancel: 'Annuller',
  confirmNow: 'Betal {price} og skift',
  confirmRenewal: 'Planlæg skift til {credits} kreditter',
  paying: 'Betaler…',
  scheduling: 'Planlægger…',
  toastSwitched: 'Din nye plan er aktiv. {credits} kreditter er lagt til din saldo.',
  toastScheduled: 'Din nye plan starter den {date}. Indtil da ændres intet.',
  toastCode: '{percent} rabat gælder fra {date} i {months} måneder.',
  toastCodeOneMonth: '{percent} rabat gælder fra {date} i 1 måned.',
  toastCodeForGood: '{percent} rabat gælder fra {date}.',
  errorDeclined:
    'Dit kort blev afvist. Opdater det under Betalingsmetode og fakturaer, og prøv igen.',
  errorDiscountActive: 'Der er allerede en aktiv rabat. Den overføres til den nye plan.',
  errorComplimentary:
    'Din plan er gratis takket være en kode, så en ny plan kan kun starte ved fornyelse.',
  errorCodeNotAccepted: 'Den kode kan ikke bruges til denne plan.',
  codeAlreadyUsed: 'Din virksomhed har allerede brugt denne kode.',
  errorGeneric: 'Vi kunne ikke skifte plan. Prøv igen.',
  errorEditing:
    'Afslut eller annuller redigeringen af dit tidligere spørgsmål, før du åbner betalingen.',
  errorAttachments:
    'Dit udkast har vedhæftede filer. Fjern dem, før du åbner betalingen. Teksten i dit spørgsmål er gemt, og du kan vedhæfte filerne igen bagefter.',
  errorStorage:
    'Din browser kunne ikke gemme dit udkast. Kopiér dit spørgsmål til et sikkert sted, og tillad sessionslagring, før du åbner betalingen.',
  planPerMonth: '{credits} kreditter/måned',
  creditsCount: '{count} kreditter',
  billingTitle: 'Fakturering',
  billingIntro: 'Din virksomheds plan, kreditter og betaling.',
  planLabel: 'Plan',
  noPlan: 'Ingen plan',
  planPaymentFailed: 'Betalingen mislykkedes',
  planEndedOn: 'Sluttede den {date}',
  planEnded: 'Afsluttet',
  codeNotPercent: 'Denne kode trækker et fast beløb fra. Kun procentkoder virker her.',
  balanceLabel: 'Kreditter tilbage',
  nextLabel: 'Næste',
  renewsWith: '+{credits} kreditter den {date}',
  switchesTo: 'Skifter til {credits} kreditter/måned den {date}',
  endsOn: 'Slutter den {date}',
  discountLabel: 'Rabat',
  creditsNeverExpire: 'Kreditter udløber aldrig.',
  paymentFailed:
    'Din seneste betaling gik ikke igennem. Opdater kortet under Betalingsmetode og fakturaer.',
  seeUsage: 'Se forbrug',
  changePlan: 'Skift plan',
  choosePlan: 'Vælg en plan',
  portal: 'Betalingsmetode og fakturaer',
  opening: 'Åbner…',
  readOnly: 'Virksomhedens ejer administrerer planen og betalingen.',
  nativeOnly: 'Planer og betaling administreres på nettet.',
  loading: 'Indlæser fakturering…',
  retry: 'Prøv igen',
  unavailable:
    'Fakturering er midlertidigt utilgængelig. Prøv igen. Din historik og dine indstillinger er stadig tilgængelige.',
  portalError: 'Betalingsindstillingerne kunne ikke åbnes. Prøv igen.',
  returnPending:
    'Venter på bekræftelse af betalingen. Dine kreditter vises, når betalingen er bekræftet.',
  returnDelayed: 'Bekræftelsen af betalingen tager længere tid end forventet. Tjek igen om lidt.',
  returnCanceled: 'Betalingen blev annulleret. Der er ikke trukket noget.',
  pausedPlanTitle: 'Vælg en plan for at fortsætte',
  pausedPlanOwner:
    'Poneva kører på månedlige kreditplaner. Vælg en plan for at blive ved med at stille spørgsmål.',
  pausedPlanMember:
    'Din virksomhed har ingen aktiv plan. Virksomhedens ejer vælger en i Indstillinger under Forbrug.',
  pausedPastDue:
    'Din seneste betaling gik ikke igennem, så planen er sat på pause. Opdater kortet under Betalingsmetode og fakturaer.',
  pausedPastDueMember:
    'Virksomhedens seneste betaling gik ikke igennem, så planen er sat på pause. Virksomhedens ejer kan opdatere kortet.',
  pausedCreditsTitle: 'Du har brugt alle dine kreditter',
  pausedCreditsBody:
    'Opgrader nu, eller vent til {date} kl. {time}, hvor din plan tilføjer {credits} kreditter.',
  pausedCreditsMember:
    'Bed virksomhedens ejer om at opgradere, eller vent til {date} kl. {time}, hvor din plan tilføjer {credits} kreditter.',
  pausedCreditsNoRenewal: 'Vælg en større plan for at fortsætte.',
  pausedLimitTitle: 'Du har nået din månedlige grænse',
  pausedLimitBody:
    'Du har brugt din månedlige grænse på {limit} kreditter. Den nulstilles den {date}, eller når virksomhedens ejer hæver den.',
  pausedLimitNoDate:
    'Du har brugt din månedlige grænse på {limit} kreditter. Virksomhedens ejer kan hæve den.',
  upgradeNow: 'Opgrader nu',
  limitNotice:
    '{left} af dine {limit} månedlige kreditter tilbage. Din grænse nulstilles den {date}.',
  limitNoticeNoDate: '{left} af dine {limit} månedlige kreditter tilbage.',
};

const de: Copy = {
  dialogTitle: 'Tarif ändern',
  dialogTitleNew: 'Tarif wählen',
  dialogSub:
    'Jeden Monat schreibt Ihr Tarif seine Credits dem Guthaben gut, das Ihr ganzes Team teilt. Nicht genutzte Credits werden übertragen und verfallen nie, und Sie können sie nutzen, solange ein Tarif aktiv ist.',
  close: 'Schließen',
  kickerUpgrade: 'Upgrade',
  kickerDowngrade: 'Downgrade',
  currentPlanTag: 'Aktueller Tarif',
  currentPin: 'Aktuell',
  creditsEveryMonth: 'Credits pro Monat',
  captionCurrent: 'Ihr aktueller Tarif.',
  captionMore: '{count} Credits mehr pro Monat als Ihr aktueller Tarif.',
  captionFewer: '{count} Credits weniger pro Monat als Ihr aktueller Tarif.',
  captionRecent: 'Ihr Team hat in den letzten 30 Tagen {count} Credits genutzt.',
  scaleEnd: '{price} pro Monat',
  sliderLabel: 'Tarif',
  sliderValue: '{credits} Credits pro Monat, {price} zzgl. MwSt.',
  priceLabel: 'Preis pro Monat',
  exclVat: 'zzgl. MwSt.',
  exclVatForMonths: 'zzgl. MwSt., für {months} Monate',
  exclVatForOneMonth: 'zzgl. MwSt., für 1 Monat',
  exclVatUntil: 'zzgl. MwSt., bis {date}',
  perCreditLabel: 'Preis pro Credit',
  volumeLabel: 'Mengenrabatt',
  volumeNone: 'Keiner',
  volumeNote: 'gegenüber {price} pro Credit im kleinsten Tarif',
  volumeBase: 'Der kleinste Tarif bestimmt den Grundpreis pro Credit',
  vatLabel: 'Inkl. 25 % MwSt.',
  perMonth: 'pro Monat',
  vatReverseLabel: 'MwSt.',
  vatReverseValue: 'Reverse-Charge-Verfahren',
  codeAdd: 'Rabattcode hinzufügen',
  codePlaceholder: 'Rabattcode',
  codeApply: 'Anwenden',
  codeChecking: 'Wird geprüft…',
  codeNotFound:
    'Dieser Code wurde nicht gefunden. Prüfen Sie die Schreibweise und versuchen Sie es erneut.',
  codeRemove: 'Code entfernen',
  codeForMonths: '{percent} Rabatt für {months} Monate',
  codeForOneMonth: '{percent} Rabatt für 1 Monat',
  codeForGood: '{percent} Rabatt dauerhaft',
  codeUntil: '{percent} Rabatt bis {date}',
  codeCarries: '{percent} Rabatt bis {date}, wird übernommen',
  codeCarriesForGood: '{percent} Rabatt dauerhaft, wird übernommen',
  ctaSwitch: 'Zu {credits} Credits wechseln',
  ctaUseCode: 'Code für diesen Tarif nutzen',
  ctaCurrent: 'Ihr aktueller Tarif',
  ctaCheckout: 'Weiter zur Zahlung',
  ctaSaving: 'Wird gespeichert…',
  ctaOpening: 'Zahlung wird geöffnet…',
  sideFoot: 'Monatliche Abrechnung per Karte',
  back: 'Alle Tarife',
  timingTitle: 'Wann soll der neue Tarif beginnen?',
  timingGroup: 'Beginn des neuen Tarifs',
  summaryInclVat: '{price} pro Monat inkl. MwSt.',
  summaryExclVat: '{price} pro Monat zzgl. MwSt.',
  summaryOff: '{percent} Rabatt',
  nowTitle: 'Jetzt starten',
  renewalTitle: 'Bei Verlängerung, {date}',
  today: 'Heute',
  fromThenOn: 'Danach',
  onDate: 'Am {date}',
  codeNote: '({percent} Rabatt)',
  codeNoteUntil: '({percent} Rabatt bis {date})',
  nowToday: 'Der neue Tarif beginnt. Sie zahlen {price}{codeNote} und erhalten sofort {credits}.',
  nowTodayOnTop:
    'Der neue Tarif beginnt. Sie zahlen {price}{codeNote} und erhalten sofort {credits}, zusätzlich zu Ihren {balance} ({total} insgesamt).',
  nowThen:
    '{credits} für {price} in jedem weiteren Monat. Ihr aktueller Tarif endet heute und wird nicht erneut berechnet.',
  nowThenUntil:
    '{credits} für {price} in jedem weiteren Monat bis {date}, danach {fullPrice}. Ihr aktueller Tarif endet heute und wird nicht erneut berechnet.',
  renewalToday:
    'Es ändert sich nichts. Sie behalten Ihren aktuellen Tarif und Ihre {balance} Credits und zahlen nichts.',
  renewalTodayPaused:
    'Es ändert sich nichts. Sie behalten Ihren aktuellen Tarif und Ihre {balance} Credits und zahlen nichts. Die Nutzung bleibt bis dahin pausiert.',
  renewalThen:
    'Der neue Tarif beginnt. Sie zahlen {price}{codeNote} und erhalten dann {credits}, nicht früher.',
  footSe:
    'Beträge inkl. 25 % MwSt. Wir belasten die Karte, die auf Ihrer Abrechnungsseite hinterlegt ist.',
  footUnknown:
    'Beträge inkl. 25 % schwedischer MwSt. Die MwSt. wird beim Bezahlen hinzugefügt, wo sie anfällt.',
  footReverse:
    'Beträge zzgl. MwSt., die im Reverse-Charge-Verfahren abgerechnet wird. Wir belasten die Karte, die auf Ihrer Abrechnungsseite hinterlegt ist.',
  cancel: 'Abbrechen',
  confirmNow: '{price} zahlen und wechseln',
  confirmRenewal: 'Wechsel zu {credits} Credits planen',
  paying: 'Wird bezahlt…',
  scheduling: 'Wird geplant…',
  toastSwitched:
    'Ihr neuer Tarif ist aktiv. {credits} Credits wurden Ihrem Guthaben gutgeschrieben.',
  toastScheduled: 'Ihr neuer Tarif beginnt am {date}. Bis dahin ändert sich nichts.',
  toastCode: '{percent} Rabatt gilt ab {date} für {months} Monate.',
  toastCodeOneMonth: '{percent} Rabatt gilt ab {date} für 1 Monat.',
  toastCodeForGood: '{percent} Rabatt gilt ab {date}.',
  errorDeclined:
    'Ihre Karte wurde abgelehnt. Aktualisieren Sie sie unter Zahlungsmethode und Rechnungen und versuchen Sie es erneut.',
  errorDiscountActive: 'Es ist bereits ein Rabatt aktiv. Er wird in den neuen Tarif übernommen.',
  errorComplimentary:
    'Ihr Tarif ist dank eines Codes kostenlos, daher kann ein neuer Tarif erst bei der Verlängerung beginnen.',
  errorCodeNotAccepted: 'Dieser Code kann für diesen Tarif nicht verwendet werden.',
  codeAlreadyUsed: 'Ihr Unternehmen hat diesen Code bereits verwendet.',
  errorGeneric: 'Der Tarif konnte nicht geändert werden. Bitte versuchen Sie es erneut.',
  errorEditing:
    'Schließen Sie die Bearbeitung Ihrer früheren Frage ab oder brechen Sie sie ab, bevor Sie die Zahlung öffnen.',
  errorAttachments:
    'Ihr Entwurf enthält angehängte Dateien. Entfernen Sie sie, bevor Sie die Zahlung öffnen. Der Text Ihrer Frage bleibt gespeichert, und Sie können die Dateien danach erneut anhängen.',
  errorStorage:
    'Ihr Browser konnte den Entwurf nicht speichern. Kopieren Sie Ihre Frage an einen sicheren Ort und erlauben Sie den Sitzungsspeicher, bevor Sie die Zahlung öffnen.',
  planPerMonth: '{credits} Credits / Monat',
  creditsCount: '{count} Credits',
  billingTitle: 'Abrechnung',
  billingIntro: 'Tarif, Credits und Zahlung Ihres Unternehmens.',
  planLabel: 'Tarif',
  noPlan: 'Kein Tarif',
  planPaymentFailed: 'Zahlung fehlgeschlagen',
  planEndedOn: 'Endete am {date}',
  planEnded: 'Beendet',
  codeNotPercent: 'Dieser Code zieht einen festen Betrag ab. Hier funktionieren nur Prozentcodes.',
  balanceLabel: 'Verbleibende Credits',
  nextLabel: 'Als Nächstes',
  renewsWith: '+{credits} Credits am {date}',
  switchesTo: 'Wechsel zu {credits} Credits / Monat am {date}',
  endsOn: 'Endet am {date}',
  discountLabel: 'Rabatt',
  creditsNeverExpire: 'Credits verfallen nie.',
  paymentFailed:
    'Ihre letzte Zahlung ist fehlgeschlagen. Aktualisieren Sie die Karte unter Zahlungsmethode und Rechnungen.',
  seeUsage: 'Nutzung ansehen',
  changePlan: 'Tarif ändern',
  choosePlan: 'Tarif wählen',
  portal: 'Zahlungsmethode und Rechnungen',
  opening: 'Wird geöffnet…',
  readOnly: 'Der Inhaber des Unternehmens verwaltet Tarif und Zahlung.',
  nativeOnly: 'Tarife und Zahlung werden im Web verwaltet.',
  loading: 'Abrechnung wird geladen…',
  retry: 'Erneut versuchen',
  unavailable:
    'Die Abrechnung ist vorübergehend nicht verfügbar. Bitte versuchen Sie es erneut. Ihr Verlauf und Ihre Einstellungen sind weiterhin verfügbar.',
  portalError:
    'Die Zahlungseinstellungen konnten nicht geöffnet werden. Bitte versuchen Sie es erneut.',
  returnPending:
    'Warten auf die Zahlungsbestätigung. Ihre Credits erscheinen, sobald die Zahlung bestätigt ist.',
  returnDelayed:
    'Die Zahlungsbestätigung dauert länger als erwartet. Sehen Sie bitte gleich noch einmal nach.',
  returnCanceled: 'Der Bezahlvorgang wurde abgebrochen. Es wurde nichts berechnet.',
  pausedPlanTitle: 'Wählen Sie einen Tarif, um fortzufahren',
  pausedPlanOwner:
    'Poneva funktioniert mit monatlichen Credit-Tarifen. Wählen Sie einen Tarif, um weiter Fragen zu stellen.',
  pausedPlanMember:
    'Ihr Unternehmen hat keinen aktiven Tarif. Der Inhaber des Unternehmens wählt einen in den Einstellungen unter Nutzung.',
  pausedPastDue:
    'Ihre letzte Zahlung ist fehlgeschlagen, daher ist der Tarif pausiert. Aktualisieren Sie die Karte unter Zahlungsmethode und Rechnungen.',
  pausedPastDueMember:
    'Die letzte Zahlung des Unternehmens ist fehlgeschlagen, daher ist der Tarif pausiert. Der Inhaber des Unternehmens kann die Karte aktualisieren.',
  pausedCreditsTitle: 'Sie haben alle Credits aufgebraucht',
  pausedCreditsBody:
    'Führen Sie jetzt ein Upgrade durch oder warten Sie bis {date} um {time}, wenn Ihr Tarif {credits} Credits hinzufügt.',
  pausedCreditsMember:
    'Bitten Sie den Inhaber des Unternehmens um ein Upgrade oder warten Sie bis {date} um {time}, wenn Ihr Tarif {credits} Credits hinzufügt.',
  pausedCreditsNoRenewal: 'Wählen Sie einen größeren Tarif, um fortzufahren.',
  pausedLimitTitle: 'Sie haben Ihr Monatslimit erreicht',
  pausedLimitBody:
    'Sie haben Ihr Monatslimit von {limit} Credits aufgebraucht. Es wird am {date} zurückgesetzt oder wenn der Inhaber des Unternehmens es erhöht.',
  pausedLimitNoDate:
    'Sie haben Ihr Monatslimit von {limit} Credits aufgebraucht. Der Inhaber des Unternehmens kann es erhöhen.',
  upgradeNow: 'Jetzt upgraden',
  limitNotice:
    'Noch {left} von Ihren {limit} monatlichen Credits übrig. Ihr Limit wird am {date} zurückgesetzt.',
  limitNoticeNoDate: 'Noch {left} von Ihren {limit} monatlichen Credits übrig.',
};

const nl: Copy = {
  dialogTitle: 'Abonnement wijzigen',
  dialogTitleNew: 'Kies een abonnement',
  dialogSub:
    'Elke maand voegt je abonnement zijn credits toe aan het saldo dat je hele team deelt. Ongebruikte credits worden meegenomen en verlopen nooit, en je kunt ze gebruiken zolang er een abonnement actief is.',
  close: 'Sluiten',
  kickerUpgrade: 'Upgrade',
  kickerDowngrade: 'Downgrade',
  currentPlanTag: 'Huidig abonnement',
  currentPin: 'Huidig',
  creditsEveryMonth: 'credits per maand',
  captionCurrent: 'Je huidige abonnement.',
  captionMore: '{count} credits per maand meer dan je huidige abonnement.',
  captionFewer: '{count} credits per maand minder dan je huidige abonnement.',
  captionRecent: 'Je team heeft de afgelopen 30 dagen {count} credits gebruikt.',
  scaleEnd: '{price} per maand',
  sliderLabel: 'Abonnement',
  sliderValue: '{credits} credits per maand, {price} excl. btw',
  priceLabel: 'Prijs per maand',
  exclVat: 'excl. btw',
  exclVatForMonths: 'excl. btw, gedurende {months} maanden',
  exclVatForOneMonth: 'excl. btw, gedurende 1 maand',
  exclVatUntil: 'excl. btw, tot {date}',
  perCreditLabel: 'Prijs per credit',
  volumeLabel: 'Volumekorting',
  volumeNone: 'Geen',
  volumeNote: 'op de {price} per credit van het kleinste abonnement',
  volumeBase: 'Het kleinste abonnement bepaalt de basisprijs per credit',
  vatLabel: 'Incl. 25% btw',
  perMonth: 'per maand',
  vatReverseLabel: 'Btw',
  vatReverseValue: 'Btw verlegd',
  codeAdd: 'Kortingscode toevoegen',
  codePlaceholder: 'Kortingscode',
  codeApply: 'Toepassen',
  codeChecking: 'Controleren…',
  codeNotFound: 'We kunnen die code niet vinden. Controleer de spelling en probeer het opnieuw.',
  codeRemove: 'Code verwijderen',
  codeForMonths: '{percent} korting gedurende {months} maanden',
  codeForOneMonth: '{percent} korting gedurende 1 maand',
  codeForGood: '{percent} korting, blijvend',
  codeUntil: '{percent} korting tot {date}',
  codeCarries: '{percent} korting tot {date}, gaat mee over',
  codeCarriesForGood: '{percent} korting, blijvend, gaat mee over',
  ctaSwitch: 'Overstappen naar {credits} credits',
  ctaUseCode: 'Code gebruiken voor dit abonnement',
  ctaCurrent: 'Je huidige abonnement',
  ctaCheckout: 'Doorgaan naar betalen',
  ctaSaving: 'Opslaan…',
  ctaOpening: 'Betaling openen…',
  sideFoot: 'Maandelijks gefactureerd per kaart',
  back: 'Alle abonnementen',
  timingTitle: 'Wanneer moet het nieuwe abonnement ingaan?',
  timingGroup: 'Wanneer het nieuwe abonnement ingaat',
  summaryInclVat: '{price} per maand incl. btw',
  summaryExclVat: '{price} per maand excl. btw',
  summaryOff: '{percent} korting',
  nowTitle: 'Nu starten',
  renewalTitle: 'Bij verlenging, {date}',
  today: 'Vandaag',
  fromThenOn: 'Daarna',
  onDate: 'Op {date}',
  codeNote: '({percent} korting)',
  codeNoteUntil: '({percent} korting tot {date})',
  nowToday:
    'Het nieuwe abonnement gaat in. Je betaalt {price}{codeNote} en krijgt meteen {credits}.',
  nowTodayOnTop:
    'Het nieuwe abonnement gaat in. Je betaalt {price}{codeNote} en krijgt meteen {credits}, bovenop de {balance} die je al hebt ({total} in totaal).',
  nowThen:
    '{credits} voor {price} per maand daarna. Je huidige abonnement eindigt vandaag en wordt niet opnieuw in rekening gebracht.',
  nowThenUntil:
    '{credits} voor {price} per maand tot {date}, daarna {fullPrice}. Je huidige abonnement eindigt vandaag en wordt niet opnieuw in rekening gebracht.',
  renewalToday:
    'Er verandert niets. Je houdt je huidige abonnement en je {balance} credits, en je betaalt niets.',
  renewalTodayPaused:
    'Er verandert niets. Je houdt je huidige abonnement en je {balance} credits, en je betaalt niets. Het gebruik blijft tot dan gepauzeerd.',
  renewalThen:
    'Het nieuwe abonnement gaat in. Je betaalt {price}{codeNote} en krijgt dan {credits}, niet eerder.',
  footSe:
    'Bedragen zijn inclusief 25% btw. We belasten de kaart die op je factureringspagina staat.',
  footUnknown:
    'Bedragen zijn inclusief 25% Zweedse btw. Waar van toepassing wordt btw bij het afrekenen toegevoegd.',
  footReverse:
    'Bedragen zijn exclusief btw, die wordt verlegd. We belasten de kaart die op je factureringspagina staat.',
  cancel: 'Annuleren',
  confirmNow: '{price} betalen en overstappen',
  confirmRenewal: 'Overstap naar {credits} credits inplannen',
  paying: 'Betalen…',
  scheduling: 'Inplannen…',
  toastSwitched:
    'Je nieuwe abonnement is actief. Er zijn {credits} credits aan je saldo toegevoegd.',
  toastScheduled: 'Je nieuwe abonnement gaat in op {date}. Tot dan verandert er niets.',
  toastCode: '{percent} korting geldt vanaf {date}, gedurende {months} maanden.',
  toastCodeOneMonth: '{percent} korting geldt vanaf {date}, gedurende 1 maand.',
  toastCodeForGood: '{percent} korting geldt vanaf {date}.',
  errorDeclined:
    'Je kaart is geweigerd. Werk deze bij onder Betaalmethode en facturen en probeer het opnieuw.',
  errorDiscountActive: 'Er is al een korting actief. Die gaat mee over naar het nieuwe abonnement.',
  errorComplimentary:
    'Je abonnement is gratis dankzij een code, dus een nieuw abonnement kan alleen bij verlenging ingaan.',
  errorCodeNotAccepted: 'Die code kan niet voor dit abonnement worden gebruikt.',
  codeAlreadyUsed: 'Je bedrijf heeft deze code al gebruikt.',
  errorGeneric: 'We konden het abonnement niet wijzigen. Probeer het opnieuw.',
  errorEditing:
    'Rond de bewerking van je eerdere vraag af of annuleer die voordat je de betaling opent.',
  errorAttachments:
    'Je concept bevat bijlagen. Verwijder ze voordat je de betaling opent. De tekst van je vraag blijft bewaard en je kunt de bestanden daarna opnieuw toevoegen.',
  errorStorage:
    'Je browser kon je concept niet opslaan. Kopieer je vraag naar een veilige plek en sta sessieopslag toe voordat je de betaling opent.',
  planPerMonth: '{credits} credits / maand',
  creditsCount: '{count} credits',
  billingTitle: 'Facturering',
  billingIntro: 'Het abonnement, de credits en de betaling van je bedrijf.',
  planLabel: 'Abonnement',
  noPlan: 'Geen abonnement',
  planPaymentFailed: 'Betaling mislukt',
  planEndedOn: 'Geëindigd op {date}',
  planEnded: 'Beëindigd',
  codeNotPercent: 'Deze code geeft een vast bedrag korting. Hier werken alleen procentcodes.',
  balanceLabel: 'Resterende credits',
  nextLabel: 'Volgende',
  renewsWith: '+{credits} credits op {date}',
  switchesTo: 'Gaat over naar {credits} credits / maand op {date}',
  endsOn: 'Eindigt op {date}',
  discountLabel: 'Korting',
  creditsNeverExpire: 'Credits verlopen nooit.',
  paymentFailed:
    'Je laatste betaling is niet gelukt. Werk de kaart bij onder Betaalmethode en facturen.',
  seeUsage: 'Gebruik bekijken',
  changePlan: 'Abonnement wijzigen',
  choosePlan: 'Kies een abonnement',
  portal: 'Betaalmethode en facturen',
  opening: 'Openen…',
  readOnly: 'De eigenaar van het bedrijf beheert het abonnement en de betaling.',
  nativeOnly: 'Abonnementen en betalingen worden op het web beheerd.',
  loading: 'Facturering laden…',
  retry: 'Opnieuw proberen',
  unavailable:
    'Facturering is tijdelijk niet beschikbaar. Probeer het opnieuw. Je geschiedenis en instellingen zijn nog steeds beschikbaar.',
  portalError: 'De betaalinstellingen konden niet worden geopend. Probeer het opnieuw.',
  returnPending:
    'Wachten op bevestiging van de betaling. Je credits verschijnen zodra de betaling is bevestigd.',
  returnDelayed: 'De betalingsbevestiging duurt langer dan verwacht. Kijk zo nog eens.',
  returnCanceled: 'Het afrekenen is geannuleerd. Er is niets in rekening gebracht.',
  pausedPlanTitle: 'Kies een abonnement om verder te gaan',
  pausedPlanOwner:
    'Poneva werkt met maandelijkse creditabonnementen. Kies een abonnement om vragen te blijven stellen.',
  pausedPlanMember:
    'Je bedrijf heeft geen actief abonnement. De eigenaar van het bedrijf kiest er een in Instellingen onder Gebruik.',
  pausedPastDue:
    'Je laatste betaling is niet gelukt, daarom is het abonnement gepauzeerd. Werk de kaart bij onder Betaalmethode en facturen.',
  pausedPastDueMember:
    'De laatste betaling van het bedrijf is niet gelukt, daarom is het abonnement gepauzeerd. De eigenaar van het bedrijf kan de kaart bijwerken.',
  pausedCreditsTitle: 'Je hebt al je credits gebruikt',
  pausedCreditsBody:
    'Upgrade nu, of wacht tot {date} om {time}, wanneer je abonnement {credits} credits toevoegt.',
  pausedCreditsMember:
    'Vraag de eigenaar van het bedrijf om te upgraden, of wacht tot {date} om {time}, wanneer je abonnement {credits} credits toevoegt.',
  pausedCreditsNoRenewal: 'Kies een groter abonnement om verder te gaan.',
  pausedLimitTitle: 'Je hebt je maandlimiet bereikt',
  pausedLimitBody:
    'Je hebt je maandlimiet van {limit} credits gebruikt. Die wordt op {date} gereset of wanneer de eigenaar van het bedrijf de limiet verhoogt.',
  pausedLimitNoDate:
    'Je hebt je maandlimiet van {limit} credits gebruikt. De eigenaar van het bedrijf kan de limiet verhogen.',
  upgradeNow: 'Nu upgraden',
  limitNotice:
    'Nog {left} van je {limit} maandelijkse credits over. Je limiet wordt op {date} gereset.',
  limitNoticeNoDate: 'Nog {left} van je {limit} maandelijkse credits over.',
};

const fr: Copy = {
  dialogTitle: 'Changer de forfait',
  dialogTitleNew: 'Choisir un forfait',
  dialogSub:
    'Chaque mois, votre forfait ajoute ses crédits au solde partagé par toute votre équipe. Les crédits non utilisés sont reportés et n’expirent jamais, et vous pouvez les utiliser tant qu’un forfait est actif.',
  close: 'Fermer',
  kickerUpgrade: 'Forfait supérieur',
  kickerDowngrade: 'Forfait inférieur',
  currentPlanTag: 'Forfait actuel',
  currentPin: 'Actuel',
  creditsEveryMonth: 'crédits par mois',
  captionCurrent: 'Votre forfait actuel.',
  captionMore: '{count} crédits de plus par mois que votre forfait actuel.',
  captionFewer: '{count} crédits de moins par mois que votre forfait actuel.',
  captionRecent: 'Votre équipe a utilisé {count} crédits au cours des 30 derniers jours.',
  scaleEnd: '{price} par mois',
  sliderLabel: 'Forfait',
  sliderValue: '{credits} crédits par mois, {price} HT',
  priceLabel: 'Prix mensuel',
  exclVat: 'HT',
  exclVatForMonths: 'HT, pendant {months} mois',
  exclVatForOneMonth: 'HT, pendant 1 mois',
  exclVatUntil: 'HT, jusqu’au {date}',
  perCreditLabel: 'Prix par crédit',
  volumeLabel: 'Remise sur volume',
  volumeNone: 'Aucune',
  volumeNote: 'par rapport aux {price} par crédit du plus petit forfait',
  volumeBase: 'Le plus petit forfait fixe le prix de base par crédit',
  vatLabel: 'TVA 25 % incluse',
  perMonth: 'par mois',
  vatReverseLabel: 'TVA',
  vatReverseValue: 'Autoliquidation',
  codeAdd: 'Ajouter un code promo',
  codePlaceholder: 'Code promo',
  codeApply: 'Appliquer',
  codeChecking: 'Vérification…',
  codeNotFound: 'Ce code est introuvable. Vérifiez l’orthographe et réessayez.',
  codeRemove: 'Retirer le code',
  codeForMonths: '{percent} de réduction pendant {months} mois',
  codeForOneMonth: '{percent} de réduction pendant 1 mois',
  codeForGood: '{percent} de réduction permanente',
  codeUntil: '{percent} de réduction jusqu’au {date}',
  codeCarries: '{percent} de réduction jusqu’au {date}, conservée',
  codeCarriesForGood: '{percent} de réduction permanente, conservée',
  ctaSwitch: 'Passer à {credits} crédits',
  ctaUseCode: 'Utiliser le code sur ce forfait',
  ctaCurrent: 'Votre forfait actuel',
  ctaCheckout: 'Continuer vers le paiement',
  ctaSaving: 'Enregistrement…',
  ctaOpening: 'Ouverture du paiement…',
  sideFoot: 'Facturé chaque mois par carte',
  back: 'Tous les forfaits',
  timingTitle: 'Quand le nouveau forfait doit-il commencer ?',
  timingGroup: 'Début du nouveau forfait',
  summaryInclVat: '{price} par mois TTC',
  summaryExclVat: '{price} par mois HT',
  summaryOff: '{percent} de réduction',
  nowTitle: 'Commencer maintenant',
  renewalTitle: 'Au renouvellement, le {date}',
  today: 'Aujourd’hui',
  fromThenOn: 'Ensuite',
  onDate: 'Le {date}',
  codeNote: '({percent} de réduction)',
  codeNoteUntil: '({percent} de réduction jusqu’au {date})',
  nowToday:
    'Le nouveau forfait commence. Vous payez {price}{codeNote} et recevez {credits} immédiatement.',
  nowTodayOnTop:
    'Le nouveau forfait commence. Vous payez {price}{codeNote} et recevez {credits} immédiatement, en plus des {balance} dont vous disposez ({total} au total).',
  nowThen:
    '{credits} et {price} chaque mois ensuite. Votre forfait actuel prend fin aujourd’hui et ne sera plus facturé.',
  nowThenUntil:
    '{credits} et {price} chaque mois jusqu’au {date}, puis {fullPrice}. Votre forfait actuel prend fin aujourd’hui et ne sera plus facturé.',
  renewalToday:
    'Rien ne change. Vous conservez votre forfait actuel et vos {balance} crédits, et ne payez rien.',
  renewalTodayPaused:
    'Rien ne change. Vous conservez votre forfait actuel et vos {balance} crédits, et ne payez rien. L’utilisation reste suspendue jusque-là.',
  renewalThen:
    'Le nouveau forfait commence. Vous payez {price}{codeNote} et recevez {credits} à ce moment-là, pas avant.',
  footSe:
    'Les montants incluent 25 % de TVA. Nous débitons la carte enregistrée sur votre page Facturation.',
  footUnknown:
    'Les montants incluent 25 % de TVA suédoise. La TVA est ajoutée lors du paiement, le cas échéant.',
  footReverse:
    'Les montants sont HT, la TVA étant due par autoliquidation. Nous débitons la carte enregistrée sur votre page Facturation.',
  cancel: 'Annuler',
  confirmNow: 'Payer {price} et changer',
  confirmRenewal: 'Programmer le passage à {credits} crédits',
  paying: 'Paiement…',
  scheduling: 'Programmation…',
  toastSwitched:
    'Votre nouveau forfait est actif. {credits} crédits ont été ajoutés à votre solde.',
  toastScheduled: 'Votre nouveau forfait commence le {date}. Rien ne change d’ici là.',
  toastCode: '{percent} de réduction à partir du {date}, pendant {months} mois.',
  toastCodeOneMonth: '{percent} de réduction à partir du {date}, pendant 1 mois.',
  toastCodeForGood: '{percent} de réduction à partir du {date}.',
  errorDeclined:
    'Votre carte a été refusée. Mettez-la à jour dans Moyen de paiement et factures, puis réessayez.',
  errorDiscountActive: 'Une réduction est déjà active. Elle est conservée sur le nouveau forfait.',
  errorComplimentary:
    'Votre forfait est gratuit grâce à un code, un nouveau forfait ne peut donc commencer qu’au renouvellement.',
  errorCodeNotAccepted: 'Ce code ne peut pas être utilisé pour ce forfait.',
  codeAlreadyUsed: 'Votre entreprise a déjà utilisé ce code.',
  errorGeneric: 'Impossible de changer de forfait. Veuillez réessayer.',
  errorEditing:
    'Terminez ou annulez la modification de votre question précédente avant d’ouvrir le paiement.',
  errorAttachments:
    'Votre brouillon contient des fichiers joints. Retirez-les avant d’ouvrir le paiement. Le texte de votre question est enregistré et vous pourrez joindre les fichiers à nouveau ensuite.',
  errorStorage:
    'Votre navigateur n’a pas pu enregistrer votre brouillon. Copiez votre question en lieu sûr et autorisez le stockage de session avant d’ouvrir le paiement.',
  planPerMonth: '{credits} crédits/mois',
  creditsCount: '{count} crédits',
  billingTitle: 'Facturation',
  billingIntro: 'Le forfait, les crédits et le paiement de votre entreprise.',
  planLabel: 'Forfait',
  noPlan: 'Aucun forfait',
  planPaymentFailed: 'Échec du paiement',
  planEndedOn: 'Terminé le {date}',
  planEnded: 'Terminé',
  codeNotPercent:
    'Ce code retire un montant fixe. Seuls les codes en pourcentage fonctionnent ici.',
  balanceLabel: 'Crédits restants',
  nextLabel: 'À venir',
  renewsWith: '+{credits} crédits le {date}',
  switchesTo: 'Passe à {credits} crédits/mois le {date}',
  endsOn: 'Prend fin le {date}',
  discountLabel: 'Réduction',
  creditsNeverExpire: 'Les crédits n’expirent jamais.',
  paymentFailed:
    'Votre dernier paiement n’a pas abouti. Mettez à jour la carte dans Moyen de paiement et factures.',
  seeUsage: 'Voir l’utilisation',
  changePlan: 'Changer de forfait',
  choosePlan: 'Choisir un forfait',
  portal: 'Moyen de paiement et factures',
  opening: 'Ouverture…',
  readOnly: 'Le propriétaire de l’entreprise gère le forfait et le paiement.',
  nativeOnly: 'Les forfaits et le paiement se gèrent sur le web.',
  loading: 'Chargement de la facturation…',
  retry: 'Réessayer',
  unavailable:
    'La facturation est temporairement indisponible. Veuillez réessayer. Votre historique et vos paramètres restent disponibles.',
  portalError: 'Impossible d’ouvrir les paramètres de paiement. Veuillez réessayer.',
  returnPending:
    'En attente de la confirmation du paiement. Vos crédits apparaîtront une fois le paiement confirmé.',
  returnDelayed:
    'La confirmation du paiement prend plus de temps que prévu. Vérifiez à nouveau dans un instant.',
  returnCanceled: 'Le paiement a été annulé. Aucun montant n’a été débité.',
  pausedPlanTitle: 'Choisissez un forfait pour continuer',
  pausedPlanOwner:
    'Poneva fonctionne avec des forfaits mensuels de crédits. Choisissez un forfait pour continuer à poser des questions.',
  pausedPlanMember:
    'Votre entreprise n’a aucun forfait actif. Le propriétaire de l’entreprise en choisit un dans Paramètres, sous Utilisation.',
  pausedPastDue:
    'Votre dernier paiement n’a pas abouti, le forfait est donc suspendu. Mettez à jour la carte dans Moyen de paiement et factures.',
  pausedPastDueMember:
    'Le dernier paiement de l’entreprise n’a pas abouti, le forfait est donc suspendu. Le propriétaire de l’entreprise peut mettre à jour la carte.',
  pausedCreditsTitle: 'Vous avez utilisé tous vos crédits',
  pausedCreditsBody:
    'Passez dès maintenant à un forfait supérieur, ou attendez le {date} à {time}, lorsque votre forfait ajoutera {credits} crédits.',
  pausedCreditsMember:
    'Demandez au propriétaire de l’entreprise de passer à un forfait supérieur, ou attendez le {date} à {time}, lorsque votre forfait ajoutera {credits} crédits.',
  pausedCreditsNoRenewal: 'Choisissez un forfait plus important pour continuer.',
  pausedLimitTitle: 'Vous avez atteint votre limite mensuelle',
  pausedLimitBody:
    'Vous avez utilisé votre limite mensuelle de {limit} crédits. Elle sera réinitialisée le {date} ou lorsque le propriétaire de l’entreprise l’augmentera.',
  pausedLimitNoDate:
    'Vous avez utilisé votre limite mensuelle de {limit} crédits. Le propriétaire de l’entreprise peut l’augmenter.',
  upgradeNow: 'Passer au forfait supérieur',
  limitNotice:
    'Il vous reste {left} de vos {limit} crédits mensuels. Votre limite sera réinitialisée le {date}.',
  limitNoticeNoDate: 'Il vous reste {left} de vos {limit} crédits mensuels.',
};

const es: Copy = {
  dialogTitle: 'Cambiar de plan',
  dialogTitleNew: 'Elegir un plan',
  dialogSub:
    'Cada mes, su plan añade sus créditos al saldo que comparte todo su equipo. Los créditos no utilizados se acumulan y nunca caducan, y puede usarlos mientras haya un plan activo.',
  close: 'Cerrar',
  kickerUpgrade: 'Plan superior',
  kickerDowngrade: 'Plan inferior',
  currentPlanTag: 'Plan actual',
  currentPin: 'Actual',
  creditsEveryMonth: 'créditos al mes',
  captionCurrent: 'Su plan actual.',
  captionMore: '{count} créditos más al mes que su plan actual.',
  captionFewer: '{count} créditos menos al mes que su plan actual.',
  captionRecent: 'Su equipo usó {count} créditos en los últimos 30 días.',
  scaleEnd: '{price} al mes',
  sliderLabel: 'Plan',
  sliderValue: '{credits} créditos al mes, {price} sin IVA',
  priceLabel: 'Precio al mes',
  exclVat: 'sin IVA',
  exclVatForMonths: 'sin IVA, durante {months} meses',
  exclVatForOneMonth: 'sin IVA, durante 1 mes',
  exclVatUntil: 'sin IVA, hasta el {date}',
  perCreditLabel: 'Precio por crédito',
  volumeLabel: 'Descuento por volumen',
  volumeNone: 'Ninguno',
  volumeNote: 'sobre los {price} por crédito del plan más pequeño',
  volumeBase: 'El plan más pequeño fija el precio base por crédito',
  vatLabel: 'IVA del 25 % incluido',
  perMonth: 'al mes',
  vatReverseLabel: 'IVA',
  vatReverseValue: 'Inversión del sujeto pasivo',
  codeAdd: 'Añadir código de descuento',
  codePlaceholder: 'Código de descuento',
  codeApply: 'Aplicar',
  codeChecking: 'Comprobando…',
  codeNotFound: 'No encontramos ese código. Revise la ortografía e inténtelo de nuevo.',
  codeRemove: 'Quitar código',
  codeForMonths: '{percent} de descuento durante {months} meses',
  codeForOneMonth: '{percent} de descuento durante 1 mes',
  codeForGood: '{percent} de descuento permanente',
  codeUntil: '{percent} de descuento hasta el {date}',
  codeCarries: '{percent} de descuento hasta el {date}, se mantiene',
  codeCarriesForGood: '{percent} de descuento permanente, se mantiene',
  ctaSwitch: 'Cambiar a {credits} créditos',
  ctaUseCode: 'Usar el código en este plan',
  ctaCurrent: 'Su plan actual',
  ctaCheckout: 'Continuar al pago',
  ctaSaving: 'Guardando…',
  ctaOpening: 'Abriendo el pago…',
  sideFoot: 'Facturación mensual con tarjeta',
  back: 'Todos los planes',
  timingTitle: '¿Cuándo debe empezar el nuevo plan?',
  timingGroup: 'Inicio del nuevo plan',
  summaryInclVat: '{price} al mes con IVA',
  summaryExclVat: '{price} al mes sin IVA',
  summaryOff: '{percent} de descuento',
  nowTitle: 'Empezar ahora',
  renewalTitle: 'En la renovación, el {date}',
  today: 'Hoy',
  fromThenOn: 'Después',
  onDate: 'El {date}',
  codeNote: '({percent} de descuento)',
  codeNoteUntil: '({percent} de descuento hasta el {date})',
  nowToday: 'Empieza el nuevo plan. Paga {price}{codeNote} y recibe {credits} al instante.',
  nowTodayOnTop:
    'Empieza el nuevo plan. Paga {price}{codeNote} y recibe {credits} al instante, además de los {balance} que ya tiene ({total} en total).',
  nowThen:
    '{credits} y {price} cada mes a partir de entonces. Su plan actual termina hoy y no se le volverá a cobrar.',
  nowThenUntil:
    '{credits} y {price} cada mes hasta el {date}, después {fullPrice}. Su plan actual termina hoy y no se le volverá a cobrar.',
  renewalToday: 'No cambia nada. Mantiene su plan actual y sus {balance} créditos, y no paga nada.',
  renewalTodayPaused:
    'No cambia nada. Mantiene su plan actual y sus {balance} créditos, y no paga nada. El uso sigue en pausa hasta entonces.',
  renewalThen:
    'Empieza el nuevo plan. Paga {price}{codeNote} y recibe {credits} en ese momento, no antes.',
  footSe:
    'Los importes incluyen un 25 % de IVA. Cobramos en la tarjeta registrada en su página de Facturación.',
  footUnknown:
    'Los importes incluyen un 25 % de IVA sueco. El IVA se añade al pagar cuando corresponda.',
  footReverse:
    'Los importes no incluyen IVA, que se liquida por inversión del sujeto pasivo. Cobramos en la tarjeta registrada en su página de Facturación.',
  cancel: 'Cancelar',
  confirmNow: 'Pagar {price} y cambiar',
  confirmRenewal: 'Programar el cambio a {credits} créditos',
  paying: 'Pagando…',
  scheduling: 'Programando…',
  toastSwitched: 'Su nuevo plan está activo. Se han añadido {credits} créditos a su saldo.',
  toastScheduled: 'Su nuevo plan empieza el {date}. Hasta entonces no cambia nada.',
  toastCode: 'El {percent} de descuento se aplica desde el {date}, durante {months} meses.',
  toastCodeOneMonth: 'El {percent} de descuento se aplica desde el {date}, durante 1 mes.',
  toastCodeForGood: 'El {percent} de descuento se aplica desde el {date}.',
  errorDeclined:
    'Su tarjeta ha sido rechazada. Actualícela en Método de pago y facturas e inténtelo de nuevo.',
  errorDiscountActive: 'Ya hay un descuento activo. Se mantiene en el nuevo plan.',
  errorComplimentary:
    'Su plan es gratuito gracias a un código, así que un nuevo plan solo puede empezar en la renovación.',
  errorCodeNotAccepted: 'Ese código no se puede usar en este plan.',
  codeAlreadyUsed: 'Su empresa ya ha usado este código.',
  errorGeneric: 'No hemos podido cambiar el plan. Inténtelo de nuevo.',
  errorEditing: 'Termine o cancele la edición de su pregunta anterior antes de abrir el pago.',
  errorAttachments:
    'Su borrador tiene archivos adjuntos. Quítelos antes de abrir el pago. El texto de su pregunta se guarda y podrá adjuntar los archivos de nuevo después.',
  errorStorage:
    'Su navegador no ha podido guardar el borrador. Copie su pregunta en un lugar seguro y permita el almacenamiento de sesión antes de abrir el pago.',
  planPerMonth: '{credits} créditos/mes',
  creditsCount: '{count} créditos',
  billingTitle: 'Facturación',
  billingIntro: 'El plan, los créditos y el pago de su empresa.',
  planLabel: 'Plan',
  noPlan: 'Sin plan',
  planPaymentFailed: 'Pago fallido',
  planEndedOn: 'Terminó el {date}',
  planEnded: 'Finalizado',
  codeNotPercent:
    'Este código descuenta un importe fijo. Aquí solo funcionan los códigos de porcentaje.',
  balanceLabel: 'Créditos disponibles',
  nextLabel: 'Próximo',
  renewsWith: '+{credits} créditos el {date}',
  switchesTo: 'Cambia a {credits} créditos/mes el {date}',
  endsOn: 'Termina el {date}',
  discountLabel: 'Descuento',
  creditsNeverExpire: 'Los créditos nunca caducan.',
  paymentFailed:
    'Su último pago no se ha completado. Actualice la tarjeta en Método de pago y facturas.',
  seeUsage: 'Ver uso',
  changePlan: 'Cambiar de plan',
  choosePlan: 'Elegir un plan',
  portal: 'Método de pago y facturas',
  opening: 'Abriendo…',
  readOnly: 'El propietario de la empresa gestiona el plan y el pago.',
  nativeOnly: 'Los planes y el pago se gestionan en la web.',
  loading: 'Cargando facturación…',
  retry: 'Reintentar',
  unavailable:
    'La facturación no está disponible temporalmente. Inténtelo de nuevo. Su historial y su configuración siguen disponibles.',
  portalError: 'No se ha podido abrir la configuración de pago. Inténtelo de nuevo.',
  returnPending:
    'Esperando la confirmación del pago. Sus créditos aparecerán cuando se confirme el pago.',
  returnDelayed:
    'La confirmación del pago está tardando más de lo previsto. Vuelva a comprobarlo en breve.',
  returnCanceled: 'Se ha cancelado el pago. No se ha cobrado nada.',
  pausedPlanTitle: 'Elija un plan para continuar',
  pausedPlanOwner:
    'Poneva funciona con planes mensuales de créditos. Elija un plan para seguir haciendo preguntas.',
  pausedPlanMember:
    'Su empresa no tiene ningún plan activo. El propietario de la empresa elige uno en Configuración, en Uso.',
  pausedPastDue:
    'Su último pago no se ha completado, así que el plan está en pausa. Actualice la tarjeta en Método de pago y facturas.',
  pausedPastDueMember:
    'El último pago de la empresa no se ha completado, así que el plan está en pausa. El propietario de la empresa puede actualizar la tarjeta.',
  pausedCreditsTitle: 'Ha usado todos sus créditos',
  pausedCreditsBody:
    'Pase ahora a un plan superior o espere hasta el {date} a las {time}, cuando su plan añada {credits} créditos.',
  pausedCreditsMember:
    'Pida al propietario de la empresa que pase a un plan superior o espere hasta el {date} a las {time}, cuando su plan añada {credits} créditos.',
  pausedCreditsNoRenewal: 'Elija un plan mayor para continuar.',
  pausedLimitTitle: 'Ha alcanzado su límite mensual',
  pausedLimitBody:
    'Ha usado su límite mensual de {limit} créditos. Se restablece el {date} o cuando el propietario de la empresa lo aumente.',
  pausedLimitNoDate:
    'Ha usado su límite mensual de {limit} créditos. El propietario de la empresa puede aumentarlo.',
  upgradeNow: 'Pasar a un plan superior',
  limitNotice:
    'Le quedan {left} de sus {limit} créditos mensuales. Su límite se restablece el {date}.',
  limitNoticeNoDate: 'Le quedan {left} de sus {limit} créditos mensuales.',
};

const it: Copy = {
  dialogTitle: 'Cambia piano',
  dialogTitleNew: 'Scegli un piano',
  dialogSub:
    'Ogni mese il tuo piano aggiunge i suoi crediti al saldo condiviso da tutto il team. I crediti non usati restano disponibili e non scadono mai, e puoi usarli finché un piano è attivo.',
  close: 'Chiudi',
  kickerUpgrade: 'Piano superiore',
  kickerDowngrade: 'Piano inferiore',
  currentPlanTag: 'Piano attuale',
  currentPin: 'Attuale',
  creditsEveryMonth: 'crediti ogni mese',
  captionCurrent: 'Il tuo piano attuale.',
  captionMore: '{count} crediti in più al mese rispetto al tuo piano attuale.',
  captionFewer: '{count} crediti in meno al mese rispetto al tuo piano attuale.',
  captionRecent: 'Il tuo team ha usato {count} crediti negli ultimi 30 giorni.',
  scaleEnd: '{price} al mese',
  sliderLabel: 'Piano',
  sliderValue: '{credits} crediti al mese, {price} IVA esclusa',
  priceLabel: 'Prezzo mensile',
  exclVat: 'IVA esclusa',
  exclVatForMonths: 'IVA esclusa, per {months} mesi',
  exclVatForOneMonth: 'IVA esclusa, per 1 mese',
  exclVatUntil: 'IVA esclusa, fino al {date}',
  perCreditLabel: 'Prezzo per credito',
  volumeLabel: 'Sconto volume',
  volumeNone: 'Nessuno',
  volumeNote: 'rispetto ai {price} per credito del piano più piccolo',
  volumeBase: 'Il piano più piccolo determina il prezzo base per credito',
  vatLabel: 'IVA 25% inclusa',
  perMonth: 'al mese',
  vatReverseLabel: 'IVA',
  vatReverseValue: 'Inversione contabile',
  codeAdd: 'Aggiungi codice sconto',
  codePlaceholder: 'Codice sconto',
  codeApply: 'Applica',
  codeChecking: 'Verifica in corso…',
  codeNotFound: 'Non abbiamo trovato questo codice. Controlla come è scritto e riprova.',
  codeRemove: 'Rimuovi codice',
  codeForMonths: 'Sconto del {percent} per {months} mesi',
  codeForOneMonth: 'Sconto del {percent} per 1 mese',
  codeForGood: 'Sconto del {percent} per sempre',
  codeUntil: 'Sconto del {percent} fino al {date}',
  codeCarries: 'Sconto del {percent} fino al {date}, resta valido',
  codeCarriesForGood: 'Sconto del {percent} per sempre, resta valido',
  ctaSwitch: 'Passa a {credits} crediti',
  ctaUseCode: 'Usa il codice su questo piano',
  ctaCurrent: 'Il tuo piano attuale',
  ctaCheckout: 'Continua al pagamento',
  ctaSaving: 'Salvataggio…',
  ctaOpening: 'Apertura del pagamento…',
  sideFoot: 'Addebito mensile su carta',
  back: 'Tutti i piani',
  timingTitle: 'Quando deve iniziare il nuovo piano?',
  timingGroup: 'Inizio del nuovo piano',
  summaryInclVat: '{price} al mese IVA inclusa',
  summaryExclVat: '{price} al mese IVA esclusa',
  summaryOff: 'sconto del {percent}',
  nowTitle: 'Inizia ora',
  renewalTitle: 'Al rinnovo, il {date}',
  today: 'Oggi',
  fromThenOn: 'In seguito',
  onDate: 'Il {date}',
  codeNote: '(sconto del {percent})',
  codeNoteUntil: '(sconto del {percent} fino al {date})',
  nowToday: 'Il nuovo piano inizia. Paghi {price}{codeNote} e ricevi subito {credits}.',
  nowTodayOnTop:
    'Il nuovo piano inizia. Paghi {price}{codeNote} e ricevi subito {credits}, in aggiunta ai {balance} che hai già ({total} in totale).',
  nowThen:
    '{credits} e {price} ogni mese da quel momento. Il tuo piano attuale termina oggi e non verrà più addebitato.',
  nowThenUntil:
    '{credits} e {price} ogni mese fino al {date}, poi {fullPrice}. Il tuo piano attuale termina oggi e non verrà più addebitato.',
  renewalToday:
    'Non cambia nulla. Mantieni il tuo piano attuale e i tuoi {balance} crediti, e non paghi nulla.',
  renewalTodayPaused:
    'Non cambia nulla. Mantieni il tuo piano attuale e i tuoi {balance} crediti, e non paghi nulla. L’utilizzo resta in pausa fino ad allora.',
  renewalThen:
    'Il nuovo piano inizia. Paghi {price}{codeNote} e ricevi {credits} in quel momento, non prima.',
  footSe:
    'Gli importi includono l’IVA al 25%. Addebitiamo la carta indicata nella pagina Fatturazione.',
  footUnknown:
    'Gli importi includono l’IVA svedese al 25%. L’IVA viene aggiunta al pagamento, dove prevista.',
  footReverse:
    'Gli importi sono al netto dell’IVA, soggetta a inversione contabile. Addebitiamo la carta indicata nella pagina Fatturazione.',
  cancel: 'Annulla',
  confirmNow: 'Paga {price} e cambia',
  confirmRenewal: 'Programma il passaggio a {credits} crediti',
  paying: 'Pagamento in corso…',
  scheduling: 'Programmazione…',
  toastSwitched: 'Il tuo nuovo piano è attivo. {credits} crediti sono stati aggiunti al tuo saldo.',
  toastScheduled: 'Il tuo nuovo piano inizia il {date}. Fino ad allora non cambia nulla.',
  toastCode: 'Lo sconto del {percent} si applica dal {date}, per {months} mesi.',
  toastCodeOneMonth: 'Lo sconto del {percent} si applica dal {date}, per 1 mese.',
  toastCodeForGood: 'Lo sconto del {percent} si applica dal {date}.',
  errorDeclined:
    'La tua carta è stata rifiutata. Aggiornala in Metodo di pagamento e fatture, poi riprova.',
  errorDiscountActive: 'Uno sconto è già attivo. Resta valido con il nuovo piano.',
  errorComplimentary:
    'Il tuo piano è gratuito grazie a un codice, quindi un nuovo piano può iniziare solo al rinnovo.',
  errorCodeNotAccepted: 'Questo codice non può essere usato per questo piano.',
  codeAlreadyUsed: 'La tua azienda ha già usato questo codice.',
  errorGeneric: 'Non è stato possibile cambiare piano. Riprova.',
  errorEditing:
    'Completa o annulla la modifica della domanda precedente prima di aprire il pagamento.',
  errorAttachments:
    'La tua bozza contiene file allegati. Rimuovili prima di aprire il pagamento. Il testo della domanda resta salvato e potrai allegare di nuovo i file in seguito.',
  errorStorage:
    'Il browser non è riuscito a salvare la bozza. Copia la domanda in un posto sicuro e consenti l’archiviazione di sessione prima di aprire il pagamento.',
  planPerMonth: '{credits} crediti/mese',
  creditsCount: '{count} crediti',
  billingTitle: 'Fatturazione',
  billingIntro: 'Piano, crediti e pagamento della tua azienda.',
  planLabel: 'Piano',
  noPlan: 'Nessun piano',
  planPaymentFailed: 'Pagamento non riuscito',
  planEndedOn: 'Terminato il {date}',
  planEnded: 'Terminato',
  codeNotPercent:
    'Questo codice sconta un importo fisso. Qui funzionano solo i codici percentuali.',
  balanceLabel: 'Crediti rimasti',
  nextLabel: 'Prossimo',
  renewsWith: '+{credits} crediti il {date}',
  switchesTo: 'Passa a {credits} crediti/mese il {date}',
  endsOn: 'Termina il {date}',
  discountLabel: 'Sconto',
  creditsNeverExpire: 'I crediti non scadono mai.',
  paymentFailed:
    'L’ultimo pagamento non è andato a buon fine. Aggiorna la carta in Metodo di pagamento e fatture.',
  seeUsage: 'Vedi utilizzo',
  changePlan: 'Cambia piano',
  choosePlan: 'Scegli un piano',
  portal: 'Metodo di pagamento e fatture',
  opening: 'Apertura…',
  readOnly: 'Il proprietario dell’azienda gestisce il piano e il pagamento.',
  nativeOnly: 'Piani e pagamenti si gestiscono sul web.',
  loading: 'Caricamento della fatturazione…',
  retry: 'Riprova',
  unavailable:
    'La fatturazione non è temporaneamente disponibile. Riprova. Cronologia e impostazioni restano disponibili.',
  portalError: 'Impossibile aprire le impostazioni di pagamento. Riprova.',
  returnPending:
    'In attesa della conferma del pagamento. I crediti compariranno dopo la conferma del pagamento.',
  returnDelayed:
    'La conferma del pagamento richiede più tempo del previsto. Controlla di nuovo tra poco.',
  returnCanceled: 'Il pagamento è stato annullato. Non è stato addebitato nulla.',
  pausedPlanTitle: 'Scegli un piano per continuare',
  pausedPlanOwner:
    'Poneva funziona con piani mensili di crediti. Scegli un piano per continuare a fare domande.',
  pausedPlanMember:
    'La tua azienda non ha un piano attivo. Il proprietario dell’azienda ne sceglie uno in Impostazioni, alla voce Utilizzo.',
  pausedPastDue:
    'L’ultimo pagamento non è andato a buon fine, quindi il piano è in pausa. Aggiorna la carta in Metodo di pagamento e fatture.',
  pausedPastDueMember:
    'L’ultimo pagamento dell’azienda non è andato a buon fine, quindi il piano è in pausa. Il proprietario dell’azienda può aggiornare la carta.',
  pausedCreditsTitle: 'Hai usato tutti i tuoi crediti',
  pausedCreditsBody:
    'Passa ora a un piano superiore oppure attendi il {date} alle {time}, quando il tuo piano aggiungerà {credits} crediti.',
  pausedCreditsMember:
    'Chiedi al proprietario dell’azienda di passare a un piano superiore oppure attendi il {date} alle {time}, quando il tuo piano aggiungerà {credits} crediti.',
  pausedCreditsNoRenewal: 'Scegli un piano più grande per continuare.',
  pausedLimitTitle: 'Hai raggiunto il tuo limite mensile',
  pausedLimitBody:
    'Hai usato il tuo limite mensile di {limit} crediti. Si azzera il {date} o quando il proprietario dell’azienda lo aumenta.',
  pausedLimitNoDate:
    'Hai usato il tuo limite mensile di {limit} crediti. Il proprietario dell’azienda può aumentarlo.',
  upgradeNow: 'Passa a un piano superiore',
  limitNotice: 'Ti restano {left} dei tuoi {limit} crediti mensili. Il limite si azzera il {date}.',
  limitNoticeNoDate: 'Ti restano {left} dei tuoi {limit} crediti mensili.',
};

const pt: Copy = {
  dialogTitle: 'Alterar plano',
  dialogTitleNew: 'Escolher um plano',
  dialogSub:
    'Todos os meses, o seu plano adiciona os respetivos créditos ao saldo partilhado por toda a equipa. Os créditos não utilizados transitam e nunca expiram, e pode utilizá-los enquanto houver um plano ativo.',
  close: 'Fechar',
  kickerUpgrade: 'Plano superior',
  kickerDowngrade: 'Plano inferior',
  currentPlanTag: 'Plano atual',
  currentPin: 'Atual',
  creditsEveryMonth: 'créditos por mês',
  captionCurrent: 'O seu plano atual.',
  captionMore: 'Mais {count} créditos por mês do que o seu plano atual.',
  captionFewer: 'Menos {count} créditos por mês do que o seu plano atual.',
  captionRecent: 'A sua equipa utilizou {count} créditos nos últimos 30 dias.',
  scaleEnd: '{price} por mês',
  sliderLabel: 'Plano',
  sliderValue: '{credits} créditos por mês, {price} sem IVA',
  priceLabel: 'Preço por mês',
  exclVat: 'sem IVA',
  exclVatForMonths: 'sem IVA, durante {months} meses',
  exclVatForOneMonth: 'sem IVA, durante 1 mês',
  exclVatUntil: 'sem IVA, até {date}',
  perCreditLabel: 'Preço por crédito',
  volumeLabel: 'Desconto de volume',
  volumeNone: 'Nenhum',
  volumeNote: 'face aos {price} por crédito do plano mais pequeno',
  volumeBase: 'O plano mais pequeno define o preço base por crédito',
  vatLabel: 'IVA de 25% incluído',
  perMonth: 'por mês',
  vatReverseLabel: 'IVA',
  vatReverseValue: 'Autoliquidação',
  codeAdd: 'Adicionar código de desconto',
  codePlaceholder: 'Código de desconto',
  codeApply: 'Aplicar',
  codeChecking: 'A verificar…',
  codeNotFound: 'Não encontrámos esse código. Verifique a ortografia e tente novamente.',
  codeRemove: 'Remover código',
  codeForMonths: '{percent} de desconto durante {months} meses',
  codeForOneMonth: '{percent} de desconto durante 1 mês',
  codeForGood: '{percent} de desconto permanente',
  codeUntil: '{percent} de desconto até {date}',
  codeCarries: '{percent} de desconto até {date}, mantém-se',
  codeCarriesForGood: '{percent} de desconto permanente, mantém-se',
  ctaSwitch: 'Mudar para {credits} créditos',
  ctaUseCode: 'Usar o código neste plano',
  ctaCurrent: 'O seu plano atual',
  ctaCheckout: 'Continuar para o pagamento',
  ctaSaving: 'A guardar…',
  ctaOpening: 'A abrir o pagamento…',
  sideFoot: 'Faturado mensalmente por cartão',
  back: 'Todos os planos',
  timingTitle: 'Quando deve começar o novo plano?',
  timingGroup: 'Início do novo plano',
  summaryInclVat: '{price} por mês com IVA',
  summaryExclVat: '{price} por mês sem IVA',
  summaryOff: '{percent} de desconto',
  nowTitle: 'Começar agora',
  renewalTitle: 'Na renovação, a {date}',
  today: 'Hoje',
  fromThenOn: 'A partir daí',
  onDate: 'A {date}',
  codeNote: '({percent} de desconto)',
  codeNoteUntil: '({percent} de desconto até {date})',
  nowToday: 'O novo plano começa. Paga {price}{codeNote} e recebe {credits} de imediato.',
  nowTodayOnTop:
    'O novo plano começa. Paga {price}{codeNote} e recebe {credits} de imediato, além dos {balance} que já tem ({total} no total).',
  nowThen:
    '{credits} e {price} todos os meses a partir daí. O seu plano atual termina hoje e não volta a ser cobrado.',
  nowThenUntil:
    '{credits} e {price} todos os meses até {date}, depois {fullPrice}. O seu plano atual termina hoje e não volta a ser cobrado.',
  renewalToday:
    'Nada muda. Mantém o seu plano atual e os seus {balance} créditos, e não paga nada.',
  renewalTodayPaused:
    'Nada muda. Mantém o seu plano atual e os seus {balance} créditos, e não paga nada. A utilização continua em pausa até lá.',
  renewalThen:
    'O novo plano começa. Paga {price}{codeNote} e recebe {credits} nessa data, não antes.',
  footSe: 'Os valores incluem IVA de 25%. Cobramos no cartão indicado na sua página de Faturação.',
  footUnknown:
    'Os valores incluem IVA sueco de 25%. O IVA é acrescentado no pagamento, quando aplicável.',
  footReverse:
    'Os valores não incluem IVA, que é liquidado por autoliquidação. Cobramos no cartão indicado na sua página de Faturação.',
  cancel: 'Cancelar',
  confirmNow: 'Pagar {price} e mudar',
  confirmRenewal: 'Agendar mudança para {credits} créditos',
  paying: 'A pagar…',
  scheduling: 'A agendar…',
  toastSwitched: 'O seu novo plano está ativo. Foram adicionados {credits} créditos ao seu saldo.',
  toastScheduled: 'O seu novo plano começa a {date}. Até lá, nada muda.',
  toastCode: 'O desconto de {percent} aplica-se a partir de {date}, durante {months} meses.',
  toastCodeOneMonth: 'O desconto de {percent} aplica-se a partir de {date}, durante 1 mês.',
  toastCodeForGood: 'O desconto de {percent} aplica-se a partir de {date}.',
  errorDeclined:
    'O seu cartão foi recusado. Atualize-o em Método de pagamento e faturas e tente novamente.',
  errorDiscountActive: 'Já existe um desconto ativo. Mantém-se no novo plano.',
  errorComplimentary:
    'O seu plano é gratuito graças a um código, por isso um novo plano só pode começar na renovação.',
  errorCodeNotAccepted: 'Esse código não pode ser utilizado neste plano.',
  codeAlreadyUsed: 'A sua empresa já utilizou este código.',
  errorGeneric: 'Não foi possível alterar o plano. Tente novamente.',
  errorEditing: 'Conclua ou cancele a edição da sua pergunta anterior antes de abrir o pagamento.',
  errorAttachments:
    'O seu rascunho tem ficheiros anexados. Remova-os antes de abrir o pagamento. O texto da pergunta fica guardado e pode voltar a anexar os ficheiros depois.',
  errorStorage:
    'O seu navegador não conseguiu guardar o rascunho. Copie a pergunta para um local seguro e permita o armazenamento de sessão antes de abrir o pagamento.',
  planPerMonth: '{credits} créditos/mês',
  creditsCount: '{count} créditos',
  billingTitle: 'Faturação',
  billingIntro: 'O plano, os créditos e o pagamento da sua empresa.',
  planLabel: 'Plano',
  noPlan: 'Sem plano',
  planPaymentFailed: 'Pagamento falhou',
  planEndedOn: 'Terminou a {date}',
  planEnded: 'Terminado',
  codeNotPercent: 'Este código desconta um valor fixo. Aqui só funcionam códigos de percentagem.',
  balanceLabel: 'Créditos restantes',
  nextLabel: 'Próximo',
  renewsWith: '+{credits} créditos a {date}',
  switchesTo: 'Muda para {credits} créditos/mês a {date}',
  endsOn: 'Termina a {date}',
  discountLabel: 'Desconto',
  creditsNeverExpire: 'Os créditos nunca expiram.',
  paymentFailed:
    'O seu último pagamento não foi concluído. Atualize o cartão em Método de pagamento e faturas.',
  seeUsage: 'Ver utilização',
  changePlan: 'Alterar plano',
  choosePlan: 'Escolher um plano',
  portal: 'Método de pagamento e faturas',
  opening: 'A abrir…',
  readOnly: 'O proprietário da empresa gere o plano e o pagamento.',
  nativeOnly: 'Os planos e o pagamento são geridos na Web.',
  loading: 'A carregar a faturação…',
  retry: 'Tentar novamente',
  unavailable:
    'A faturação está temporariamente indisponível. Tente novamente. O seu histórico e as suas definições continuam disponíveis.',
  portalError: 'Não foi possível abrir as definições de pagamento. Tente novamente.',
  returnPending:
    'A aguardar a confirmação do pagamento. Os créditos aparecem quando o pagamento for confirmado.',
  returnDelayed:
    'A confirmação do pagamento está a demorar mais do que o esperado. Volte a verificar dentro de momentos.',
  returnCanceled: 'O pagamento foi cancelado. Não foi cobrado nada.',
  pausedPlanTitle: 'Escolha um plano para continuar',
  pausedPlanOwner:
    'Poneva funciona com planos mensais de créditos. Escolha um plano para continuar a fazer perguntas.',
  pausedPlanMember:
    'A sua empresa não tem nenhum plano ativo. O proprietário da empresa escolhe um em Definições, em Utilização.',
  pausedPastDue:
    'O seu último pagamento não foi concluído, por isso o plano está em pausa. Atualize o cartão em Método de pagamento e faturas.',
  pausedPastDueMember:
    'O último pagamento da empresa não foi concluído, por isso o plano está em pausa. O proprietário da empresa pode atualizar o cartão.',
  pausedCreditsTitle: 'Utilizou todos os seus créditos',
  pausedCreditsBody:
    'Mude agora para um plano superior ou aguarde até {date} às {time}, quando o seu plano adiciona {credits} créditos.',
  pausedCreditsMember:
    'Peça ao proprietário da empresa para mudar para um plano superior ou aguarde até {date} às {time}, quando o seu plano adiciona {credits} créditos.',
  pausedCreditsNoRenewal: 'Escolha um plano maior para continuar.',
  pausedLimitTitle: 'Atingiu o seu limite mensal',
  pausedLimitBody:
    'Utilizou o seu limite mensal de {limit} créditos. É reposto a {date} ou quando o proprietário da empresa o aumentar.',
  pausedLimitNoDate:
    'Utilizou o seu limite mensal de {limit} créditos. O proprietário da empresa pode aumentá-lo.',
  upgradeNow: 'Mudar para plano superior',
  limitNotice: 'Restam {left} dos seus {limit} créditos mensais. O seu limite é reposto a {date}.',
  limitNoticeNoDate: 'Restam {left} dos seus {limit} créditos mensais.',
};

const fi: Copy = {
  dialogTitle: 'Vaihda pakettia',
  dialogTitleNew: 'Valitse paketti',
  dialogSub:
    'Paketti lisää joka kuukausi krediittinsä saldoon, jonka koko tiimisi jakaa. Käyttämättömät krediitit siirtyvät eteenpäin eivätkä koskaan vanhene, ja voit käyttää niitä niin kauan kuin paketti on voimassa.',
  close: 'Sulje',
  kickerUpgrade: 'Suurempi paketti',
  kickerDowngrade: 'Pienempi paketti',
  currentPlanTag: 'Nykyinen paketti',
  currentPin: 'Nykyinen',
  creditsEveryMonth: 'krediittiä kuukaudessa',
  captionCurrent: 'Nykyinen pakettisi.',
  captionMore: '{count} krediittiä kuukaudessa enemmän kuin nykyisessä paketissasi.',
  captionFewer: '{count} krediittiä kuukaudessa vähemmän kuin nykyisessä paketissasi.',
  captionRecent: 'Tiimisi käytti {count} krediittiä viimeisten 30 päivän aikana.',
  scaleEnd: '{price} kuukaudessa',
  sliderLabel: 'Paketti',
  sliderValue: '{credits} krediittiä kuukaudessa, {price} alv 0 %',
  priceLabel: 'Hinta kuukaudessa',
  exclVat: 'alv 0 %',
  exclVatForMonths: 'alv 0 %, {months} kuukauden ajan',
  exclVatForOneMonth: 'alv 0 %, 1 kuukauden ajan',
  exclVatUntil: 'alv 0 %, {date} asti',
  perCreditLabel: 'Krediitin hinta',
  volumeLabel: 'Määräalennus',
  volumeNone: 'Ei alennusta',
  volumeNote: 'verrattuna pienimmän paketin hintaan {price} per krediitti',
  volumeBase: 'Pienin paketti määrää krediitin perushinnan',
  vatLabel: 'Sis. ALV 25 %',
  perMonth: 'kuukaudessa',
  vatReverseLabel: 'ALV',
  vatReverseValue: 'Käännetty verovelvollisuus',
  codeAdd: 'Lisää alennuskoodi',
  codePlaceholder: 'Alennuskoodi',
  codeApply: 'Käytä',
  codeChecking: 'Tarkistetaan…',
  codeNotFound: 'Koodia ei löytynyt. Tarkista kirjoitusasu ja yritä uudelleen.',
  codeRemove: 'Poista koodi',
  codeForMonths: '{percent} alennus {months} kuukauden ajan',
  codeForOneMonth: '{percent} alennus 1 kuukauden ajan',
  codeForGood: '{percent} alennus pysyvästi',
  codeUntil: '{percent} alennus {date} asti',
  codeCarries: '{percent} alennus {date} asti, siirtyy uuteen pakettiin',
  codeCarriesForGood: '{percent} alennus pysyvästi, siirtyy uuteen pakettiin',
  ctaSwitch: 'Vaihda {credits} krediitin pakettiin',
  ctaUseCode: 'Käytä koodia tähän pakettiin',
  ctaCurrent: 'Nykyinen pakettisi',
  ctaCheckout: 'Jatka maksamaan',
  ctaSaving: 'Tallennetaan…',
  ctaOpening: 'Avataan maksua…',
  sideFoot: 'Laskutetaan kuukausittain kortilta',
  back: 'Kaikki paketit',
  timingTitle: 'Milloin uusi paketti alkaa?',
  timingGroup: 'Uuden paketin alkamisajankohta',
  summaryInclVat: '{price} kuukaudessa, sis. ALV',
  summaryExclVat: '{price} kuukaudessa, alv 0 %',
  summaryOff: '{percent} alennus',
  nowTitle: 'Aloita nyt',
  renewalTitle: 'Uusiutuessa, {date}',
  today: 'Tänään',
  fromThenOn: 'Sen jälkeen',
  onDate: 'Päivänä {date}',
  codeNote: '({percent} alennus)',
  codeNoteUntil: '({percent} alennus {date} asti)',
  nowToday: 'Uusi paketti alkaa. Maksat {price}{codeNote} ja saat {credits} heti.',
  nowTodayOnTop:
    'Uusi paketti alkaa. Maksat {price}{codeNote} ja saat {credits} heti nykyisten {balance} krediitin lisäksi (yhteensä {total}).',
  nowThen:
    '{credits} ja {price} joka kuukausi sen jälkeen. Nykyinen pakettisi päättyy tänään, eikä sitä veloiteta enää.',
  nowThenUntil:
    '{credits} ja {price} joka kuukausi {date} asti, sen jälkeen {fullPrice}. Nykyinen pakettisi päättyy tänään, eikä sitä veloiteta enää.',
  renewalToday:
    'Mikään ei muutu. Pidät nykyisen pakettisi ja {balance} krediittiäsi etkä maksa mitään.',
  renewalTodayPaused:
    'Mikään ei muutu. Pidät nykyisen pakettisi ja {balance} krediittiäsi etkä maksa mitään. Käyttö on keskeytetty siihen asti.',
  renewalThen:
    'Uusi paketti alkaa. Maksat {price}{codeNote} ja saat {credits} silloin, et aiemmin.',
  footSe: 'Summat sisältävät ALV:n 25 %. Veloitamme Laskutus-sivulla olevaa korttia.',
  footUnknown: 'Summat sisältävät Ruotsin ALV:n 25 %. ALV lisätään tarvittaessa kassalla.',
  footReverse:
    'Summat eivät sisällä ALV:tä, koska sovelletaan käännettyä verovelvollisuutta. Veloitamme Laskutus-sivulla olevaa korttia.',
  cancel: 'Peruuta',
  confirmNow: 'Maksa {price} ja vaihda',
  confirmRenewal: 'Ajoita vaihto {credits} krediitin pakettiin',
  paying: 'Maksetaan…',
  scheduling: 'Ajoitetaan…',
  toastSwitched: 'Uusi pakettisi on käytössä. Saldoosi lisättiin {credits} krediittiä.',
  toastScheduled: 'Uusi pakettisi alkaa {date}. Siihen asti mikään ei muutu.',
  toastCode: '{percent} alennus on voimassa {date} alkaen {months} kuukauden ajan.',
  toastCodeOneMonth: '{percent} alennus on voimassa {date} alkaen 1 kuukauden ajan.',
  toastCodeForGood: '{percent} alennus on voimassa {date} alkaen.',
  errorDeclined: 'Korttisi hylättiin. Päivitä se kohdassa Maksutapa ja laskut ja yritä uudelleen.',
  errorDiscountActive: 'Alennus on jo voimassa. Se siirtyy uuteen pakettiin.',
  errorComplimentary:
    'Pakettisi on maksuton koodin ansiosta, joten uusi paketti voi alkaa vasta uusiutumisen yhteydessä.',
  errorCodeNotAccepted: 'Tätä koodia ei voi käyttää tähän pakettiin.',
  codeAlreadyUsed: 'Yrityksesi on jo käyttänyt tämän koodin.',
  errorGeneric: 'Pakettia ei voitu vaihtaa. Yritä uudelleen.',
  errorEditing: 'Viimeistele tai peruuta aiemman kysymyksesi muokkaus ennen maksun avaamista.',
  errorAttachments:
    'Luonnoksessasi on liitetiedostoja. Poista ne ennen maksun avaamista. Kysymyksesi teksti on tallessa, ja voit liittää tiedostot uudelleen myöhemmin.',
  errorStorage:
    'Selaimesi ei voinut tallentaa luonnostasi. Kopioi kysymyksesi turvaan ja salli istuntotallennus ennen maksun avaamista.',
  planPerMonth: '{credits} krediittiä / kk',
  creditsCount: '{count} krediittiä',
  billingTitle: 'Laskutus',
  billingIntro: 'Yrityksesi paketti, krediitit ja maksut.',
  planLabel: 'Paketti',
  noPlan: 'Ei pakettia',
  planPaymentFailed: 'Maksu epäonnistui',
  planEndedOn: 'Päättyi {date}',
  planEnded: 'Päättynyt',
  codeNotPercent: 'Tämä koodi vähentää kiinteän summan. Täällä toimivat vain prosenttikoodit.',
  balanceLabel: 'Krediittejä jäljellä',
  nextLabel: 'Seuraavaksi',
  renewsWith: '+{credits} krediittiä {date}',
  switchesTo: 'Vaihtuu {date}: {credits} krediittiä / kk',
  endsOn: 'Päättyy {date}',
  discountLabel: 'Alennus',
  creditsNeverExpire: 'Krediitit eivät koskaan vanhene.',
  paymentFailed: 'Viimeisin maksusi epäonnistui. Päivitä kortti kohdassa Maksutapa ja laskut.',
  seeUsage: 'Näytä käyttö',
  changePlan: 'Vaihda pakettia',
  choosePlan: 'Valitse paketti',
  portal: 'Maksutapa ja laskut',
  opening: 'Avataan…',
  readOnly: 'Yrityksen omistaja hallinnoi pakettia ja maksuja.',
  nativeOnly: 'Paketteja ja maksuja hallitaan verkossa.',
  loading: 'Ladataan laskutusta…',
  retry: 'Yritä uudelleen',
  unavailable:
    'Laskutus ei ole tilapäisesti käytettävissä. Yritä uudelleen. Historiasi ja asetuksesi ovat edelleen käytettävissä.',
  portalError: 'Maksuasetuksia ei voitu avata. Yritä uudelleen.',
  returnPending: 'Odotetaan maksun vahvistusta. Krediitit näkyvät, kun maksu on vahvistettu.',
  returnDelayed: 'Maksun vahvistus kestää odotettua kauemmin. Tarkista hetken kuluttua uudelleen.',
  returnCanceled: 'Maksu peruutettiin. Mitään ei veloitettu.',
  pausedPlanTitle: 'Valitse paketti jatkaaksesi',
  pausedPlanOwner:
    'Poneva toimii kuukausittaisilla krediittipaketeilla. Valitse paketti, niin voit jatkaa kysymistä.',
  pausedPlanMember:
    'Yritykselläsi ei ole voimassa olevaa pakettia. Yrityksen omistaja valitsee paketin kohdassa Asetukset, sivulla Käyttö.',
  pausedPastDue:
    'Viimeisin maksusi epäonnistui, joten paketti on keskeytetty. Päivitä kortti kohdassa Maksutapa ja laskut.',
  pausedPastDueMember:
    'Yrityksen viimeisin maksu epäonnistui, joten paketti on keskeytetty. Yrityksen omistaja voi päivittää kortin.',
  pausedCreditsTitle: 'Olet käyttänyt kaikki krediittisi',
  pausedCreditsBody:
    'Vaihda suurempaan pakettiin nyt tai odota {date} klo {time} asti, jolloin pakettisi lisää {credits} krediittiä.',
  pausedCreditsMember:
    'Pyydä yrityksen omistajaa vaihtamaan suurempaan pakettiin tai odota {date} klo {time} asti, jolloin pakettisi lisää {credits} krediittiä.',
  pausedCreditsNoRenewal: 'Valitse suurempi paketti jatkaaksesi.',
  pausedLimitTitle: 'Olet saavuttanut kuukausirajasi',
  pausedLimitBody:
    'Olet käyttänyt {limit} krediitin kuukausirajasi. Se nollautuu {date} tai kun yrityksen omistaja nostaa sitä.',
  pausedLimitNoDate:
    'Olet käyttänyt {limit} krediitin kuukausirajasi. Yrityksen omistaja voi nostaa sitä.',
  upgradeNow: 'Vaihda suurempaan nyt',
  limitNotice: 'Kuukausirajastasi ({limit} krediittiä) on jäljellä {left}. Raja nollautuu {date}.',
  limitNoticeNoDate: 'Kuukausirajastasi ({limit} krediittiä) on jäljellä {left}.',
};

const et: Copy = {
  dialogTitle: 'Muuda paketti',
  dialogTitleNew: 'Vali pakett',
  dialogSub:
    'Iga kuu lisab pakett oma krediidid saldole, mida kogu su meeskond jagab. Kasutamata krediidid kanduvad edasi ega aegu kunagi ning saad neid kasutada seni, kuni pakett on aktiivne.',
  close: 'Sulge',
  kickerUpgrade: 'Suurem pakett',
  kickerDowngrade: 'Väiksem pakett',
  currentPlanTag: 'Praegune pakett',
  currentPin: 'Praegune',
  creditsEveryMonth: 'krediiti iga kuu',
  captionCurrent: 'Sinu praegune pakett.',
  captionMore: '{count} krediiti kuus rohkem kui praeguses paketis.',
  captionFewer: '{count} krediiti kuus vähem kui praeguses paketis.',
  captionRecent: 'Sinu meeskond kasutas viimase 30 päeva jooksul {count} krediiti.',
  scaleEnd: '{price} kuus',
  sliderLabel: 'Pakett',
  sliderValue: '{credits} krediiti kuus, {price} km-ta',
  priceLabel: 'Hind kuus',
  exclVat: 'km-ta',
  exclVatForMonths: 'km-ta, {months} kuu jooksul',
  exclVatForOneMonth: 'km-ta, 1 kuu jooksul',
  exclVatUntil: 'km-ta, kuni {date}',
  perCreditLabel: 'Krediidi hind',
  volumeLabel: 'Mahuallahindlus',
  volumeNone: 'Puudub',
  volumeNote: 'võrreldes väikseima paketi hinnaga {price} krediidi kohta',
  volumeBase: 'Väikseim pakett määrab krediidi baashinna',
  vatLabel: 'Sh käibemaks 25%',
  perMonth: 'kuus',
  vatReverseLabel: 'Käibemaks',
  vatReverseValue: 'Pöördmaksustamine',
  codeAdd: 'Lisa sooduskood',
  codePlaceholder: 'Sooduskood',
  codeApply: 'Rakenda',
  codeChecking: 'Kontrollin…',
  codeNotFound: 'Seda koodi ei leitud. Kontrolli õigekirja ja proovi uuesti.',
  codeRemove: 'Eemalda kood',
  codeForMonths: '{percent} soodustust {months} kuu jooksul',
  codeForOneMonth: '{percent} soodustust 1 kuu jooksul',
  codeForGood: '{percent} soodustust jäädavalt',
  codeUntil: '{percent} soodustust kuni {date}',
  codeCarries: '{percent} soodustust kuni {date}, kandub üle',
  codeCarriesForGood: '{percent} soodustust jäädavalt, kandub üle',
  ctaSwitch: 'Vaheta {credits} krediidi paketile',
  ctaUseCode: 'Kasuta koodi selle paketi puhul',
  ctaCurrent: 'Sinu praegune pakett',
  ctaCheckout: 'Jätka maksmisega',
  ctaSaving: 'Salvestan…',
  ctaOpening: 'Avan makset…',
  sideFoot: 'Arveldatakse igakuiselt kaardiga',
  back: 'Kõik paketid',
  timingTitle: 'Millal peaks uus pakett algama?',
  timingGroup: 'Uue paketi algus',
  summaryInclVat: '{price} kuus, sh käibemaks',
  summaryExclVat: '{price} kuus, km-ta',
  summaryOff: '{percent} soodustust',
  nowTitle: 'Alusta kohe',
  renewalTitle: 'Uuendamisel, {date}',
  today: 'Täna',
  fromThenOn: 'Edaspidi',
  onDate: 'Kuupäeval {date}',
  codeNote: '({percent} soodustust)',
  codeNoteUntil: '({percent} soodustust kuni {date})',
  nowToday: 'Uus pakett algab. Maksad {price}{codeNote} ja saad kohe {credits}.',
  nowTodayOnTop:
    'Uus pakett algab. Maksad {price}{codeNote} ja saad kohe {credits} lisaks olemasolevale {balance} krediidile (kokku {total}).',
  nowThen:
    '{credits} ja {price} iga kuu pärast seda. Sinu praegune pakett lõpeb täna ja selle eest enam tasu ei võeta.',
  nowThenUntil:
    '{credits} ja {price} iga kuu kuni {date}, seejärel {fullPrice}. Sinu praegune pakett lõpeb täna ja selle eest enam tasu ei võeta.',
  renewalToday:
    'Midagi ei muutu. Sinu praegune pakett ja {balance} krediiti jäävad alles ning sa ei maksa midagi.',
  renewalTodayPaused:
    'Midagi ei muutu. Sinu praegune pakett ja {balance} krediiti jäävad alles ning sa ei maksa midagi. Kasutus on seni peatatud.',
  renewalThen:
    'Uus pakett algab. Maksad {price}{codeNote} ja saad {credits} alles siis, mitte varem.',
  footSe: 'Summad sisaldavad 25% käibemaksu. Võtame tasu kaardilt, mis on lisatud Arvelduse lehel.',
  footUnknown:
    'Summad sisaldavad 25% Rootsi käibemaksu. Käibemaks lisatakse maksmisel, kui see kehtib.',
  footReverse:
    'Summad on käibemaksuta, kuna kohaldub pöördmaksustamine. Võtame tasu kaardilt, mis on lisatud Arvelduse lehel.',
  cancel: 'Tühista',
  confirmNow: 'Maksa {price} ja vaheta',
  confirmRenewal: 'Planeeri üleminek {credits} krediidi paketile',
  paying: 'Maksan…',
  scheduling: 'Planeerin…',
  toastSwitched: 'Sinu uus pakett on aktiivne. Saldole lisati {credits} krediiti.',
  toastScheduled: 'Sinu uus pakett algab {date}. Seni ei muutu midagi.',
  toastCode: '{percent} soodustus kehtib alates {date}, {months} kuu jooksul.',
  toastCodeOneMonth: '{percent} soodustus kehtib alates {date}, 1 kuu jooksul.',
  toastCodeForGood: '{percent} soodustus kehtib alates {date}.',
  errorDeclined:
    'Sinu kaart lükati tagasi. Uuenda seda jaotises Makseviis ja arved ning proovi uuesti.',
  errorDiscountActive: 'Soodustus on juba aktiivne. See kandub üle uuele paketile.',
  errorComplimentary:
    'Sinu pakett on koodiga tasuta, seega saab uus pakett alata alles uuendamisel.',
  errorCodeNotAccepted: 'Seda koodi ei saa selle paketi puhul kasutada.',
  codeAlreadyUsed: 'Sinu ettevõte on seda koodi juba kasutanud.',
  errorGeneric: 'Paketti ei õnnestunud muuta. Proovi uuesti.',
  errorEditing: 'Lõpeta või tühista oma varasema küsimuse muutmine enne makse avamist.',
  errorAttachments:
    'Sinu mustandil on manused. Eemalda need enne makse avamist. Küsimuse tekst on salvestatud ja saad failid hiljem uuesti lisada.',
  errorStorage:
    'Brauser ei saanud mustandit salvestada. Kopeeri küsimus turvalisse kohta ja luba seansi salvestusruum enne makse avamist.',
  planPerMonth: '{credits} krediiti / kuu',
  creditsCount: '{count} krediiti',
  billingTitle: 'Arveldus',
  billingIntro: 'Sinu ettevõtte pakett, krediidid ja maksed.',
  planLabel: 'Pakett',
  noPlan: 'Paketti pole',
  planPaymentFailed: 'Makse ebaõnnestus',
  planEndedOn: 'Lõppes {date}',
  planEnded: 'Lõppenud',
  codeNotPercent: 'See kood annab kindla summa allahindlust. Siin töötavad ainult protsendikoodid.',
  balanceLabel: 'Krediite alles',
  nextLabel: 'Järgmine',
  renewsWith: '+{credits} krediiti {date}',
  switchesTo: 'Vahetub {date}: {credits} krediiti / kuu',
  endsOn: 'Lõpeb {date}',
  discountLabel: 'Soodustus',
  creditsNeverExpire: 'Krediidid ei aegu kunagi.',
  paymentFailed: 'Viimane makse ei läinud läbi. Uuenda kaarti jaotises Makseviis ja arved.',
  seeUsage: 'Vaata kasutust',
  changePlan: 'Muuda paketti',
  choosePlan: 'Vali pakett',
  portal: 'Makseviis ja arved',
  opening: 'Avan…',
  readOnly: 'Paketti ja makseid haldab ettevõtte omanik.',
  nativeOnly: 'Pakette ja makseid hallatakse veebis.',
  loading: 'Laadin arveldust…',
  retry: 'Proovi uuesti',
  unavailable:
    'Arveldus pole ajutiselt saadaval. Proovi uuesti. Sinu ajalugu ja seaded on endiselt saadaval.',
  portalError: 'Makseseadeid ei õnnestunud avada. Proovi uuesti.',
  returnPending: 'Ootame makse kinnitust. Krediidid ilmuvad pärast makse kinnitamist.',
  returnDelayed: 'Makse kinnitamine võtab oodatust kauem aega. Kontrolli varsti uuesti.',
  returnCanceled: 'Maksmine tühistati. Tasu ei võetud.',
  pausedPlanTitle: 'Jätkamiseks vali pakett',
  pausedPlanOwner:
    'Poneva töötab igakuiste krediidipakettidega. Vali pakett, et küsimuste esitamist jätkata.',
  pausedPlanMember:
    'Sinu ettevõttel pole aktiivset paketti. Ettevõtte omanik valib selle jaotises Seaded, lehel Kasutus.',
  pausedPastDue:
    'Viimane makse ei läinud läbi, seega on pakett peatatud. Uuenda kaarti jaotises Makseviis ja arved.',
  pausedPastDueMember:
    'Ettevõtte viimane makse ei läinud läbi, seega on pakett peatatud. Ettevõtte omanik saab kaarti uuendada.',
  pausedCreditsTitle: 'Oled kõik krediidid ära kasutanud',
  pausedCreditsBody:
    'Vali kohe suurem pakett või oota kuni {date} kell {time}, mil pakett lisab {credits} krediiti.',
  pausedCreditsMember:
    'Palu ettevõtte omanikul valida suurem pakett või oota kuni {date} kell {time}, mil pakett lisab {credits} krediiti.',
  pausedCreditsNoRenewal: 'Jätkamiseks vali suurem pakett.',
  pausedLimitTitle: 'Oled jõudnud oma kuulimiidini',
  pausedLimitBody:
    'Oled kasutanud ära oma {limit} krediidi suuruse kuulimiidi. See lähtestub {date} või siis, kui ettevõtte omanik seda tõstab.',
  pausedLimitNoDate:
    'Oled kasutanud ära oma {limit} krediidi suuruse kuulimiidi. Ettevõtte omanik saab seda tõsta.',
  upgradeNow: 'Vali suurem pakett',
  limitNotice:
    'Sinu {limit} krediidi suurusest kuulimiidist on alles {left}. Limiit lähtestub {date}.',
  limitNoticeNoDate: 'Sinu {limit} krediidi suurusest kuulimiidist on alles {left}.',
};

const lv: Copy = {
  dialogTitle: 'Mainīt plānu',
  dialogTitleNew: 'Izvēlieties plānu',
  dialogSub:
    'Katru mēnesi plāns pievieno savus kredītus atlikumam, ko kopīgi izmanto visa Jūsu komanda. Neizmantotie kredīti tiek pārnesti un nekad nezaudē derīgumu, un Jūs varat tos izmantot, kamēr plāns ir aktīvs.',
  close: 'Aizvērt',
  kickerUpgrade: 'Lielāks plāns',
  kickerDowngrade: 'Mazāks plāns',
  currentPlanTag: 'Pašreizējais plāns',
  currentPin: 'Pašreizējais',
  creditsEveryMonth: 'kredīti katru mēnesi',
  captionCurrent: 'Jūsu pašreizējais plāns.',
  captionMore: 'Par {count} kredītiem mēnesī vairāk nekā pašreizējā plānā.',
  captionFewer: 'Par {count} kredītiem mēnesī mazāk nekā pašreizējā plānā.',
  captionRecent: 'Jūsu komanda pēdējās 30 dienās izmantoja {count} kredītus.',
  scaleEnd: '{price} mēnesī',
  sliderLabel: 'Plāns',
  sliderValue: '{credits} kredīti mēnesī, {price} bez PVN',
  priceLabel: 'Cena mēnesī',
  exclVat: 'bez PVN',
  exclVatForMonths: 'bez PVN, {months} mēnešus',
  exclVatForOneMonth: 'bez PVN, 1 mēnesi',
  exclVatUntil: 'bez PVN, līdz {date}',
  perCreditLabel: 'Cena par kredītu',
  volumeLabel: 'Apjoma atlaide',
  volumeNone: 'Nav',
  volumeNote: 'salīdzinot ar mazākā plāna cenu {price} par kredītu',
  volumeBase: 'Mazākais plāns nosaka kredīta pamatcenu',
  vatLabel: 'Ar 25% PVN',
  perMonth: 'mēnesī',
  vatReverseLabel: 'PVN',
  vatReverseValue: 'Apgrieztā maksāšana',
  codeAdd: 'Pievienot atlaides kodu',
  codePlaceholder: 'Atlaides kods',
  codeApply: 'Lietot',
  codeChecking: 'Pārbauda…',
  codeNotFound: 'Šo kodu neizdevās atrast. Pārbaudiet rakstību un mēģiniet vēlreiz.',
  codeRemove: 'Noņemt kodu',
  codeForMonths: '{percent} atlaide {months} mēnešus',
  codeForOneMonth: '{percent} atlaide 1 mēnesi',
  codeForGood: '{percent} atlaide uz visiem laikiem',
  codeUntil: '{percent} atlaide līdz {date}',
  codeCarries: '{percent} atlaide līdz {date}, tiek pārnesta',
  codeCarriesForGood: '{percent} atlaide uz visiem laikiem, tiek pārnesta',
  ctaSwitch: 'Pāriet uz {credits} kredītu plānu',
  ctaUseCode: 'Izmantot kodu šim plānam',
  ctaCurrent: 'Jūsu pašreizējais plāns',
  ctaCheckout: 'Turpināt uz apmaksu',
  ctaSaving: 'Saglabā…',
  ctaOpening: 'Atver apmaksu…',
  sideFoot: 'Ikmēneša maksājums ar karti',
  back: 'Visi plāni',
  timingTitle: 'Kad jāsākas jaunajam plānam?',
  timingGroup: 'Jaunā plāna sākums',
  summaryInclVat: '{price} mēnesī ar PVN',
  summaryExclVat: '{price} mēnesī bez PVN',
  summaryOff: '{percent} atlaide',
  nowTitle: 'Sākt tūlīt',
  renewalTitle: 'Atjaunošanas brīdī, {date}',
  today: 'Šodien',
  fromThenOn: 'Turpmāk',
  onDate: 'Datums: {date}',
  codeNote: '({percent} atlaide)',
  codeNoteUntil: '({percent} atlaide līdz {date})',
  nowToday: 'Sākas jaunais plāns. Jūs maksājat {price}{codeNote} un uzreiz saņemat {credits}.',
  nowTodayOnTop:
    'Sākas jaunais plāns. Jūs maksājat {price}{codeNote} un uzreiz saņemat {credits} papildus esošajiem {balance} kredītiem (kopā {total}).',
  nowThen:
    '{credits} un {price} katru mēnesi pēc tam. Pašreizējais plāns beidzas šodien, un par to vairs netiks iekasēta maksa.',
  nowThenUntil:
    '{credits} un {price} katru mēnesi līdz {date}, pēc tam {fullPrice}. Pašreizējais plāns beidzas šodien, un par to vairs netiks iekasēta maksa.',
  renewalToday:
    'Nekas nemainās. Jūs paturat pašreizējo plānu un savus {balance} kredītus un neko nemaksājat.',
  renewalTodayPaused:
    'Nekas nemainās. Jūs paturat pašreizējo plānu un savus {balance} kredītus un neko nemaksājat. Līdz tam lietošana ir apturēta.',
  renewalThen:
    'Sākas jaunais plāns. Jūs maksājat {price}{codeNote} un saņemat {credits} tad, ne agrāk.',
  footSe: 'Summās ir iekļauts 25% PVN. Maksu iekasējam no kartes, kas norādīta lapā Norēķini.',
  footUnknown:
    'Summās ir iekļauts 25% Zviedrijas PVN. PVN tiek pievienots apmaksas laikā, ja tas ir piemērojams.',
  footReverse:
    'Summās nav iekļauts PVN, jo tiek piemērota apgrieztā maksāšana. Maksu iekasējam no kartes, kas norādīta lapā Norēķini.',
  cancel: 'Atcelt',
  confirmNow: 'Samaksāt {price} un pāriet',
  confirmRenewal: 'Ieplānot pāreju uz {credits} kredītu plānu',
  paying: 'Notiek maksājums…',
  scheduling: 'Plāno…',
  toastSwitched: 'Jūsu jaunais plāns ir aktīvs. Atlikumam pievienoti {credits} kredīti.',
  toastScheduled: 'Jūsu jaunais plāns sāksies {date}. Līdz tam nekas nemainās.',
  toastCode: '{percent} atlaide ir spēkā no {date}, {months} mēnešus.',
  toastCodeOneMonth: '{percent} atlaide ir spēkā no {date}, 1 mēnesi.',
  toastCodeForGood: '{percent} atlaide ir spēkā no {date}.',
  errorDeclined:
    'Jūsu karte tika noraidīta. Atjauniniet to sadaļā Maksājuma veids un rēķini un mēģiniet vēlreiz.',
  errorDiscountActive: 'Atlaide jau ir aktīva. Tā tiek pārnesta uz jauno plānu.',
  errorComplimentary:
    'Jūsu plāns ir bezmaksas ar kodu, tāpēc jauns plāns var sākties tikai atjaunošanas brīdī.',
  errorCodeNotAccepted: 'Šo kodu nevar izmantot šim plānam.',
  codeAlreadyUsed: 'Jūsu uzņēmums jau ir izmantojis šo kodu.',
  errorGeneric: 'Plānu neizdevās mainīt. Lūdzu, mēģiniet vēlreiz.',
  errorEditing:
    'Pirms apmaksas atvēršanas pabeidziet vai atceliet iepriekšējā jautājuma rediģēšanu.',
  errorAttachments:
    'Jūsu melnrakstam ir pievienoti faili. Pirms apmaksas atvēršanas noņemiet tos. Jautājuma teksts ir saglabāts, un failus vēlāk varēsiet pievienot vēlreiz.',
  errorStorage:
    'Pārlūkprogramma nevarēja saglabāt melnrakstu. Pirms apmaksas atvēršanas nokopējiet jautājumu drošā vietā un atļaujiet sesijas krātuvi.',
  planPerMonth: '{credits} kredīti / mēnesī',
  creditsCount: '{count} kredīti',
  billingTitle: 'Norēķini',
  billingIntro: 'Jūsu uzņēmuma plāns, kredīti un maksājumi.',
  planLabel: 'Plāns',
  noPlan: 'Nav plāna',
  planPaymentFailed: 'Maksājums neizdevās',
  planEndedOn: 'Beidzās {date}',
  planEnded: 'Beidzies',
  codeNotPercent: 'Šis kods dod fiksētas summas atlaidi. Šeit darbojas tikai procentu kodi.',
  balanceLabel: 'Atlikušie kredīti',
  nextLabel: 'Nākamais',
  renewsWith: '+{credits} kredīti {date}',
  switchesTo: '{date} mainās uz {credits} kredītiem mēnesī',
  endsOn: 'Beidzas {date}',
  discountLabel: 'Atlaide',
  creditsNeverExpire: 'Kredīti nekad nezaudē derīgumu.',
  paymentFailed:
    'Jūsu pēdējais maksājums neizdevās. Atjauniniet karti sadaļā Maksājuma veids un rēķini.',
  seeUsage: 'Skatīt lietojumu',
  changePlan: 'Mainīt plānu',
  choosePlan: 'Izvēlēties plānu',
  portal: 'Maksājuma veids un rēķini',
  opening: 'Atver…',
  readOnly: 'Plānu un maksājumus pārvalda uzņēmuma īpašnieks.',
  nativeOnly: 'Plāni un maksājumi tiek pārvaldīti tīmeklī.',
  loading: 'Ielādē norēķinus…',
  retry: 'Mēģināt vēlreiz',
  unavailable:
    'Norēķini īslaicīgi nav pieejami. Lūdzu, mēģiniet vēlreiz. Jūsu vēsture un iestatījumi joprojām ir pieejami.',
  portalError: 'Maksājumu iestatījumus neizdevās atvērt. Lūdzu, mēģiniet vēlreiz.',
  returnPending:
    'Gaidām maksājuma apstiprinājumu. Kredīti parādīsies, tiklīdz maksājums būs apstiprināts.',
  returnDelayed:
    'Maksājuma apstiprināšana aizņem ilgāku laiku nekā gaidīts. Drīz pārbaudiet vēlreiz.',
  returnCanceled: 'Apmaksa tika atcelta. Nekas netika iekasēts.',
  pausedPlanTitle: 'Lai turpinātu, izvēlieties plānu',
  pausedPlanOwner:
    'Poneva darbojas ar ikmēneša kredītu plāniem. Izvēlieties plānu, lai turpinātu uzdot jautājumus.',
  pausedPlanMember:
    'Jūsu uzņēmumam nav aktīva plāna. Uzņēmuma īpašnieks to izvēlas sadaļā Iestatījumi, lapā Lietojums.',
  pausedPastDue:
    'Jūsu pēdējais maksājums neizdevās, tāpēc plāns ir apturēts. Atjauniniet karti sadaļā Maksājuma veids un rēķini.',
  pausedPastDueMember:
    'Uzņēmuma pēdējais maksājums neizdevās, tāpēc plāns ir apturēts. Uzņēmuma īpašnieks var atjaunināt karti.',
  pausedCreditsTitle: 'Visi kredīti ir izmantoti',
  pausedCreditsBody:
    'Pārejiet uz lielāku plānu tūlīt vai gaidiet līdz {date} plkst. {time}, kad plāns pievienos {credits} kredītus.',
  pausedCreditsMember:
    'Lūdziet uzņēmuma īpašniekam pāriet uz lielāku plānu vai gaidiet līdz {date} plkst. {time}, kad plāns pievienos {credits} kredītus.',
  pausedCreditsNoRenewal: 'Lai turpinātu, izvēlieties lielāku plānu.',
  pausedLimitTitle: 'Sasniegts mēneša limits',
  pausedLimitBody:
    'Jūsu mēneša limits, {limit} kredīti, ir izmantots. Tas tiks atiestatīts {date} vai tad, kad uzņēmuma īpašnieks to palielinās.',
  pausedLimitNoDate:
    'Jūsu mēneša limits, {limit} kredīti, ir izmantots. Uzņēmuma īpašnieks var to palielināt.',
  upgradeNow: 'Pāriet uz lielāku plānu',
  limitNotice: 'Atlikuši {left} no Jūsu {limit} mēneša kredītiem. Limits tiks atiestatīts {date}.',
  limitNoticeNoDate: 'Atlikuši {left} no Jūsu {limit} mēneša kredītiem.',
};

const lt: Copy = {
  dialogTitle: 'Keisti planą',
  dialogTitleNew: 'Pasirinkite planą',
  dialogSub:
    'Kiekvieną mėnesį planas prideda savo kreditus prie likučio, kuriuo dalijasi visa jūsų komanda. Nepanaudoti kreditai perkeliami ir niekada nenustoja galioti, o juos galite naudoti, kol planas yra aktyvus.',
  close: 'Uždaryti',
  kickerUpgrade: 'Didesnis planas',
  kickerDowngrade: 'Mažesnis planas',
  currentPlanTag: 'Dabartinis planas',
  currentPin: 'Dabartinis',
  creditsEveryMonth: 'kreditų kas mėnesį',
  captionCurrent: 'Jūsų dabartinis planas.',
  captionMore: '{count} kreditų per mėnesį daugiau nei dabartiniame plane.',
  captionFewer: '{count} kreditų per mėnesį mažiau nei dabartiniame plane.',
  captionRecent: 'Per pastarąsias 30 dienų jūsų komanda panaudojo {count} kreditų.',
  scaleEnd: '{price} per mėnesį',
  sliderLabel: 'Planas',
  sliderValue: '{credits} kreditų per mėnesį, {price} be PVM',
  priceLabel: 'Kaina per mėnesį',
  exclVat: 'be PVM',
  exclVatForMonths: 'be PVM, {months} mėn.',
  exclVatForOneMonth: 'be PVM, 1 mėn.',
  exclVatUntil: 'be PVM, iki {date}',
  perCreditLabel: 'Vieno kredito kaina',
  volumeLabel: 'Kiekio nuolaida',
  volumeNone: 'Nėra',
  volumeNote: 'palyginti su mažiausio plano kaina {price} už kreditą',
  volumeBase: 'Mažiausias planas nustato bazinę kredito kainą',
  vatLabel: 'Su 25 % PVM',
  perMonth: 'per mėnesį',
  vatReverseLabel: 'PVM',
  vatReverseValue: 'Atvirkštinis apmokestinimas',
  codeAdd: 'Pridėti nuolaidos kodą',
  codePlaceholder: 'Nuolaidos kodas',
  codeApply: 'Taikyti',
  codeChecking: 'Tikrinama…',
  codeNotFound: 'Tokio kodo neradome. Patikrinkite rašybą ir bandykite dar kartą.',
  codeRemove: 'Pašalinti kodą',
  codeForMonths: '{percent} nuolaida {months} mėn.',
  codeForOneMonth: '{percent} nuolaida 1 mėn.',
  codeForGood: '{percent} nuolaida visam laikui',
  codeUntil: '{percent} nuolaida iki {date}',
  codeCarries: '{percent} nuolaida iki {date}, perkeliama',
  codeCarriesForGood: '{percent} nuolaida visam laikui, perkeliama',
  ctaSwitch: 'Pereiti prie {credits} kreditų plano',
  ctaUseCode: 'Taikyti kodą šiam planui',
  ctaCurrent: 'Jūsų dabartinis planas',
  ctaCheckout: 'Tęsti apmokėjimą',
  ctaSaving: 'Išsaugoma…',
  ctaOpening: 'Atidaromas apmokėjimas…',
  sideFoot: 'Mokestis kas mėnesį nuskaitomas nuo kortelės',
  back: 'Visi planai',
  timingTitle: 'Kada turėtų prasidėti naujas planas?',
  timingGroup: 'Naujo plano pradžia',
  summaryInclVat: '{price} per mėnesį su PVM',
  summaryExclVat: '{price} per mėnesį be PVM',
  summaryOff: '{percent} nuolaida',
  nowTitle: 'Pradėti dabar',
  renewalTitle: 'Atnaujinimo metu, {date}',
  today: 'Šiandien',
  fromThenOn: 'Vėliau',
  onDate: 'Data: {date}',
  codeNote: '({percent} nuolaida)',
  codeNoteUntil: '({percent} nuolaida iki {date})',
  nowToday: 'Prasideda naujas planas. Sumokate {price}{codeNote} ir iškart gaunate {credits}.',
  nowTodayOnTop:
    'Prasideda naujas planas. Sumokate {price}{codeNote} ir iškart gaunate {credits}, be jau turimų {balance} kreditų (iš viso {total}).',
  nowThen:
    '{credits} ir {price} kas mėnesį po to. Dabartinis planas baigiasi šiandien, ir už jį daugiau mokestis nebus imamas.',
  nowThenUntil:
    '{credits} ir {price} kas mėnesį iki {date}, vėliau {fullPrice}. Dabartinis planas baigiasi šiandien, ir už jį daugiau mokestis nebus imamas.',
  renewalToday:
    'Niekas nesikeičia. Pasiliekate dabartinį planą ir savo {balance} kreditų ir nieko nemokate.',
  renewalTodayPaused:
    'Niekas nesikeičia. Pasiliekate dabartinį planą ir savo {balance} kreditų ir nieko nemokate. Iki tol naudojimas sustabdytas.',
  renewalThen:
    'Prasideda naujas planas. Sumokate {price}{codeNote} ir gaunate {credits} tada, ne anksčiau.',
  footSe:
    'Sumos nurodytos su 25 % PVM. Mokestis nuskaitomas nuo kortelės, nurodytos puslapyje Atsiskaitymai.',
  footUnknown: 'Sumos nurodytos su 25 % Švedijos PVM. PVM pridedamas apmokant, jei jis taikomas.',
  footReverse:
    'Sumos nurodytos be PVM, nes taikomas atvirkštinis apmokestinimas. Mokestis nuskaitomas nuo kortelės, nurodytos puslapyje Atsiskaitymai.',
  cancel: 'Atšaukti',
  confirmNow: 'Sumokėti {price} ir pereiti',
  confirmRenewal: 'Suplanuoti perėjimą prie {credits} kreditų plano',
  paying: 'Mokama…',
  scheduling: 'Planuojama…',
  toastSwitched: 'Naujas planas aktyvus. Prie likučio pridėta {credits} kreditų.',
  toastScheduled: 'Naujas planas prasidės {date}. Iki tol niekas nesikeis.',
  toastCode: '{percent} nuolaida taikoma nuo {date}, {months} mėn.',
  toastCodeOneMonth: '{percent} nuolaida taikoma nuo {date}, 1 mėn.',
  toastCodeForGood: '{percent} nuolaida taikoma nuo {date}.',
  errorDeclined:
    'Jūsų kortelė atmesta. Atnaujinkite ją skiltyje Mokėjimo būdas ir sąskaitos ir bandykite dar kartą.',
  errorDiscountActive: 'Nuolaida jau taikoma. Ji bus perkelta į naują planą.',
  errorComplimentary:
    'Jūsų planas nemokamas pagal kodą, todėl naujas planas gali prasidėti tik atnaujinimo metu.',
  errorCodeNotAccepted: 'Šio kodo negalima taikyti šiam planui.',
  codeAlreadyUsed: 'Jūsų įmonė jau panaudojo šį kodą.',
  errorGeneric: 'Nepavyko pakeisti plano. Bandykite dar kartą.',
  errorEditing:
    'Prieš atidarydami apmokėjimą baikite arba atšaukite ankstesnio klausimo redagavimą.',
  errorAttachments:
    'Jūsų juodraštyje yra pridėtų failų. Prieš atidarydami apmokėjimą juos pašalinkite. Klausimo tekstas išsaugotas, o failus vėliau galėsite pridėti iš naujo.',
  errorStorage:
    'Naršyklė negalėjo išsaugoti juodraščio. Prieš atidarydami apmokėjimą nukopijuokite klausimą į saugią vietą ir leiskite sesijos saugyklą.',
  planPerMonth: '{credits} kreditų / mėn.',
  creditsCount: '{count} kreditų',
  billingTitle: 'Atsiskaitymai',
  billingIntro: 'Jūsų įmonės planas, kreditai ir mokėjimai.',
  planLabel: 'Planas',
  noPlan: 'Plano nėra',
  planPaymentFailed: 'Mokėjimas nepavyko',
  planEndedOn: 'Baigėsi {date}',
  planEnded: 'Pasibaigęs',
  codeNotPercent: 'Šis kodas suteikia fiksuotos sumos nuolaidą. Čia tinka tik procentiniai kodai.',
  balanceLabel: 'Liko kreditų',
  nextLabel: 'Kitas',
  renewsWith: '+{credits} kreditų {date}',
  switchesTo: '{date} keičiamas į {credits} kreditų / mėn.',
  endsOn: 'Baigiasi {date}',
  discountLabel: 'Nuolaida',
  creditsNeverExpire: 'Kreditai niekada nenustoja galioti.',
  paymentFailed:
    'Paskutinis mokėjimas nepavyko. Atnaujinkite kortelę skiltyje Mokėjimo būdas ir sąskaitos.',
  seeUsage: 'Žiūrėti naudojimą',
  changePlan: 'Keisti planą',
  choosePlan: 'Pasirinkti planą',
  portal: 'Mokėjimo būdas ir sąskaitos',
  opening: 'Atidaroma…',
  readOnly: 'Planą ir mokėjimus tvarko įmonės savininkas.',
  nativeOnly: 'Planai ir mokėjimai tvarkomi žiniatinklyje.',
  loading: 'Įkeliami atsiskaitymai…',
  retry: 'Bandyti dar kartą',
  unavailable:
    'Atsiskaitymai laikinai nepasiekiami. Bandykite dar kartą. Jūsų istorija ir nustatymai vis dar pasiekiami.',
  portalError: 'Nepavyko atidaryti mokėjimo nustatymų. Bandykite dar kartą.',
  returnPending:
    'Laukiama mokėjimo patvirtinimo. Kreditai atsiras, kai mokėjimas bus patvirtintas.',
  returnDelayed:
    'Mokėjimo patvirtinimas užtrunka ilgiau nei tikėtasi. Netrukus patikrinkite dar kartą.',
  returnCanceled: 'Apmokėjimas atšauktas. Mokestis nenuskaitytas.',
  pausedPlanTitle: 'Norėdami tęsti, pasirinkite planą',
  pausedPlanOwner:
    'Poneva veikia mėnesiniais kreditų planais. Pasirinkite planą, kad galėtumėte toliau užduoti klausimus.',
  pausedPlanMember:
    'Jūsų įmonė neturi aktyvaus plano. Įmonės savininkas jį pasirenka skiltyje Nustatymai, puslapyje Naudojimas.',
  pausedPastDue:
    'Paskutinis mokėjimas nepavyko, todėl planas sustabdytas. Atnaujinkite kortelę skiltyje Mokėjimo būdas ir sąskaitos.',
  pausedPastDueMember:
    'Paskutinis įmonės mokėjimas nepavyko, todėl planas sustabdytas. Įmonės savininkas gali atnaujinti kortelę.',
  pausedCreditsTitle: 'Visi kreditai panaudoti',
  pausedCreditsBody:
    'Pereikite prie didesnio plano dabar arba palaukite iki {date} {time}, kai planas pridės {credits} kreditų.',
  pausedCreditsMember:
    'Paprašykite įmonės savininko pereiti prie didesnio plano arba palaukite iki {date} {time}, kai planas pridės {credits} kreditų.',
  pausedCreditsNoRenewal: 'Norėdami tęsti, pasirinkite didesnį planą.',
  pausedLimitTitle: 'Pasiektas mėnesio limitas',
  pausedLimitBody:
    'Panaudojote savo mėnesio limitą: {limit} kreditų. Jis bus nustatytas iš naujo {date} arba kai įmonės savininkas jį padidins.',
  pausedLimitNoDate:
    'Panaudojote savo mėnesio limitą: {limit} kreditų. Įmonės savininkas gali jį padidinti.',
  upgradeNow: 'Pereiti prie didesnio plano',
  limitNotice:
    'Liko {left} iš {limit} jūsų mėnesio kreditų. Limitas bus nustatytas iš naujo {date}.',
  limitNoticeNoDate: 'Liko {left} iš {limit} jūsų mėnesio kreditų.',
};

const pl: Copy = {
  dialogTitle: 'Zmień plan',
  dialogTitleNew: 'Wybierz plan',
  dialogSub:
    'Co miesiąc plan dodaje swoje kredyty do salda, które dzieli cały zespół. Niewykorzystane kredyty przechodzą na kolejne miesiące i nigdy nie wygasają. Możesz z nich korzystać, dopóki plan jest aktywny.',
  close: 'Zamknij',
  kickerUpgrade: 'Wyższy plan',
  kickerDowngrade: 'Niższy plan',
  currentPlanTag: 'Obecny plan',
  currentPin: 'Obecny',
  creditsEveryMonth: 'kredytów co miesiąc',
  captionCurrent: 'Twój obecny plan.',
  captionMore: 'O {count} kredytów więcej co miesiąc niż w obecnym planie.',
  captionFewer: 'O {count} kredytów mniej co miesiąc niż w obecnym planie.',
  captionRecent: 'Twój zespół wykorzystał {count} kredytów w ciągu ostatnich 30 dni.',
  scaleEnd: '{price} miesięcznie',
  sliderLabel: 'Plan',
  sliderValue: '{credits} kredytów miesięcznie, {price} bez VAT',
  priceLabel: 'Cena miesięcznie',
  exclVat: 'bez VAT',
  exclVatForMonths: 'bez VAT, przez {months} mies.',
  exclVatForOneMonth: 'bez VAT, przez 1 miesiąc',
  exclVatUntil: 'bez VAT, do {date}',
  perCreditLabel: 'Cena za kredyt',
  volumeLabel: 'Rabat ilościowy',
  volumeNone: 'Brak',
  volumeNote: 'względem ceny {price} za kredyt w najmniejszym planie',
  volumeBase: 'Najmniejszy plan wyznacza cenę bazową za kredyt',
  vatLabel: 'Z 25% VAT',
  perMonth: 'miesięcznie',
  vatReverseLabel: 'VAT',
  vatReverseValue: 'Odwrotne obciążenie',
  codeAdd: 'Dodaj kod rabatowy',
  codePlaceholder: 'Kod rabatowy',
  codeApply: 'Zastosuj',
  codeChecking: 'Sprawdzanie…',
  codeNotFound: 'Nie znaleźliśmy tego kodu. Sprawdź pisownię i spróbuj ponownie.',
  codeRemove: 'Usuń kod',
  codeForMonths: '{percent} rabatu przez {months} mies.',
  codeForOneMonth: '{percent} rabatu przez 1 miesiąc',
  codeForGood: '{percent} rabatu na stałe',
  codeUntil: '{percent} rabatu do {date}',
  codeCarries: '{percent} rabatu do {date}, przechodzi na nowy plan',
  codeCarriesForGood: '{percent} rabatu na stałe, przechodzi na nowy plan',
  ctaSwitch: 'Przejdź na {credits} kredytów',
  ctaUseCode: 'Użyj kodu w tym planie',
  ctaCurrent: 'Twój obecny plan',
  ctaCheckout: 'Przejdź do płatności',
  ctaSaving: 'Zapisywanie…',
  ctaOpening: 'Otwieranie płatności…',
  sideFoot: 'Płatność kartą co miesiąc',
  back: 'Wszystkie plany',
  timingTitle: 'Kiedy ma się zacząć nowy plan?',
  timingGroup: 'Początek nowego planu',
  summaryInclVat: '{price} miesięcznie z VAT',
  summaryExclVat: '{price} miesięcznie bez VAT',
  summaryOff: '{percent} rabatu',
  nowTitle: 'Zacznij teraz',
  renewalTitle: 'Przy odnowieniu, {date}',
  today: 'Dzisiaj',
  fromThenOn: 'Następnie',
  onDate: 'Dnia {date}',
  codeNote: '({percent} rabatu)',
  codeNoteUntil: '({percent} rabatu do {date})',
  nowToday: 'Nowy plan zaczyna działać. Płacisz {price}{codeNote} i od razu otrzymujesz {credits}.',
  nowTodayOnTop:
    'Nowy plan zaczyna działać. Płacisz {price}{codeNote} i od razu otrzymujesz {credits}, oprócz {balance}, które już masz (łącznie {total}).',
  nowThen:
    '{credits} i {price} co kolejny miesiąc. Obecny plan kończy się dzisiaj i nie będzie już rozliczany.',
  nowThenUntil:
    '{credits} i {price} co miesiąc do {date}, potem {fullPrice}. Obecny plan kończy się dzisiaj i nie będzie już rozliczany.',
  renewalToday:
    'Nic się nie zmienia. Zachowujesz obecny plan oraz {balance} kredytów i nic nie płacisz.',
  renewalTodayPaused:
    'Nic się nie zmienia. Zachowujesz obecny plan oraz {balance} kredytów i nic nie płacisz. Do tego czasu korzystanie pozostaje wstrzymane.',
  renewalThen:
    'Nowy plan zaczyna działać. Płacisz {price}{codeNote} i otrzymujesz {credits} dopiero wtedy.',
  footSe: 'Kwoty zawierają 25% VAT. Obciążamy kartę podaną na stronie Płatności.',
  footUnknown:
    'Kwoty zawierają 25% szwedzkiego VAT. VAT jest doliczany przy płatności, jeśli ma zastosowanie.',
  footReverse:
    'Kwoty nie zawierają VAT, który jest rozliczany w ramach odwrotnego obciążenia. Obciążamy kartę podaną na stronie Płatności.',
  cancel: 'Anuluj',
  confirmNow: 'Zapłać {price} i zmień plan',
  confirmRenewal: 'Zaplanuj zmianę na {credits} kredytów',
  paying: 'Trwa płatność…',
  scheduling: 'Planowanie…',
  toastSwitched: 'Nowy plan jest aktywny. Do salda dodano {credits} kredytów.',
  toastScheduled: 'Nowy plan zacznie działać {date}. Do tego czasu nic się nie zmienia.',
  toastCode: '{percent} rabatu obowiązuje od {date} przez {months} mies.',
  toastCodeOneMonth: '{percent} rabatu obowiązuje od {date} przez 1 miesiąc.',
  toastCodeForGood: '{percent} rabatu obowiązuje od {date}.',
  errorDeclined:
    'Karta została odrzucona. Zaktualizuj ją w sekcji Metoda płatności i faktury, a potem spróbuj ponownie.',
  errorDiscountActive: 'Rabat jest już aktywny. Przechodzi na nowy plan.',
  errorComplimentary:
    'Twój plan jest bezpłatny dzięki kodowi, więc nowy plan może zacząć się dopiero przy odnowieniu.',
  errorCodeNotAccepted: 'Tego kodu nie można użyć w tym planie.',
  codeAlreadyUsed: 'Twoja firma już użyła tego kodu.',
  errorGeneric: 'Nie udało się zmienić planu. Spróbuj ponownie.',
  errorEditing: 'Zakończ lub anuluj edycję wcześniejszego pytania, zanim otworzysz płatność.',
  errorAttachments:
    'Twoja wersja robocza ma załączone pliki. Usuń je, zanim otworzysz płatność. Treść pytania zostanie zapisana, a pliki możesz później załączyć ponownie.',
  errorStorage:
    'Przeglądarka nie mogła zapisać wersji roboczej. Skopiuj pytanie w bezpieczne miejsce i zezwól na pamięć sesji, zanim otworzysz płatność.',
  planPerMonth: '{credits} kredytów / miesiąc',
  creditsCount: '{count} kredytów',
  billingTitle: 'Płatności',
  billingIntro: 'Plan, kredyty i płatności Twojej firmy.',
  planLabel: 'Plan',
  noPlan: 'Brak planu',
  planPaymentFailed: 'Płatność nie powiodła się',
  planEndedOn: 'Zakończony {date}',
  planEnded: 'Zakończony',
  codeNotPercent: 'Ten kod odejmuje stałą kwotę. Tutaj działają tylko kody procentowe.',
  balanceLabel: 'Pozostałe kredyty',
  nextLabel: 'Następnie',
  renewsWith: '+{credits} kredytów dnia {date}',
  switchesTo: 'Zmiana na {credits} kredytów / miesiąc dnia {date}',
  endsOn: 'Kończy się dnia {date}',
  discountLabel: 'Rabat',
  creditsNeverExpire: 'Kredyty nigdy nie wygasają.',
  paymentFailed:
    'Ostatnia płatność nie powiodła się. Zaktualizuj kartę w sekcji Metoda płatności i faktury.',
  seeUsage: 'Zobacz wykorzystanie',
  changePlan: 'Zmień plan',
  choosePlan: 'Wybierz plan',
  portal: 'Metoda płatności i faktury',
  opening: 'Otwieranie…',
  readOnly: 'Planem i płatnościami zarządza właściciel firmy.',
  nativeOnly: 'Planami i płatnościami zarządzasz w wersji internetowej.',
  loading: 'Wczytywanie płatności…',
  retry: 'Spróbuj ponownie',
  unavailable:
    'Płatności są chwilowo niedostępne. Spróbuj ponownie. Historia i ustawienia są nadal dostępne.',
  portalError: 'Nie udało się otworzyć ustawień płatności. Spróbuj ponownie.',
  returnPending:
    'Czekamy na potwierdzenie płatności. Kredyty pojawią się, gdy płatność zostanie potwierdzona.',
  returnDelayed: 'Potwierdzenie płatności trwa dłużej niż zwykle. Sprawdź ponownie za chwilę.',
  returnCanceled: 'Płatność została anulowana. Nic nie pobrano.',
  pausedPlanTitle: 'Wybierz plan, aby kontynuować',
  pausedPlanOwner:
    'Poneva działa w modelu miesięcznych planów z kredytami. Wybierz plan, aby dalej zadawać pytania.',
  pausedPlanMember:
    'Twoja firma nie ma aktywnego planu. Właściciel firmy wybiera go w Ustawieniach, w sekcji Wykorzystanie.',
  pausedPastDue:
    'Ostatnia płatność nie powiodła się, więc plan jest wstrzymany. Zaktualizuj kartę w sekcji Metoda płatności i faktury.',
  pausedPastDueMember:
    'Ostatnia płatność firmy nie powiodła się, więc plan jest wstrzymany. Właściciel firmy może zaktualizować kartę.',
  pausedCreditsTitle: 'Wszystkie kredyty zostały wykorzystane',
  pausedCreditsBody:
    'Przejdź teraz na wyższy plan albo poczekaj do {date}, godz. {time}, gdy plan doda {credits} kredytów.',
  pausedCreditsMember:
    'Poproś właściciela firmy o wyższy plan albo poczekaj do {date}, godz. {time}, gdy plan doda {credits} kredytów.',
  pausedCreditsNoRenewal: 'Wybierz większy plan, aby kontynuować.',
  pausedLimitTitle: 'Miesięczny limit został osiągnięty',
  pausedLimitBody:
    'Wykorzystano Twój miesięczny limit {limit} kredytów. Zostanie zresetowany {date} lub gdy właściciel firmy go podniesie.',
  pausedLimitNoDate:
    'Wykorzystano Twój miesięczny limit {limit} kredytów. Właściciel firmy może go podnieść.',
  upgradeNow: 'Przejdź na wyższy plan',
  limitNotice:
    'Zostało {left} z {limit} kredytów w Twoim miesięcznym limicie. Limit odnowi się {date}.',
  limitNoticeNoDate: 'Zostało {left} z {limit} kredytów w Twoim miesięcznym limicie.',
};

const cs: Copy = {
  dialogTitle: 'Změnit tarif',
  dialogTitleNew: 'Vyberte tarif',
  dialogSub:
    'Tarif každý měsíc přidá své kredity do zůstatku, který sdílí celý váš tým. Nevyužité kredity se převádějí do dalších měsíců a nikdy nevyprší. Můžete je používat, dokud je tarif aktivní.',
  close: 'Zavřít',
  kickerUpgrade: 'Vyšší tarif',
  kickerDowngrade: 'Nižší tarif',
  currentPlanTag: 'Aktuální tarif',
  currentPin: 'Aktuální',
  creditsEveryMonth: 'kreditů každý měsíc',
  captionCurrent: 'Váš aktuální tarif.',
  captionMore: 'O {count} kreditů měsíčně více než váš aktuální tarif.',
  captionFewer: 'O {count} kreditů měsíčně méně než váš aktuální tarif.',
  captionRecent: 'Váš tým za posledních 30 dní využil {count} kreditů.',
  scaleEnd: '{price} měsíčně',
  sliderLabel: 'Tarif',
  sliderValue: '{credits} kreditů měsíčně, {price} bez DPH',
  priceLabel: 'Cena za měsíc',
  exclVat: 'bez DPH',
  exclVatForMonths: 'bez DPH, po dobu {months} měs.',
  exclVatForOneMonth: 'bez DPH, po dobu 1 měsíce',
  exclVatUntil: 'bez DPH, do {date}',
  perCreditLabel: 'Cena za kredit',
  volumeLabel: 'Množstevní sleva',
  volumeNone: 'Žádná',
  volumeNote: 'oproti ceně {price} za kredit v nejmenším tarifu',
  volumeBase: 'Nejmenší tarif určuje základní cenu za kredit',
  vatLabel: 'Vč. 25% DPH',
  perMonth: 'měsíčně',
  vatReverseLabel: 'DPH',
  vatReverseValue: 'Přenesená daňová povinnost',
  codeAdd: 'Přidat slevový kód',
  codePlaceholder: 'Slevový kód',
  codeApply: 'Použít',
  codeChecking: 'Ověřování…',
  codeNotFound: 'Tento kód jsme nenašli. Zkontrolujte, zda je správně napsaný, a zkuste to znovu.',
  codeRemove: 'Odebrat kód',
  codeForMonths: 'Sleva {percent} na {months} měs.',
  codeForOneMonth: 'Sleva {percent} na 1 měsíc',
  codeForGood: 'Sleva {percent} natrvalo',
  codeUntil: 'Sleva {percent} do {date}',
  codeCarries: 'Sleva {percent} do {date}, přechází na nový tarif',
  codeCarriesForGood: 'Sleva {percent} natrvalo, přechází na nový tarif',
  ctaSwitch: 'Přejít na {credits} kreditů',
  ctaUseCode: 'Použít kód pro tento tarif',
  ctaCurrent: 'Váš aktuální tarif',
  ctaCheckout: 'Pokračovat k platbě',
  ctaSaving: 'Ukládání…',
  ctaOpening: 'Otevírání platby…',
  sideFoot: 'Účtováno měsíčně kartou',
  back: 'Všechny tarify',
  timingTitle: 'Kdy má nový tarif začít?',
  timingGroup: 'Začátek nového tarifu',
  summaryInclVat: '{price} měsíčně vč. DPH',
  summaryExclVat: '{price} měsíčně bez DPH',
  summaryOff: 'sleva {percent}',
  nowTitle: 'Začít hned',
  renewalTitle: 'Při obnovení, {date}',
  today: 'Dnes',
  fromThenOn: 'Poté',
  onDate: 'Dne {date}',
  codeNote: '(sleva {percent})',
  codeNoteUntil: '(sleva {percent} do {date})',
  nowToday: 'Nový tarif začne. Zaplatíte {price}{codeNote} a hned získáte {credits}.',
  nowTodayOnTop:
    'Nový tarif začne. Zaplatíte {price}{codeNote} a hned získáte {credits} navíc k {balance}, které už máte (celkem {total}).',
  nowThen:
    '{credits} a {price} každý další měsíc. Váš aktuální tarif dnes končí a už nebude účtován.',
  nowThenUntil:
    '{credits} a {price} každý měsíc do {date}, poté {fullPrice}. Váš aktuální tarif dnes končí a už nebude účtován.',
  renewalToday:
    'Nic se nemění. Ponecháte si aktuální tarif i svých {balance} kreditů a nic neplatíte.',
  renewalTodayPaused:
    'Nic se nemění. Ponecháte si aktuální tarif i svých {balance} kreditů a nic neplatíte. Do té doby zůstává využívání pozastaveno.',
  renewalThen:
    'Nový tarif začne. Zaplatíte {price}{codeNote} a {credits} získáte až tehdy, ne dříve.',
  footSe: 'Částky zahrnují 25% DPH. Strhneme je z karty uvedené na stránce Fakturace.',
  footUnknown: 'Částky zahrnují 25% švédskou DPH. DPH se připočítá při platbě, pokud se uplatňuje.',
  footReverse:
    'Částky jsou bez DPH, která se uplatní v režimu přenesené daňové povinnosti. Strhneme je z karty uvedené na stránce Fakturace.',
  cancel: 'Zrušit',
  confirmNow: 'Zaplatit {price} a změnit tarif',
  confirmRenewal: 'Naplánovat přechod na {credits} kreditů',
  paying: 'Probíhá platba…',
  scheduling: 'Plánování…',
  toastSwitched: 'Váš nový tarif je aktivní. Do zůstatku bylo připsáno {credits} kreditů.',
  toastScheduled: 'Váš nový tarif začne {date}. Do té doby se nic nemění.',
  toastCode: 'Sleva {percent} platí od {date} po dobu {months} měs.',
  toastCodeOneMonth: 'Sleva {percent} platí od {date} po dobu 1 měsíce.',
  toastCodeForGood: 'Sleva {percent} platí od {date}.',
  errorDeclined:
    'Vaše karta byla zamítnuta. Aktualizujte ji v části Platební metoda a faktury a zkuste to znovu.',
  errorDiscountActive: 'Sleva už je aktivní. Přechází na nový tarif.',
  errorComplimentary: 'Váš tarif je díky kódu zdarma, takže nový tarif může začít až při obnovení.',
  errorCodeNotAccepted: 'Tento kód nelze pro tento tarif použít.',
  codeAlreadyUsed: 'Vaše firma už tento kód použila.',
  errorGeneric: 'Tarif se nepodařilo změnit. Zkuste to prosím znovu.',
  errorEditing: 'Než otevřete platbu, dokončete nebo zrušte úpravu předchozího dotazu.',
  errorAttachments:
    'Váš koncept obsahuje přiložené soubory. Než otevřete platbu, odeberte je. Text dotazu zůstane uložený a soubory můžete později přiložit znovu.',
  errorStorage:
    'Prohlížeč nemohl uložit váš koncept. Než otevřete platbu, zkopírujte si dotaz na bezpečné místo a povolte úložiště relace.',
  planPerMonth: '{credits} kreditů / měsíc',
  creditsCount: '{count} kreditů',
  billingTitle: 'Fakturace',
  billingIntro: 'Tarif, kredity a platby vaší firmy.',
  planLabel: 'Tarif',
  noPlan: 'Žádný tarif',
  planPaymentFailed: 'Platba se nezdařila',
  planEndedOn: 'Skončil {date}',
  planEnded: 'Skončil',
  codeNotPercent: 'Tento kód odečítá pevnou částku. Zde fungují jen procentní kódy.',
  balanceLabel: 'Zbývající kredity',
  nextLabel: 'Příště',
  renewsWith: '+{credits} kreditů dne {date}',
  switchesTo: 'Přechod na {credits} kreditů / měsíc dne {date}',
  endsOn: 'Končí dne {date}',
  discountLabel: 'Sleva',
  creditsNeverExpire: 'Kredity nikdy nevyprší.',
  paymentFailed:
    'Vaše poslední platba neprošla. Aktualizujte kartu v části Platební metoda a faktury.',
  seeUsage: 'Zobrazit využití',
  changePlan: 'Změnit tarif',
  choosePlan: 'Vybrat tarif',
  portal: 'Platební metoda a faktury',
  opening: 'Otevírání…',
  readOnly: 'Tarif a platby spravuje vlastník firmy.',
  nativeOnly: 'Tarify a platby se spravují na webu.',
  loading: 'Načítání fakturace…',
  retry: 'Zkusit znovu',
  unavailable:
    'Fakturace je dočasně nedostupná. Zkuste to prosím znovu. Historie a nastavení jsou nadále k dispozici.',
  portalError: 'Nastavení plateb se nepodařilo otevřít. Zkuste to prosím znovu.',
  returnPending: 'Čekáme na potvrzení platby. Kredity se zobrazí, jakmile bude platba potvrzena.',
  returnDelayed: 'Potvrzení platby trvá déle, než se čekalo. Zkontrolujte to znovu za chvíli.',
  returnCanceled: 'Platba byla zrušena. Nic nebylo strženo.',
  pausedPlanTitle: 'Pro pokračování vyberte tarif',
  pausedPlanOwner:
    'Poneva funguje na měsíčních tarifech s kredity. Vyberte tarif, abyste mohli dál klást dotazy.',
  pausedPlanMember:
    'Vaše firma nemá aktivní tarif. Vlastník firmy ho vybere v Nastavení v části Využití.',
  pausedPastDue:
    'Vaše poslední platba neprošla, proto je tarif pozastaven. Aktualizujte kartu v části Platební metoda a faktury.',
  pausedPastDueMember:
    'Poslední platba firmy neprošla, proto je tarif pozastaven. Kartu může aktualizovat vlastník firmy.',
  pausedCreditsTitle: 'Vyčerpali jste všechny kredity',
  pausedCreditsBody:
    'Přejděte hned na vyšší tarif, nebo počkejte do {date} v {time}, kdy tarif přidá {credits} kreditů.',
  pausedCreditsMember:
    'Požádejte vlastníka firmy o vyšší tarif, nebo počkejte do {date} v {time}, kdy tarif přidá {credits} kreditů.',
  pausedCreditsNoRenewal: 'Pro pokračování vyberte vyšší tarif.',
  pausedLimitTitle: 'Dosáhli jste měsíčního limitu',
  pausedLimitBody:
    'Vyčerpali jste svůj měsíční limit {limit} kreditů. Obnoví se {date}, nebo až ho vlastník firmy navýší.',
  pausedLimitNoDate:
    'Vyčerpali jste svůj měsíční limit {limit} kreditů. Vlastník firmy ho může navýšit.',
  upgradeNow: 'Přejít na vyšší tarif',
  limitNotice: 'Zbývá {left} z vašich {limit} měsíčních kreditů. Limit se obnoví {date}.',
  limitNoticeNoDate: 'Zbývá {left} z vašich {limit} měsíčních kreditů.',
};

const sk: Copy = {
  dialogTitle: 'Zmeniť plán',
  dialogTitleNew: 'Vyberte si plán',
  dialogSub:
    'Plán každý mesiac pridá svoje kredity do zostatku, ktorý zdieľa celý váš tím. Nevyužité kredity sa prenášajú do ďalších mesiacov a nikdy nepropadnú. Môžete ich používať, kým je plán aktívny.',
  close: 'Zavrieť',
  kickerUpgrade: 'Vyšší plán',
  kickerDowngrade: 'Nižší plán',
  currentPlanTag: 'Aktuálny plán',
  currentPin: 'Aktuálny',
  creditsEveryMonth: 'kreditov každý mesiac',
  captionCurrent: 'Váš aktuálny plán.',
  captionMore: 'O {count} kreditov mesačne viac ako váš aktuálny plán.',
  captionFewer: 'O {count} kreditov mesačne menej ako váš aktuálny plán.',
  captionRecent: 'Váš tím za posledných 30 dní využil {count} kreditov.',
  scaleEnd: '{price} mesačne',
  sliderLabel: 'Plán',
  sliderValue: '{credits} kreditov mesačne, {price} bez DPH',
  priceLabel: 'Cena za mesiac',
  exclVat: 'bez DPH',
  exclVatForMonths: 'bez DPH, počas {months} mes.',
  exclVatForOneMonth: 'bez DPH, počas 1 mesiaca',
  exclVatUntil: 'bez DPH, do {date}',
  perCreditLabel: 'Cena za kredit',
  volumeLabel: 'Množstevná zľava',
  volumeNone: 'Žiadna',
  volumeNote: 'oproti cene {price} za kredit v najmenšom pláne',
  volumeBase: 'Najmenší plán určuje základnú cenu za kredit',
  vatLabel: 'Vrátane 25 % DPH',
  perMonth: 'mesačne',
  vatReverseLabel: 'DPH',
  vatReverseValue: 'Prenesenie daňovej povinnosti',
  codeAdd: 'Pridať zľavový kód',
  codePlaceholder: 'Zľavový kód',
  codeApply: 'Použiť',
  codeChecking: 'Overuje sa…',
  codeNotFound: 'Tento kód sme nenašli. Skontrolujte, či je správne napísaný, a skúste to znova.',
  codeRemove: 'Odstrániť kód',
  codeForMonths: 'Zľava {percent} na {months} mes.',
  codeForOneMonth: 'Zľava {percent} na 1 mesiac',
  codeForGood: 'Zľava {percent} natrvalo',
  codeUntil: 'Zľava {percent} do {date}',
  codeCarries: 'Zľava {percent} do {date}, prechádza na nový plán',
  codeCarriesForGood: 'Zľava {percent} natrvalo, prechádza na nový plán',
  ctaSwitch: 'Prejsť na {credits} kreditov',
  ctaUseCode: 'Použiť kód pre tento plán',
  ctaCurrent: 'Váš aktuálny plán',
  ctaCheckout: 'Pokračovať na platbu',
  ctaSaving: 'Ukladá sa…',
  ctaOpening: 'Otvára sa platba…',
  sideFoot: 'Fakturované mesačne kartou',
  back: 'Všetky plány',
  timingTitle: 'Kedy sa má nový plán začať?',
  timingGroup: 'Začiatok nového plánu',
  summaryInclVat: '{price} mesačne vrátane DPH',
  summaryExclVat: '{price} mesačne bez DPH',
  summaryOff: 'zľava {percent}',
  nowTitle: 'Začať hneď',
  renewalTitle: 'Pri obnovení, {date}',
  today: 'Dnes',
  fromThenOn: 'Potom',
  onDate: 'Dňa {date}',
  codeNote: '(zľava {percent})',
  codeNoteUntil: '(zľava {percent} do {date})',
  nowToday: 'Nový plán sa začne. Zaplatíte {price}{codeNote} a hneď získate {credits}.',
  nowTodayOnTop:
    'Nový plán sa začne. Zaplatíte {price}{codeNote} a hneď získate {credits} navyše k {balance}, ktoré už máte (spolu {total}).',
  nowThen:
    '{credits} a {price} každý ďalší mesiac. Váš aktuálny plán sa dnes končí a už sa nebude účtovať.',
  nowThenUntil:
    '{credits} a {price} každý mesiac do {date}, potom {fullPrice}. Váš aktuálny plán sa dnes končí a už sa nebude účtovať.',
  renewalToday:
    'Nič sa nemení. Ponecháte si aktuálny plán aj svojich {balance} kreditov a nič neplatíte.',
  renewalTodayPaused:
    'Nič sa nemení. Ponecháte si aktuálny plán aj svojich {balance} kreditov a nič neplatíte. Dovtedy zostáva používanie pozastavené.',
  renewalThen:
    'Nový plán sa začne. Zaplatíte {price}{codeNote} a {credits} získate až vtedy, nie skôr.',
  footSe: 'Sumy zahŕňajú 25 % DPH. Strhneme ich z karty uvedenej na stránke Fakturácia.',
  footUnknown: 'Sumy zahŕňajú 25 % švédsku DPH. DPH sa pripočíta pri platbe, ak sa uplatňuje.',
  footReverse:
    'Sumy sú bez DPH, ktorá sa uplatní v režime prenesenia daňovej povinnosti. Strhneme ich z karty uvedenej na stránke Fakturácia.',
  cancel: 'Zrušiť',
  confirmNow: 'Zaplatiť {price} a zmeniť plán',
  confirmRenewal: 'Naplánovať prechod na {credits} kreditov',
  paying: 'Prebieha platba…',
  scheduling: 'Plánuje sa…',
  toastSwitched: 'Váš nový plán je aktívny. Do zostatku sa pripísalo {credits} kreditov.',
  toastScheduled: 'Váš nový plán sa začne {date}. Dovtedy sa nič nemení.',
  toastCode: 'Zľava {percent} platí od {date} počas {months} mes.',
  toastCodeOneMonth: 'Zľava {percent} platí od {date} počas 1 mesiaca.',
  toastCodeForGood: 'Zľava {percent} platí od {date}.',
  errorDeclined:
    'Vaša karta bola zamietnutá. Aktualizujte ju v časti Spôsob platby a faktúry a skúste to znova.',
  errorDiscountActive: 'Zľava je už aktívna. Prechádza na nový plán.',
  errorComplimentary:
    'Váš plán je vďaka kódu zadarmo, takže nový plán sa môže začať až pri obnovení.',
  errorCodeNotAccepted: 'Tento kód sa pre tento plán nedá použiť.',
  codeAlreadyUsed: 'Vaša firma už tento kód použila.',
  errorGeneric: 'Plán sa nepodarilo zmeniť. Skúste to znova.',
  errorEditing: 'Pred otvorením platby dokončite alebo zrušte úpravu predchádzajúcej otázky.',
  errorAttachments:
    'Váš koncept obsahuje priložené súbory. Pred otvorením platby ich odstráňte. Text otázky zostane uložený a súbory môžete neskôr priložiť znova.',
  errorStorage:
    'Prehliadač nemohol uložiť váš koncept. Pred otvorením platby si otázku skopírujte na bezpečné miesto a povoľte úložisko relácie.',
  planPerMonth: '{credits} kreditov / mesiac',
  creditsCount: '{count} kreditov',
  billingTitle: 'Fakturácia',
  billingIntro: 'Plán, kredity a platby vašej firmy.',
  planLabel: 'Plán',
  noPlan: 'Žiadny plán',
  planPaymentFailed: 'Platba zlyhala',
  planEndedOn: 'Skončil {date}',
  planEnded: 'Skončený',
  codeNotPercent: 'Tento kód odpočíta pevnú sumu. Tu fungujú iba percentuálne kódy.',
  balanceLabel: 'Zostávajúce kredity',
  nextLabel: 'Najbližšie',
  renewsWith: '+{credits} kreditov dňa {date}',
  switchesTo: 'Prechod na {credits} kreditov / mesiac dňa {date}',
  endsOn: 'Končí dňa {date}',
  discountLabel: 'Zľava',
  creditsNeverExpire: 'Kredity nikdy nepropadnú.',
  paymentFailed:
    'Vaša posledná platba neprešla. Aktualizujte kartu v časti Spôsob platby a faktúry.',
  seeUsage: 'Zobraziť využitie',
  changePlan: 'Zmeniť plán',
  choosePlan: 'Vybrať plán',
  portal: 'Spôsob platby a faktúry',
  opening: 'Otvára sa…',
  readOnly: 'Plán a platby spravuje vlastník firmy.',
  nativeOnly: 'Plány a platby sa spravujú na webe.',
  loading: 'Načítava sa fakturácia…',
  retry: 'Skúsiť znova',
  unavailable:
    'Fakturácia je dočasne nedostupná. Skúste to znova. História a nastavenia sú naďalej dostupné.',
  portalError: 'Nastavenia platieb sa nepodarilo otvoriť. Skúste to znova.',
  returnPending: 'Čakáme na potvrdenie platby. Kredity sa zobrazia po potvrdení platby.',
  returnDelayed: 'Potvrdenie platby trvá dlhšie, ako sa čakalo. Skontrolujte to znova o chvíľu.',
  returnCanceled: 'Platba bola zrušená. Nič sa nestrhlo.',
  pausedPlanTitle: 'Ak chcete pokračovať, vyberte si plán',
  pausedPlanOwner:
    'Poneva funguje na mesačných plánoch s kreditmi. Vyberte si plán, aby ste mohli ďalej klásť otázky.',
  pausedPlanMember:
    'Vaša firma nemá aktívny plán. Vlastník firmy ho vyberie v Nastaveniach v časti Využitie.',
  pausedPastDue:
    'Vaša posledná platba neprešla, preto je plán pozastavený. Aktualizujte kartu v časti Spôsob platby a faktúry.',
  pausedPastDueMember:
    'Posledná platba firmy neprešla, preto je plán pozastavený. Kartu môže aktualizovať vlastník firmy.',
  pausedCreditsTitle: 'Minuli ste všetky kredity',
  pausedCreditsBody:
    'Prejdite hneď na vyšší plán, alebo počkajte do {date} o {time}, keď plán pridá {credits} kreditov.',
  pausedCreditsMember:
    'Požiadajte vlastníka firmy o vyšší plán, alebo počkajte do {date} o {time}, keď plán pridá {credits} kreditov.',
  pausedCreditsNoRenewal: 'Ak chcete pokračovať, vyberte si väčší plán.',
  pausedLimitTitle: 'Dosiahli ste mesačný limit',
  pausedLimitBody:
    'Vyčerpali ste svoj mesačný limit {limit} kreditov. Obnoví sa {date} alebo keď ho vlastník firmy zvýši.',
  pausedLimitNoDate:
    'Vyčerpali ste svoj mesačný limit {limit} kreditov. Vlastník firmy ho môže zvýšiť.',
  upgradeNow: 'Prejsť na vyšší plán',
  limitNotice: 'Zostáva {left} z vašich {limit} mesačných kreditov. Limit sa obnoví {date}.',
  limitNoticeNoDate: 'Zostáva {left} z vašich {limit} mesačných kreditov.',
};

const sl: Copy = {
  dialogTitle: 'Spremeni paket',
  dialogTitleNew: 'Izberite paket',
  dialogSub:
    'Paket vsak mesec doda svoje kredite v stanje, ki si ga deli vsa vaša ekipa. Neporabljeni krediti se prenesejo v naslednje mesece in nikoli ne potečejo. Uporabljate jih lahko, dokler je paket aktiven.',
  close: 'Zapri',
  kickerUpgrade: 'Večji paket',
  kickerDowngrade: 'Manjši paket',
  currentPlanTag: 'Trenutni paket',
  currentPin: 'Trenutni',
  creditsEveryMonth: 'kreditov vsak mesec',
  captionCurrent: 'Vaš trenutni paket.',
  captionMore: '{count} kreditov na mesec več kot v vašem trenutnem paketu.',
  captionFewer: '{count} kreditov na mesec manj kot v vašem trenutnem paketu.',
  captionRecent: 'Vaša ekipa je v zadnjih 30 dneh porabila {count} kreditov.',
  scaleEnd: '{price} na mesec',
  sliderLabel: 'Paket',
  sliderValue: '{credits} kreditov na mesec, {price} brez DDV',
  priceLabel: 'Cena na mesec',
  exclVat: 'brez DDV',
  exclVatForMonths: 'brez DDV, za {months} mes.',
  exclVatForOneMonth: 'brez DDV, za 1 mesec',
  exclVatUntil: 'brez DDV, do {date}',
  perCreditLabel: 'Cena na kredit',
  volumeLabel: 'Količinski popust',
  volumeNone: 'Brez',
  volumeNote: 'glede na ceno {price} na kredit v najmanjšem paketu',
  volumeBase: 'Najmanjši paket določa osnovno ceno na kredit',
  vatLabel: 'Z 25 % DDV',
  perMonth: 'na mesec',
  vatReverseLabel: 'DDV',
  vatReverseValue: 'Obrnjena davčna obveznost',
  codeAdd: 'Dodaj kodo za popust',
  codePlaceholder: 'Koda za popust',
  codeApply: 'Uporabi',
  codeChecking: 'Preverjanje…',
  codeNotFound: 'Te kode nismo našli. Preverite črkovanje in poskusite znova.',
  codeRemove: 'Odstrani kodo',
  codeForMonths: '{percent} popusta za {months} mes.',
  codeForOneMonth: '{percent} popusta za 1 mesec',
  codeForGood: '{percent} popusta za stalno',
  codeUntil: '{percent} popusta do {date}',
  codeCarries: '{percent} popusta do {date}, prenese se na nov paket',
  codeCarriesForGood: '{percent} popusta za stalno, prenese se na nov paket',
  ctaSwitch: 'Preklopi na {credits} kreditov',
  ctaUseCode: 'Uporabi kodo za ta paket',
  ctaCurrent: 'Vaš trenutni paket',
  ctaCheckout: 'Nadaljuj na plačilo',
  ctaSaving: 'Shranjevanje…',
  ctaOpening: 'Odpiranje plačila…',
  sideFoot: 'Mesečno obračunano s kartico',
  back: 'Vsi paketi',
  timingTitle: 'Kdaj naj se začne novi paket?',
  timingGroup: 'Začetek novega paketa',
  summaryInclVat: '{price} na mesec z DDV',
  summaryExclVat: '{price} na mesec brez DDV',
  summaryOff: '{percent} popusta',
  nowTitle: 'Začni zdaj',
  renewalTitle: 'Ob podaljšanju, {date}',
  today: 'Danes',
  fromThenOn: 'Nato',
  onDate: 'Dne {date}',
  codeNote: '({percent} popusta)',
  codeNoteUntil: '({percent} popusta do {date})',
  nowToday: 'Novi paket se začne. Plačate {price}{codeNote} in takoj dobite {credits}.',
  nowTodayOnTop:
    'Novi paket se začne. Plačate {price}{codeNote} in takoj dobite {credits}, poleg {balance}, ki jih že imate (skupaj {total}).',
  nowThen:
    '{credits} in {price} vsak naslednji mesec. Vaš trenutni paket se danes konča in ne bo več zaračunan.',
  nowThenUntil:
    '{credits} in {price} vsak mesec do {date}, nato {fullPrice}. Vaš trenutni paket se danes konča in ne bo več zaračunan.',
  renewalToday:
    'Nič se ne spremeni. Obdržite trenutni paket in svojih {balance} kreditov ter ne plačate ničesar.',
  renewalTodayPaused:
    'Nič se ne spremeni. Obdržite trenutni paket in svojih {balance} kreditov ter ne plačate ničesar. Do takrat ostane uporaba zaustavljena.',
  renewalThen: 'Novi paket se začne. Plačate {price}{codeNote} in {credits} dobite šele takrat.',
  footSe: 'Zneski vključujejo 25 % DDV. Bremenimo kartico, navedeno na strani Obračun.',
  footUnknown: 'Zneski vključujejo 25 % švedski DDV. DDV se doda pri plačilu, kjer je to potrebno.',
  footReverse:
    'Zneski ne vključujejo DDV, ki se obračuna po načelu obrnjene davčne obveznosti. Bremenimo kartico, navedeno na strani Obračun.',
  cancel: 'Prekliči',
  confirmNow: 'Plačaj {price} in preklopi',
  confirmRenewal: 'Načrtuj preklop na {credits} kreditov',
  paying: 'Plačevanje…',
  scheduling: 'Načrtovanje…',
  toastSwitched: 'Vaš novi paket je aktiven. Na stanje smo dodali {credits} kreditov.',
  toastScheduled: 'Vaš novi paket se začne {date}. Do takrat se nič ne spremeni.',
  toastCode: '{percent} popusta velja od {date} za {months} mes.',
  toastCodeOneMonth: '{percent} popusta velja od {date} za 1 mesec.',
  toastCodeForGood: '{percent} popusta velja od {date}.',
  errorDeclined:
    'Vaša kartica je bila zavrnjena. Posodobite jo v razdelku Način plačila in računi ter poskusite znova.',
  errorDiscountActive: 'Popust je že aktiven. Prenese se na nov paket.',
  errorComplimentary:
    'Vaš paket je zaradi kode brezplačen, zato se lahko nov paket začne šele ob podaljšanju.',
  errorCodeNotAccepted: 'Te kode ni mogoče uporabiti za ta paket.',
  codeAlreadyUsed: 'Vaše podjetje je to kodo že uporabilo.',
  errorGeneric: 'Paketa ni bilo mogoče spremeniti. Poskusite znova.',
  errorEditing: 'Preden odprete plačilo, dokončajte ali prekličite urejanje prejšnjega vprašanja.',
  errorAttachments:
    'Vaš osnutek ima priložene datoteke. Preden odprete plačilo, jih odstranite. Besedilo vprašanja ostane shranjeno, datoteke pa lahko pozneje znova priložite.',
  errorStorage:
    'Brskalnik ni mogel shraniti osnutka. Preden odprete plačilo, vprašanje kopirajte na varno mesto in dovolite shranjevanje seje.',
  planPerMonth: '{credits} kreditov / mesec',
  creditsCount: '{count} kreditov',
  billingTitle: 'Obračun',
  billingIntro: 'Paket, krediti in plačila vašega podjetja.',
  planLabel: 'Paket',
  noPlan: 'Ni paketa',
  planPaymentFailed: 'Plačilo ni uspelo',
  planEndedOn: 'Končan {date}',
  planEnded: 'Končan',
  codeNotPercent: 'Ta koda odšteje fiksni znesek. Tukaj delujejo le odstotne kode.',
  balanceLabel: 'Preostali krediti',
  nextLabel: 'Naslednje',
  renewsWith: '+{credits} kreditov dne {date}',
  switchesTo: 'Preklop na {credits} kreditov / mesec dne {date}',
  endsOn: 'Konča se dne {date}',
  discountLabel: 'Popust',
  creditsNeverExpire: 'Krediti nikoli ne potečejo.',
  paymentFailed:
    'Vaše zadnje plačilo ni uspelo. Posodobite kartico v razdelku Način plačila in računi.',
  seeUsage: 'Prikaži porabo',
  changePlan: 'Spremeni paket',
  choosePlan: 'Izberi paket',
  portal: 'Način plačila in računi',
  opening: 'Odpiranje…',
  readOnly: 'Paket in plačila upravlja lastnik podjetja.',
  nativeOnly: 'Pakete in plačila upravljate v spletni različici.',
  loading: 'Nalaganje obračuna…',
  retry: 'Poskusi znova',
  unavailable:
    'Obračun je začasno nedosegljiv. Poskusite znova. Zgodovina in nastavitve so še vedno na voljo.',
  portalError: 'Nastavitev plačil ni bilo mogoče odpreti. Poskusite znova.',
  returnPending: 'Čakamo na potrditev plačila. Krediti bodo prikazani, ko bo plačilo potrjeno.',
  returnDelayed: 'Potrditev plačila traja dlje kot običajno. Kmalu preverite znova.',
  returnCanceled: 'Plačilo je bilo preklicano. Nič ni bilo zaračunano.',
  pausedPlanTitle: 'Za nadaljevanje izberite paket',
  pausedPlanOwner:
    'Poneva deluje z mesečnimi paketi kreditov. Izberite paket, da boste lahko še naprej postavljali vprašanja.',
  pausedPlanMember:
    'Vaše podjetje nima aktivnega paketa. Lastnik podjetja ga izbere v Nastavitvah, v razdelku Poraba.',
  pausedPastDue:
    'Vaše zadnje plačilo ni uspelo, zato je paket zaustavljen. Posodobite kartico v razdelku Način plačila in računi.',
  pausedPastDueMember:
    'Zadnje plačilo podjetja ni uspelo, zato je paket zaustavljen. Kartico lahko posodobi lastnik podjetja.',
  pausedCreditsTitle: 'Porabili ste vse kredite',
  pausedCreditsBody:
    'Zdaj preklopite na večji paket ali počakajte do {date} ob {time}, ko paket doda {credits} kreditov.',
  pausedCreditsMember:
    'Prosite lastnika podjetja za večji paket ali počakajte do {date} ob {time}, ko paket doda {credits} kreditov.',
  pausedCreditsNoRenewal: 'Za nadaljevanje izberite večji paket.',
  pausedLimitTitle: 'Dosegli ste mesečno omejitev',
  pausedLimitBody:
    'Porabili ste svojo mesečno omejitev {limit} kreditov. Ponastavi se {date} ali ko jo lastnik podjetja zviša.',
  pausedLimitNoDate:
    'Porabili ste svojo mesečno omejitev {limit} kreditov. Lastnik podjetja jo lahko zviša.',
  upgradeNow: 'Preklopi na večji paket',
  limitNotice:
    'Od vaših {limit} mesečnih kreditov jih je ostalo še {left}. Omejitev se ponastavi {date}.',
  limitNoticeNoDate: 'Od vaših {limit} mesečnih kreditov jih je ostalo še {left}.',
};

const hr: Copy = {
  dialogTitle: 'Promijeni paket',
  dialogTitleNew: 'Odaberi paket',
  dialogSub:
    'Vaš paket svaki mjesec dodaje svoje kredite u saldo koji dijeli cijeli tim. Neiskorišteni krediti prenose se dalje i nikad ne istječu, a možete ih trošiti dok je paket aktivan.',
  close: 'Zatvori',
  kickerUpgrade: 'Nadogradnja',
  kickerDowngrade: 'Smanjenje paketa',
  currentPlanTag: 'Trenutačni paket',
  currentPin: 'Trenutačni',
  creditsEveryMonth: 'kredita svaki mjesec',
  captionCurrent: 'Vaš trenutačni paket.',
  captionMore: '{count} kredita mjesečno više nego u trenutačnom paketu.',
  captionFewer: '{count} kredita mjesečno manje nego u trenutačnom paketu.',
  captionRecent: 'Vaš je tim u posljednjih 30 dana potrošio {count} kredita.',
  scaleEnd: '{price} mjesečno',
  sliderLabel: 'Paket',
  sliderValue: '{credits} kredita mjesečno, {price} bez PDV-a',
  priceLabel: 'Cijena mjesečno',
  exclVat: 'bez PDV-a',
  exclVatForMonths: 'bez PDV-a, tijekom {months} mj.',
  exclVatForOneMonth: 'bez PDV-a, tijekom 1 mjeseca',
  exclVatUntil: 'bez PDV-a, do {date}',
  perCreditLabel: 'Cijena po kreditu',
  volumeLabel: 'Količinski popust',
  volumeNone: 'Nema',
  volumeNote: 'u odnosu na {price} po kreditu u najmanjem paketu',
  volumeBase: 'Najmanji paket određuje osnovnu cijenu po kreditu',
  vatLabel: 'S PDV-om od 25 %',
  perMonth: 'mjesečno',
  vatReverseLabel: 'PDV',
  vatReverseValue: 'Prijenos porezne obveze',
  codeAdd: 'Dodaj kod za popust',
  codePlaceholder: 'Kod za popust',
  codeApply: 'Primijeni',
  codeChecking: 'Provjera…',
  codeNotFound: 'Taj kod nismo pronašli. Provjerite pravopis i pokušajte ponovno.',
  codeRemove: 'Ukloni kod',
  codeForMonths: '{percent} popusta tijekom {months} mj.',
  codeForOneMonth: '{percent} popusta tijekom 1 mjeseca',
  codeForGood: '{percent} popusta trajno',
  codeUntil: '{percent} popusta do {date}',
  codeCarries: '{percent} popusta do {date}, prenosi se',
  codeCarriesForGood: '{percent} popusta trajno, prenosi se',
  ctaSwitch: 'Prijeđi na {credits} kredita',
  ctaUseCode: 'Primijeni kod na ovaj paket',
  ctaCurrent: 'Vaš trenutačni paket',
  ctaCheckout: 'Nastavi na plaćanje',
  ctaSaving: 'Spremanje…',
  ctaOpening: 'Otvaranje plaćanja…',
  sideFoot: 'Mjesečna naplata karticom',
  back: 'Svi paketi',
  timingTitle: 'Kada novi paket treba početi?',
  timingGroup: 'Kada novi paket počinje',
  summaryInclVat: '{price} mjesečno s PDV-om',
  summaryExclVat: '{price} mjesečno bez PDV-a',
  summaryOff: '{percent} popusta',
  nowTitle: 'Počni odmah',
  renewalTitle: 'Pri obnovi, {date}',
  today: 'Danas',
  fromThenOn: 'Nakon toga',
  onDate: 'Dana {date}',
  codeNote: '({percent} popusta)',
  codeNoteUntil: '({percent} popusta do {date})',
  nowToday: 'Novi paket počinje. Plaćate {price}{codeNote} i odmah dobivate {credits}.',
  nowTodayOnTop:
    'Novi paket počinje. Plaćate {price}{codeNote} i odmah dobivate {credits}, uz {balance} kredita koje već imate (ukupno {total}).',
  nowThen:
    'Nakon toga {credits} i {price} svaki mjesec. Trenutačni paket završava danas i više se ne naplaćuje.',
  nowThenUntil:
    '{credits} i {price} svaki mjesec do {date}, zatim {fullPrice}. Trenutačni paket završava danas i više se ne naplaćuje.',
  renewalToday:
    'Ništa se ne mijenja. Zadržavate trenutačni paket i svojih {balance} kredita i ništa ne plaćate.',
  renewalTodayPaused:
    'Ništa se ne mijenja. Zadržavate trenutačni paket i svojih {balance} kredita i ništa ne plaćate. Korištenje ostaje pauzirano do tada.',
  renewalThen:
    'Novi paket počinje. Plaćate {price}{codeNote} i tada dobivate {credits}, ne ranije.',
  footSe: 'Iznosi uključuju 25 % PDV-a. Naplaćujemo karticu navedenu na stranici Naplata.',
  footUnknown:
    'Iznosi uključuju 25 % švedskog PDV-a. PDV se obračunava pri plaćanju gdje je primjenjiv.',
  footReverse:
    'Iznosi ne uključuju PDV, koji se obračunava prijenosom porezne obveze. Naplaćujemo karticu navedenu na stranici Naplata.',
  cancel: 'Odustani',
  confirmNow: 'Plati {price} i promijeni',
  confirmRenewal: 'Zakaži prelazak na {credits} kredita',
  paying: 'Plaćanje…',
  scheduling: 'Zakazivanje…',
  toastSwitched: 'Novi paket je aktivan. Saldu je dodano {credits} kredita.',
  toastScheduled: 'Novi paket počinje {date}. Do tada se ništa ne mijenja.',
  toastCode: '{percent} popusta vrijedi od {date}, tijekom {months} mj.',
  toastCodeOneMonth: '{percent} popusta vrijedi od {date}, tijekom 1 mjeseca.',
  toastCodeForGood: '{percent} popusta vrijedi od {date}.',
  errorDeclined:
    'Vaša je kartica odbijena. Ažurirajte je u odjeljku Način plaćanja i računi, a zatim pokušajte ponovno.',
  errorDiscountActive: 'Popust je već aktivan. Prenosi se na novi paket.',
  errorComplimentary:
    'Vaš je paket besplatan zahvaljujući kodu, pa novi paket može početi tek pri obnovi.',
  errorCodeNotAccepted: 'Taj se kod ne može koristiti za ovaj paket.',
  codeAlreadyUsed: 'Vaša je tvrtka već iskoristila ovaj kod.',
  errorGeneric: 'Paket nije bilo moguće promijeniti. Pokušajte ponovno.',
  errorEditing: 'Dovršite ili otkažite uređivanje prethodnog pitanja prije otvaranja plaćanja.',
  errorAttachments:
    'Vaš nacrt ima priložene datoteke. Uklonite ih prije otvaranja plaćanja. Tekst pitanja je spremljen, a datoteke možete ponovno priložiti nakon toga.',
  errorStorage:
    'Vaš preglednik nije mogao spremiti nacrt. Kopirajte pitanje na sigurno mjesto i dopustite pohranu sesije prije otvaranja plaćanja.',
  planPerMonth: '{credits} kredita / mjesec',
  creditsCount: '{count} kredita',
  billingTitle: 'Naplata',
  billingIntro: 'Paket, krediti i plaćanje vaše tvrtke.',
  planLabel: 'Paket',
  noPlan: 'Nema paketa',
  planPaymentFailed: 'Plaćanje nije uspjelo',
  planEndedOn: 'Završio {date}',
  planEnded: 'Završen',
  codeNotPercent: 'Ovaj kod oduzima fiksni iznos. Ovdje rade samo postotni kodovi.',
  balanceLabel: 'Preostali krediti',
  nextLabel: 'Sljedeće',
  renewsWith: '+{credits} kredita {date}',
  switchesTo: 'Prelazi na {credits} kredita / mjesec {date}',
  endsOn: 'Završava {date}',
  discountLabel: 'Popust',
  creditsNeverExpire: 'Krediti nikad ne istječu.',
  paymentFailed:
    'Vaše posljednje plaćanje nije uspjelo. Ažurirajte karticu u odjeljku Način plaćanja i računi.',
  seeUsage: 'Prikaži potrošnju',
  changePlan: 'Promijeni paket',
  choosePlan: 'Odaberi paket',
  portal: 'Način plaćanja i računi',
  opening: 'Otvaranje…',
  readOnly: 'Paketom i plaćanjem upravlja vlasnik tvrtke.',
  nativeOnly: 'Paketima i plaćanjem upravlja se na webu.',
  loading: 'Učitavanje naplate…',
  retry: 'Pokušaj ponovno',
  unavailable:
    'Naplata je privremeno nedostupna. Pokušajte ponovno. Vaša povijest i postavke i dalje su dostupne.',
  portalError: 'Postavke plaćanja nije bilo moguće otvoriti. Pokušajte ponovno.',
  returnPending: 'Čeka se potvrda plaćanja. Krediti će se prikazati kada plaćanje bude potvrđeno.',
  returnDelayed: 'Potvrda plaćanja traje dulje od očekivanog. Provjerite ponovno uskoro.',
  returnCanceled: 'Plaćanje je otkazano. Ništa nije naplaćeno.',
  pausedPlanTitle: 'Odaberite paket za nastavak',
  pausedPlanOwner:
    'Poneva radi s mjesečnim paketima kredita. Odaberite paket kako biste nastavili postavljati pitanja.',
  pausedPlanMember:
    'Vaša tvrtka nema aktivan paket. Vlasnik tvrtke odabire ga u Postavkama, u odjeljku Potrošnja.',
  pausedPastDue:
    'Vaše posljednje plaćanje nije uspjelo pa je paket pauziran. Ažurirajte karticu u odjeljku Način plaćanja i računi.',
  pausedPastDueMember:
    'Posljednje plaćanje tvrtke nije uspjelo pa je paket pauziran. Vlasnik tvrtke može ažurirati karticu.',
  pausedCreditsTitle: 'Potrošili ste sve kredite',
  pausedCreditsBody:
    'Nadogradite paket sada ili pričekajte do {date} u {time}, kada paket dodaje {credits} kredita.',
  pausedCreditsMember:
    'Zamolite vlasnika tvrtke da nadogradi paket ili pričekajte do {date} u {time}, kada paket dodaje {credits} kredita.',
  pausedCreditsNoRenewal: 'Odaberite veći paket za nastavak.',
  pausedLimitTitle: 'Dosegnuli ste mjesečno ograničenje',
  pausedLimitBody:
    'Iskoristili ste mjesečno ograničenje od {limit} kredita. Ono se poništava {date} ili kada ga vlasnik tvrtke poveća.',
  pausedLimitNoDate:
    'Iskoristili ste mjesečno ograničenje od {limit} kredita. Vlasnik tvrtke može ga povećati.',
  upgradeNow: 'Nadogradi sada',
  limitNotice:
    'Preostalo je {left} od vaših {limit} mjesečnih kredita. Ograničenje se poništava {date}.',
  limitNoticeNoDate: 'Preostalo je {left} od vaših {limit} mjesečnih kredita.',
};

const bg: Copy = {
  dialogTitle: 'Смяна на плана',
  dialogTitleNew: 'Изберете план',
  dialogSub:
    'Всеки месец планът добавя своите кредити към баланса, който целият екип споделя. Неизползваните кредити се прехвърлят и никога не изтичат, а можете да ги използвате, докато има активен план.',
  close: 'Затвори',
  kickerUpgrade: 'По-голям план',
  kickerDowngrade: 'По-малък план',
  currentPlanTag: 'Текущ план',
  currentPin: 'Текущ',
  creditsEveryMonth: 'кредита всеки месец',
  captionCurrent: 'Текущият Ви план.',
  captionMore: '{count} кредита повече на месец от текущия Ви план.',
  captionFewer: '{count} кредита по-малко на месец от текущия Ви план.',
  captionRecent: 'Екипът Ви е използвал {count} кредита през последните 30 дни.',
  scaleEnd: '{price} на месец',
  sliderLabel: 'План',
  sliderValue: '{credits} кредита на месец, {price} без ДДС',
  priceLabel: 'Цена на месец',
  exclVat: 'без ДДС',
  exclVatForMonths: 'без ДДС, за {months} мес.',
  exclVatForOneMonth: 'без ДДС, за 1 месец',
  exclVatUntil: 'без ДДС, до {date}',
  perCreditLabel: 'Цена за кредит',
  volumeLabel: 'Отстъпка за обем',
  volumeNone: 'Няма',
  volumeNote: 'спрямо цената от {price} за кредит при най-малкия план',
  volumeBase: 'Най-малкият план определя базовата цена за кредит',
  vatLabel: 'С 25% ДДС',
  perMonth: 'на месец',
  vatReverseLabel: 'ДДС',
  vatReverseValue: 'Обратно начисляване',
  codeAdd: 'Добавете код за отстъпка',
  codePlaceholder: 'Код за отстъпка',
  codeApply: 'Приложи',
  codeChecking: 'Проверка…',
  codeNotFound: 'Не намерихме този код. Проверете изписването и опитайте отново.',
  codeRemove: 'Премахни кода',
  codeForMonths: '{percent} отстъпка за {months} мес.',
  codeForOneMonth: '{percent} отстъпка за 1 месец',
  codeForGood: '{percent} отстъпка завинаги',
  codeUntil: '{percent} отстъпка до {date}',
  codeCarries: '{percent} отстъпка до {date}, прехвърля се',
  codeCarriesForGood: '{percent} отстъпка завинаги, прехвърля се',
  ctaSwitch: 'Преминете към {credits} кредита',
  ctaUseCode: 'Използвайте кода за този план',
  ctaCurrent: 'Текущият Ви план',
  ctaCheckout: 'Продължете към плащане',
  ctaSaving: 'Запазване…',
  ctaOpening: 'Отваряне на плащането…',
  sideFoot: 'Месечно плащане с карта',
  back: 'Всички планове',
  timingTitle: 'Кога да започне новият план?',
  timingGroup: 'Кога започва новият план',
  summaryInclVat: '{price} на месец с ДДС',
  summaryExclVat: '{price} на месец без ДДС',
  summaryOff: '{percent} отстъпка',
  nowTitle: 'Започнете сега',
  renewalTitle: 'При подновяване, {date}',
  today: 'Днес',
  fromThenOn: 'След това',
  onDate: 'На {date}',
  codeNote: '({percent} отстъпка)',
  codeNoteUntil: '({percent} отстъпка до {date})',
  nowToday: 'Новият план започва. Плащате {price}{codeNote} и получавате {credits} веднага.',
  nowTodayOnTop:
    'Новият план започва. Плащате {price}{codeNote} и получавате {credits} веднага, в допълнение към {balance} кредита, които вече имате (общо {total}).',
  nowThen:
    '{credits} и {price} всеки месец след това. Текущият Ви план приключва днес и няма да бъде таксуван отново.',
  nowThenUntil:
    '{credits} и {price} всеки месец до {date}, след това {fullPrice}. Текущият Ви план приключва днес и няма да бъде таксуван отново.',
  renewalToday:
    'Нищо не се променя. Запазвате текущия си план и своите {balance} кредита и не плащате нищо.',
  renewalTodayPaused:
    'Нищо не се променя. Запазвате текущия си план и своите {balance} кредита и не плащате нищо. Използването остава на пауза дотогава.',
  renewalThen:
    'Новият план започва. Плащате {price}{codeNote} и получавате {credits} тогава, не по-рано.',
  footSe: 'Сумите включват 25% ДДС. Таксуваме картата от страницата Плащания.',
  footUnknown: 'Сумите включват 25% шведски ДДС. ДДС се добавя при плащането, където е приложимо.',
  footReverse:
    'Сумите са без ДДС, който се дължи чрез обратно начисляване. Таксуваме картата от страницата Плащания.',
  cancel: 'Отказ',
  confirmNow: 'Платете {price} и сменете',
  confirmRenewal: 'Насрочете смяна на {credits} кредита',
  paying: 'Плащане…',
  scheduling: 'Насрочване…',
  toastSwitched: 'Новият Ви план е активен. Към баланса Ви бяха добавени {credits} кредита.',
  toastScheduled: 'Новият Ви план започва на {date}. Дотогава нищо не се променя.',
  toastCode: '{percent} отстъпка се прилага от {date}, за {months} мес.',
  toastCodeOneMonth: '{percent} отстъпка се прилага от {date}, за 1 месец.',
  toastCodeForGood: '{percent} отстъпка се прилага от {date}.',
  errorDeclined:
    'Картата Ви беше отхвърлена. Обновете я в Метод на плащане и фактури и опитайте отново.',
  errorDiscountActive: 'Вече има активна отстъпка. Тя се прехвърля към новия план.',
  errorComplimentary:
    'Планът Ви е безплатен с код, затова нов план може да започне само при подновяване.',
  errorCodeNotAccepted: 'Този код не може да се използва за този план.',
  codeAlreadyUsed: 'Компанията Ви вече е използвала този код.',
  errorGeneric: 'Не успяхме да сменим плана. Опитайте отново.',
  errorEditing:
    'Завършете или отменете редакцията на предишния си въпрос, преди да отворите плащането.',
  errorAttachments:
    'Черновата Ви има прикачени файлове. Премахнете ги, преди да отворите плащането. Текстът на въпроса е запазен и можете да прикачите файловете отново след това.',
  errorStorage:
    'Браузърът Ви не успя да запази черновата. Копирайте въпроса си на сигурно място и разрешете хранилището на сесията, преди да отворите плащането.',
  planPerMonth: '{credits} кредита / месец',
  creditsCount: '{count} кредита',
  billingTitle: 'Плащания',
  billingIntro: 'Планът, кредитите и плащанията на компанията Ви.',
  planLabel: 'План',
  noPlan: 'Няма план',
  planPaymentFailed: 'Плащането е неуспешно',
  planEndedOn: 'Приключи на {date}',
  planEnded: 'Приключил',
  codeNotPercent: 'Този код дава отстъпка с фиксирана сума. Тук работят само процентни кодове.',
  balanceLabel: 'Оставащи кредити',
  nextLabel: 'Следващо',
  renewsWith: '+{credits} кредита на {date}',
  switchesTo: 'Преминава към {credits} кредита / месец на {date}',
  endsOn: 'Приключва на {date}',
  discountLabel: 'Отстъпка',
  creditsNeverExpire: 'Кредитите никога не изтичат.',
  paymentFailed:
    'Последното Ви плащане не беше успешно. Обновете картата в Метод на плащане и фактури.',
  seeUsage: 'Вижте потреблението',
  changePlan: 'Смяна на плана',
  choosePlan: 'Изберете план',
  portal: 'Метод на плащане и фактури',
  opening: 'Отваряне…',
  readOnly: 'Собственикът на компанията управлява плана и плащанията.',
  nativeOnly: 'Плановете и плащанията се управляват в уеб версията.',
  loading: 'Зареждане на плащанията…',
  retry: 'Опитайте отново',
  unavailable:
    'Плащанията временно не са достъпни. Опитайте отново. Историята и настройките Ви все още са достъпни.',
  portalError: 'Настройките за плащане не можаха да се отворят. Опитайте отново.',
  returnPending:
    'Изчакваме потвърждение на плащането. Кредитите ще се появят, след като плащането бъде потвърдено.',
  returnDelayed:
    'Потвърждението на плащането отнема повече време от очакваното. Проверете отново след малко.',
  returnCanceled: 'Плащането беше отменено. Нищо не е таксувано.',
  pausedPlanTitle: 'Изберете план, за да продължите',
  pausedPlanOwner:
    'Poneva работи с месечни планове с кредити. Изберете план, за да продължите да задавате въпроси.',
  pausedPlanMember:
    'Компанията Ви няма активен план. Собственикът на компанията избира план в Настройки, раздел Потребление.',
  pausedPastDue:
    'Последното Ви плащане не беше успешно, затова планът е на пауза. Обновете картата в Метод на плащане и фактури.',
  pausedPastDueMember:
    'Последното плащане на компанията не беше успешно, затова планът е на пауза. Собственикът на компанията може да обнови картата.',
  pausedCreditsTitle: 'Използвахте всичките си кредити',
  pausedCreditsBody:
    'Преминете към по-голям план сега или изчакайте до {date} в {time}, когато планът Ви добавя {credits} кредита.',
  pausedCreditsMember:
    'Помолете собственика на компанията да премине към по-голям план или изчакайте до {date} в {time}, когато планът Ви добавя {credits} кредита.',
  pausedCreditsNoRenewal: 'Изберете по-голям план, за да продължите.',
  pausedLimitTitle: 'Достигнахте месечния си лимит',
  pausedLimitBody:
    'Използвахте месечния си лимит от {limit} кредита. Той се нулира на {date} или когато собственикът на компанията го увеличи.',
  pausedLimitNoDate:
    'Използвахте месечния си лимит от {limit} кредита. Собственикът на компанията може да го увеличи.',
  upgradeNow: 'Към по-голям план',
  limitNotice: 'Остават {left} от Вашите {limit} месечни кредита. Лимитът Ви се нулира на {date}.',
  limitNoticeNoDate: 'Остават {left} от Вашите {limit} месечни кредита.',
};

const el: Copy = {
  dialogTitle: 'Αλλαγή προγράμματος',
  dialogTitleNew: 'Επιλογή προγράμματος',
  dialogSub:
    'Κάθε μήνα το πρόγραμμά σας προσθέτει τις πιστώσεις του στο υπόλοιπο που μοιράζεται όλη η ομάδα. Οι πιστώσεις που δεν χρησιμοποιούνται μεταφέρονται και δεν λήγουν ποτέ, και μπορείτε να τις χρησιμοποιείτε όσο υπάρχει ενεργό πρόγραμμα.',
  close: 'Κλείσιμο',
  kickerUpgrade: 'Αναβάθμιση',
  kickerDowngrade: 'Υποβάθμιση',
  currentPlanTag: 'Τρέχον πρόγραμμα',
  currentPin: 'Τρέχον',
  creditsEveryMonth: 'πιστώσεις κάθε μήνα',
  captionCurrent: 'Το τρέχον πρόγραμμά σας.',
  captionMore: '{count} περισσότερες πιστώσεις τον μήνα από το τρέχον πρόγραμμά σας.',
  captionFewer: '{count} λιγότερες πιστώσεις τον μήνα από το τρέχον πρόγραμμά σας.',
  captionRecent: 'Η ομάδα σας χρησιμοποίησε {count} πιστώσεις τις τελευταίες 30 ημέρες.',
  scaleEnd: '{price} τον μήνα',
  sliderLabel: 'Πρόγραμμα',
  sliderValue: '{credits} πιστώσεις τον μήνα, {price} χωρίς ΦΠΑ',
  priceLabel: 'Τιμή ανά μήνα',
  exclVat: 'χωρίς ΦΠΑ',
  exclVatForMonths: 'χωρίς ΦΠΑ, για {months} μήνες',
  exclVatForOneMonth: 'χωρίς ΦΠΑ, για 1 μήνα',
  exclVatUntil: 'χωρίς ΦΠΑ, έως {date}',
  perCreditLabel: 'Τιμή ανά πίστωση',
  volumeLabel: 'Έκπτωση όγκου',
  volumeNone: 'Καμία',
  volumeNote: 'σε σχέση με την τιμή {price} ανά πίστωση του μικρότερου προγράμματος',
  volumeBase: 'Το μικρότερο πρόγραμμα ορίζει τη βασική τιμή ανά πίστωση',
  vatLabel: 'Με ΦΠΑ 25%',
  perMonth: 'τον μήνα',
  vatReverseLabel: 'ΦΠΑ',
  vatReverseValue: 'Αντίστροφη επιβάρυνση',
  codeAdd: 'Προσθήκη κωδικού έκπτωσης',
  codePlaceholder: 'Κωδικός έκπτωσης',
  codeApply: 'Εφαρμογή',
  codeChecking: 'Έλεγχος…',
  codeNotFound: 'Δεν βρήκαμε αυτόν τον κωδικό. Ελέγξτε την ορθογραφία και δοκιμάστε ξανά.',
  codeRemove: 'Αφαίρεση κωδικού',
  codeForMonths: 'Έκπτωση {percent} για {months} μήνες',
  codeForOneMonth: 'Έκπτωση {percent} για 1 μήνα',
  codeForGood: 'Έκπτωση {percent} μόνιμα',
  codeUntil: 'Έκπτωση {percent} έως {date}',
  codeCarries: 'Έκπτωση {percent} έως {date}, μεταφέρεται',
  codeCarriesForGood: 'Έκπτωση {percent} μόνιμα, μεταφέρεται',
  ctaSwitch: 'Αλλαγή σε {credits} πιστώσεις',
  ctaUseCode: 'Χρήση κωδικού σε αυτό το πρόγραμμα',
  ctaCurrent: 'Το τρέχον πρόγραμμά σας',
  ctaCheckout: 'Συνέχεια στην πληρωμή',
  ctaSaving: 'Αποθήκευση…',
  ctaOpening: 'Άνοιγμα πληρωμής…',
  sideFoot: 'Μηνιαία χρέωση με κάρτα',
  back: 'Όλα τα προγράμματα',
  timingTitle: 'Πότε να ξεκινήσει το νέο πρόγραμμα;',
  timingGroup: 'Πότε ξεκινά το νέο πρόγραμμα',
  summaryInclVat: '{price} τον μήνα με ΦΠΑ',
  summaryExclVat: '{price} τον μήνα χωρίς ΦΠΑ',
  summaryOff: 'Έκπτωση {percent}',
  nowTitle: 'Έναρξη τώρα',
  renewalTitle: 'Στην ανανέωση, {date}',
  today: 'Σήμερα',
  fromThenOn: 'Στη συνέχεια',
  onDate: 'Στις {date}',
  codeNote: '(έκπτωση {percent})',
  codeNoteUntil: '(έκπτωση {percent} έως {date})',
  nowToday: 'Το νέο πρόγραμμα ξεκινά. Πληρώνετε {price}{codeNote} και λαμβάνετε αμέσως {credits}.',
  nowTodayOnTop:
    'Το νέο πρόγραμμα ξεκινά. Πληρώνετε {price}{codeNote} και λαμβάνετε αμέσως {credits}, επιπλέον των {balance} πιστώσεων που ήδη έχετε ({total} συνολικά).',
  nowThen:
    '{credits} και {price} κάθε μήνα μετά από αυτό. Το τρέχον πρόγραμμά σας λήγει σήμερα και δεν χρεώνεται ξανά.',
  nowThenUntil:
    '{credits} και {price} κάθε μήνα έως {date}, μετά {fullPrice}. Το τρέχον πρόγραμμά σας λήγει σήμερα και δεν χρεώνεται ξανά.',
  renewalToday:
    'Τίποτα δεν αλλάζει. Διατηρείτε το τρέχον πρόγραμμά σας και τις {balance} πιστώσεις σας και δεν πληρώνετε τίποτα.',
  renewalTodayPaused:
    'Τίποτα δεν αλλάζει. Διατηρείτε το τρέχον πρόγραμμά σας και τις {balance} πιστώσεις σας και δεν πληρώνετε τίποτα. Η χρήση παραμένει σε παύση μέχρι τότε.',
  renewalThen:
    'Το νέο πρόγραμμα ξεκινά. Πληρώνετε {price}{codeNote} και λαμβάνετε {credits} τότε, όχι νωρίτερα.',
  footSe: 'Τα ποσά περιλαμβάνουν ΦΠΑ 25%. Χρεώνουμε την κάρτα που ορίζεται στη σελίδα Χρέωση.',
  footUnknown:
    'Τα ποσά περιλαμβάνουν σουηδικό ΦΠΑ 25%. Ο ΦΠΑ προστίθεται κατά την πληρωμή όπου ισχύει.',
  footReverse:
    'Τα ποσά δεν περιλαμβάνουν ΦΠΑ, ο οποίος καταλογίζεται με αντίστροφη επιβάρυνση. Χρεώνουμε την κάρτα που ορίζεται στη σελίδα Χρέωση.',
  cancel: 'Ακύρωση',
  confirmNow: 'Πληρωμή {price} και αλλαγή',
  confirmRenewal: 'Προγραμματισμός αλλαγής σε {credits} πιστώσεις',
  paying: 'Πληρωμή…',
  scheduling: 'Προγραμματισμός…',
  toastSwitched:
    'Το νέο πρόγραμμά σας είναι ενεργό. Προστέθηκαν {credits} πιστώσεις στο υπόλοιπό σας.',
  toastScheduled: 'Το νέο πρόγραμμά σας ξεκινά στις {date}. Μέχρι τότε τίποτα δεν αλλάζει.',
  toastCode: 'Η έκπτωση {percent} ισχύει από {date}, για {months} μήνες.',
  toastCodeOneMonth: 'Η έκπτωση {percent} ισχύει από {date}, για 1 μήνα.',
  toastCodeForGood: 'Η έκπτωση {percent} ισχύει από {date}.',
  errorDeclined:
    'Η κάρτα σας απορρίφθηκε. Ενημερώστε την στην ενότητα Τρόπος πληρωμής και τιμολόγια και δοκιμάστε ξανά.',
  errorDiscountActive: 'Υπάρχει ήδη ενεργή έκπτωση. Μεταφέρεται στο νέο πρόγραμμα.',
  errorComplimentary:
    'Το πρόγραμμά σας είναι δωρεάν με κωδικό, οπότε νέο πρόγραμμα μπορεί να ξεκινήσει μόνο στην ανανέωση.',
  errorCodeNotAccepted: 'Αυτός ο κωδικός δεν μπορεί να χρησιμοποιηθεί για αυτό το πρόγραμμα.',
  codeAlreadyUsed: 'Η εταιρεία σας έχει ήδη χρησιμοποιήσει αυτόν τον κωδικό.',
  errorGeneric: 'Δεν ήταν δυνατή η αλλαγή του προγράμματος. Δοκιμάστε ξανά.',
  errorEditing:
    'Ολοκληρώστε ή ακυρώστε την επεξεργασία της προηγούμενης ερώτησής σας πριν ανοίξετε την πληρωμή.',
  errorAttachments:
    'Το πρόχειρό σας έχει συνημμένα αρχεία. Αφαιρέστε τα πριν ανοίξετε την πληρωμή. Το κείμενο της ερώτησής σας αποθηκεύεται και μπορείτε να επισυνάψετε ξανά τα αρχεία μετά.',
  errorStorage:
    'Το πρόγραμμα περιήγησής σας δεν μπόρεσε να αποθηκεύσει το πρόχειρο. Αντιγράψτε την ερώτησή σας σε ασφαλές σημείο και επιτρέψτε την αποθήκευση περιόδου λειτουργίας πριν ανοίξετε την πληρωμή.',
  planPerMonth: '{credits} πιστώσεις / μήνα',
  creditsCount: '{count} πιστώσεις',
  billingTitle: 'Χρέωση',
  billingIntro: 'Το πρόγραμμα, οι πιστώσεις και οι πληρωμές της εταιρείας σας.',
  planLabel: 'Πρόγραμμα',
  noPlan: 'Χωρίς πρόγραμμα',
  planPaymentFailed: 'Η πληρωμή απέτυχε',
  planEndedOn: 'Έληξε στις {date}',
  planEnded: 'Έληξε',
  codeNotPercent: 'Αυτός ο κωδικός αφαιρεί σταθερό ποσό. Εδώ λειτουργούν μόνο κωδικοί ποσοστού.',
  balanceLabel: 'Υπόλοιπες πιστώσεις',
  nextLabel: 'Επόμενη',
  renewsWith: '+{credits} πιστώσεις στις {date}',
  switchesTo: 'Αλλάζει σε {credits} πιστώσεις / μήνα στις {date}',
  endsOn: 'Λήγει στις {date}',
  discountLabel: 'Έκπτωση',
  creditsNeverExpire: 'Οι πιστώσεις δεν λήγουν ποτέ.',
  paymentFailed:
    'Η τελευταία σας πληρωμή δεν ολοκληρώθηκε. Ενημερώστε την κάρτα στην ενότητα Τρόπος πληρωμής και τιμολόγια.',
  seeUsage: 'Προβολή χρήσης',
  changePlan: 'Αλλαγή προγράμματος',
  choosePlan: 'Επιλογή προγράμματος',
  portal: 'Τρόπος πληρωμής και τιμολόγια',
  opening: 'Άνοιγμα…',
  readOnly: 'Ο κάτοχος της εταιρείας διαχειρίζεται το πρόγραμμα και τις πληρωμές.',
  nativeOnly: 'Η διαχείριση προγραμμάτων και πληρωμών γίνεται στον ιστό.',
  loading: 'Φόρτωση χρέωσης…',
  retry: 'Δοκιμάστε ξανά',
  unavailable:
    'Η χρέωση δεν είναι προσωρινά διαθέσιμη. Δοκιμάστε ξανά. Το ιστορικό και οι ρυθμίσεις σας παραμένουν διαθέσιμα.',
  portalError: 'Δεν ήταν δυνατό να ανοίξουν οι ρυθμίσεις πληρωμής. Δοκιμάστε ξανά.',
  returnPending:
    'Αναμονή επιβεβαίωσης πληρωμής. Οι πιστώσεις σας θα εμφανιστούν μόλις επιβεβαιωθεί η πληρωμή.',
  returnDelayed:
    'Η επιβεβαίωση της πληρωμής καθυστερεί περισσότερο από το αναμενόμενο. Ελέγξτε ξανά σε λίγο.',
  returnCanceled: 'Η πληρωμή ακυρώθηκε. Δεν έγινε καμία χρέωση.',
  pausedPlanTitle: 'Επιλέξτε πρόγραμμα για να συνεχίσετε',
  pausedPlanOwner:
    'Το Poneva λειτουργεί με μηνιαία προγράμματα πιστώσεων. Επιλέξτε πρόγραμμα για να συνεχίσετε να κάνετε ερωτήσεις.',
  pausedPlanMember:
    'Η εταιρεία σας δεν έχει ενεργό πρόγραμμα. Ο κάτοχος της εταιρείας επιλέγει ένα στις Ρυθμίσεις, στην ενότητα Χρήση.',
  pausedPastDue:
    'Η τελευταία σας πληρωμή δεν ολοκληρώθηκε, οπότε το πρόγραμμα είναι σε παύση. Ενημερώστε την κάρτα στην ενότητα Τρόπος πληρωμής και τιμολόγια.',
  pausedPastDueMember:
    'Η τελευταία πληρωμή της εταιρείας δεν ολοκληρώθηκε, οπότε το πρόγραμμα είναι σε παύση. Ο κάτοχος της εταιρείας μπορεί να ενημερώσει την κάρτα.',
  pausedCreditsTitle: 'Χρησιμοποιήσατε όλες τις πιστώσεις σας',
  pausedCreditsBody:
    'Κάντε αναβάθμιση τώρα ή περιμένετε έως {date} στις {time}, όταν το πρόγραμμά σας προσθέτει {credits} πιστώσεις.',
  pausedCreditsMember:
    'Ζητήστε από τον κάτοχο της εταιρείας να κάνει αναβάθμιση ή περιμένετε έως {date} στις {time}, όταν το πρόγραμμά σας προσθέτει {credits} πιστώσεις.',
  pausedCreditsNoRenewal: 'Επιλέξτε μεγαλύτερο πρόγραμμα για να συνεχίσετε.',
  pausedLimitTitle: 'Φτάσατε το μηνιαίο όριό σας',
  pausedLimitBody:
    'Χρησιμοποιήσατε το μηνιαίο όριό σας των {limit} πιστώσεων. Μηδενίζεται στις {date} ή όταν ο κάτοχος της εταιρείας το αυξήσει.',
  pausedLimitNoDate:
    'Χρησιμοποιήσατε το μηνιαίο όριό σας των {limit} πιστώσεων. Ο κάτοχος της εταιρείας μπορεί να το αυξήσει.',
  upgradeNow: 'Αναβάθμιση τώρα',
  limitNotice:
    'Απομένουν {left} από τις {limit} μηνιαίες πιστώσεις σας. Το όριό σας μηδενίζεται στις {date}.',
  limitNoticeNoDate: 'Απομένουν {left} από τις {limit} μηνιαίες πιστώσεις σας.',
};

const hu: Copy = {
  dialogTitle: 'Csomag módosítása',
  dialogTitleNew: 'Csomag kiválasztása',
  dialogSub:
    'A csomag minden hónapban hozzáadja a kreditjeit a teljes csapat közös egyenlegéhez. A fel nem használt kreditek megmaradnak és soha nem járnak le, és addig használhatja őket, amíg van aktív csomag.',
  close: 'Bezárás',
  kickerUpgrade: 'Nagyobb csomag',
  kickerDowngrade: 'Kisebb csomag',
  currentPlanTag: 'Jelenlegi csomag',
  currentPin: 'Jelenlegi',
  creditsEveryMonth: 'kredit havonta',
  captionCurrent: 'Az Ön jelenlegi csomagja.',
  captionMore: 'Havonta {count} kredittel több, mint a jelenlegi csomagja.',
  captionFewer: 'Havonta {count} kredittel kevesebb, mint a jelenlegi csomagja.',
  captionRecent: 'A csapata {count} kreditet használt fel az elmúlt 30 napban.',
  scaleEnd: '{price} havonta',
  sliderLabel: 'Csomag',
  sliderValue: 'Havi {credits} kredit, {price} áfa nélkül',
  priceLabel: 'Havi ár',
  exclVat: 'áfa nélkül',
  exclVatForMonths: 'áfa nélkül, {months} hónapig',
  exclVatForOneMonth: 'áfa nélkül, 1 hónapig',
  exclVatUntil: 'áfa nélkül, eddig: {date}',
  perCreditLabel: 'Kreditenkénti ár',
  volumeLabel: 'Mennyiségi kedvezmény',
  volumeNone: 'Nincs',
  volumeNote: 'a legkisebb csomag kreditenkénti {price} árához képest',
  volumeBase: 'A legkisebb csomag határozza meg a kreditenkénti alapárat',
  vatLabel: '25% áfával',
  perMonth: 'havonta',
  vatReverseLabel: 'Áfa',
  vatReverseValue: 'Fordított adózás',
  codeAdd: 'Kedvezménykód hozzáadása',
  codePlaceholder: 'Kedvezménykód',
  codeApply: 'Alkalmaz',
  codeChecking: 'Ellenőrzés…',
  codeNotFound: 'Nem találjuk ezt a kódot. Ellenőrizze a helyesírást, és próbálja újra.',
  codeRemove: 'Kód eltávolítása',
  codeForMonths: '{percent} kedvezmény {months} hónapig',
  codeForOneMonth: '{percent} kedvezmény 1 hónapig',
  codeForGood: '{percent} kedvezmény véglegesen',
  codeUntil: '{percent} kedvezmény, eddig: {date}',
  codeCarries: '{percent} kedvezmény, eddig: {date}, megmarad',
  codeCarriesForGood: '{percent} kedvezmény véglegesen, megmarad',
  ctaSwitch: 'Váltás {credits} kreditre',
  ctaUseCode: 'Kód használata ehhez a csomaghoz',
  ctaCurrent: 'Az Ön jelenlegi csomagja',
  ctaCheckout: 'Tovább a fizetéshez',
  ctaSaving: 'Mentés…',
  ctaOpening: 'Fizetés megnyitása…',
  sideFoot: 'Havi számlázás bankkártyával',
  back: 'Összes csomag',
  timingTitle: 'Mikor induljon az új csomag?',
  timingGroup: 'Az új csomag kezdete',
  summaryInclVat: '{price} havonta, áfával',
  summaryExclVat: '{price} havonta, áfa nélkül',
  summaryOff: '{percent} kedvezmény',
  nowTitle: 'Indítás most',
  renewalTitle: 'Megújításkor, {date}',
  today: 'Ma',
  fromThenOn: 'Ezt követően',
  onDate: 'Ekkor: {date}',
  codeNote: '({percent} kedvezmény)',
  codeNoteUntil: '({percent} kedvezmény, eddig: {date})',
  nowToday: 'Az új csomag elindul. Fizetendő: {price}{codeNote}. Azonnal jóváírva: {credits}.',
  nowTodayOnTop:
    'Az új csomag elindul. Fizetendő: {price}{codeNote}. Azonnal jóváírva: {credits}, a meglévő {balance} mellé (összesen {total}).',
  nowThen:
    'Ezután havonta {credits}, ára {price}. A jelenlegi csomag ma véget ér, és többé nem számítunk fel érte díjat.',
  nowThenUntil:
    'Havonta {credits} {price} áron, ez az ár eddig érvényes: {date}. Utána {fullPrice}. A jelenlegi csomag ma véget ér, és többé nem számítunk fel érte díjat.',
  renewalToday:
    'Nincs változás. Megtartja a jelenlegi csomagját és a meglévő {balance} kreditjét, és nem fizet semmit.',
  renewalTodayPaused:
    'Nincs változás. Megtartja a jelenlegi csomagját és a meglévő {balance} kreditjét, és nem fizet semmit. A használat addig szünetel.',
  renewalThen:
    'Az új csomag elindul. Fizetendő: {price}{codeNote}. Ekkor jóváírva: {credits}, nem korábban.',
  footSe: 'Az összegek tartalmazzák a 25% áfát. A Számlázás oldalon megadott kártyát terheljük.',
  footUnknown:
    'Az összegek tartalmazzák a 25% svéd áfát. Az áfát a fizetéskor számítjuk fel, ahol alkalmazandó.',
  footReverse:
    'Az összegek nem tartalmaznak áfát, mert fordított adózás alá esnek. A Számlázás oldalon megadott kártyát terheljük.',
  cancel: 'Mégse',
  confirmNow: '{price} fizetése és váltás',
  confirmRenewal: 'Váltás ütemezése {credits} kreditre',
  paying: 'Fizetés…',
  scheduling: 'Ütemezés…',
  toastSwitched: 'Az új csomag aktív. {credits} kreditet hozzáadtunk az egyenlegéhez.',
  toastScheduled: 'Az új csomag ekkor indul: {date}. Addig nincs változás.',
  toastCode: '{percent} kedvezmény, érvényes ettől: {date}, {months} hónapig.',
  toastCodeOneMonth: '{percent} kedvezmény, érvényes ettől: {date}, 1 hónapig.',
  toastCodeForGood: '{percent} kedvezmény, érvényes ettől: {date}.',
  errorDeclined:
    'A kártyát elutasították. Frissítse a Fizetési mód és számlák alatt, majd próbálja újra.',
  errorDiscountActive: 'Már van aktív kedvezmény. Ez átkerül az új csomagra.',
  errorComplimentary: 'A csomag egy kóddal ingyenes, így új csomag csak megújításkor indulhat.',
  errorCodeNotAccepted: 'Ez a kód nem használható ehhez a csomaghoz.',
  codeAlreadyUsed: 'A cége már felhasználta ezt a kódot.',
  errorGeneric: 'Nem sikerült módosítani a csomagot. Próbálja újra.',
  errorEditing:
    'A fizetés megnyitása előtt fejezze be vagy vesse el a korábbi kérdés szerkesztését.',
  errorAttachments:
    'A piszkozathoz fájlok vannak csatolva. Távolítsa el őket a fizetés megnyitása előtt. A kérdés szövege mentve van, és a fájlokat utána újra csatolhatja.',
  errorStorage:
    'A böngésző nem tudta menteni a piszkozatot. Másolja a kérdést biztonságos helyre, és engedélyezze a munkamenet-tárolást a fizetés megnyitása előtt.',
  planPerMonth: '{credits} kredit / hó',
  creditsCount: '{count} kredit',
  billingTitle: 'Számlázás',
  billingIntro: 'A cég csomagja, kreditjei és fizetési adatai.',
  planLabel: 'Csomag',
  noPlan: 'Nincs csomag',
  planPaymentFailed: 'Sikertelen fizetés',
  planEndedOn: 'Véget ért: {date}',
  planEnded: 'Véget ért',
  codeNotPercent: 'Ez a kód fix összeget von le. Itt csak százalékos kódok működnek.',
  balanceLabel: 'Fennmaradó kredit',
  nextLabel: 'Következő',
  renewsWith: '+{credits} kredit, {date}',
  switchesTo: 'Váltás erre: {credits} kredit / hó, {date}',
  endsOn: 'Véget ér: {date}',
  discountLabel: 'Kedvezmény',
  creditsNeverExpire: 'A kreditek soha nem járnak le.',
  paymentFailed:
    'Az utolsó fizetés nem sikerült. Frissítse a kártyát a Fizetési mód és számlák alatt.',
  seeUsage: 'Felhasználás megtekintése',
  changePlan: 'Csomag módosítása',
  choosePlan: 'Csomag kiválasztása',
  portal: 'Fizetési mód és számlák',
  opening: 'Megnyitás…',
  readOnly: 'A csomagot és a fizetést a cég tulajdonosa kezeli.',
  nativeOnly: 'A csomagok és a fizetés a weben kezelhetők.',
  loading: 'Számlázás betöltése…',
  retry: 'Újrapróbálás',
  unavailable:
    'A számlázás átmenetileg nem érhető el. Próbálja újra. Az előzmények és a beállítások továbbra is elérhetők.',
  portalError: 'A fizetési beállításokat nem sikerült megnyitni. Próbálja újra.',
  returnPending:
    'Várakozás a fizetés megerősítésére. A kreditek a fizetés megerősítése után jelennek meg.',
  returnDelayed: 'A fizetés megerősítése a vártnál tovább tart. Nézzen vissza kicsit később.',
  returnCanceled: 'A fizetés megszakadt. Nem történt terhelés.',
  pausedPlanTitle: 'A folytatáshoz válasszon csomagot',
  pausedPlanOwner:
    'A Poneva havi kreditcsomagokkal működik. Válasszon csomagot, hogy továbbra is kérdezhessen.',
  pausedPlanMember:
    'A cégnek nincs aktív csomagja. A cég tulajdonosa választhat egyet a Beállítások, Felhasználás részben.',
  pausedPastDue:
    'Az utolsó fizetés nem sikerült, ezért a csomag szünetel. Frissítse a kártyát a Fizetési mód és számlák alatt.',
  pausedPastDueMember:
    'A cég utolsó fizetése nem sikerült, ezért a csomag szünetel. A cég tulajdonosa frissítheti a kártyát.',
  pausedCreditsTitle: 'Elfogyott az összes kreditje',
  pausedCreditsBody:
    'Váltson most nagyobb csomagra, vagy várja meg a következő feltöltést ({date} {time}), amikor a csomag {credits} kreditet ad hozzá.',
  pausedCreditsMember:
    'Kérje meg a cég tulajdonosát, hogy váltson nagyobb csomagra, vagy várja meg a következő feltöltést ({date} {time}), amikor a csomag {credits} kreditet ad hozzá.',
  pausedCreditsNoRenewal: 'A folytatáshoz válasszon nagyobb csomagot.',
  pausedLimitTitle: 'Elérte a havi keretét',
  pausedLimitBody:
    'Felhasználta a havi {limit} kredites keretét. A keret ekkor nullázódik: {date}, vagy amikor a cég tulajdonosa megemeli.',
  pausedLimitNoDate: 'Felhasználta a havi {limit} kredites keretét. A cég tulajdonosa megemelheti.',
  upgradeNow: 'Váltás nagyobb csomagra',
  limitNotice: 'A havi {limit} kreditből {left} maradt. A keret ekkor nullázódik: {date}.',
  limitNoticeNoDate: 'A havi {limit} kreditből {left} maradt.',
};

const ro: Copy = {
  dialogTitle: 'Schimbați planul',
  dialogTitleNew: 'Alegeți un plan',
  dialogSub:
    'În fiecare lună, planul adaugă creditele sale la soldul comun al întregii echipe. Creditele nefolosite se reportează și nu expiră niciodată, și le puteți folosi cât timp un plan este activ.',
  close: 'Închideți',
  kickerUpgrade: 'Plan superior',
  kickerDowngrade: 'Plan inferior',
  currentPlanTag: 'Planul actual',
  currentPin: 'Actual',
  creditsEveryMonth: 'credite în fiecare lună',
  captionCurrent: 'Planul dumneavoastră actual.',
  captionMore: 'Cu {count} credite pe lună mai mult decât planul actual.',
  captionFewer: 'Cu {count} credite pe lună mai puțin decât planul actual.',
  captionRecent: 'Echipa dumneavoastră a folosit {count} credite în ultimele 30 de zile.',
  scaleEnd: '{price} pe lună',
  sliderLabel: 'Plan',
  sliderValue: '{credits} credite pe lună, {price} fără TVA',
  priceLabel: 'Preț pe lună',
  exclVat: 'fără TVA',
  exclVatForMonths: 'fără TVA, timp de {months} luni',
  exclVatForOneMonth: 'fără TVA, timp de 1 lună',
  exclVatUntil: 'fără TVA, până la {date}',
  perCreditLabel: 'Preț pe credit',
  volumeLabel: 'Reducere de volum',
  volumeNone: 'Niciuna',
  volumeNote: 'față de prețul de {price} pe credit al celui mai mic plan',
  volumeBase: 'Cel mai mic plan stabilește prețul de bază pe credit',
  vatLabel: 'Cu TVA 25% inclus',
  perMonth: 'pe lună',
  vatReverseLabel: 'TVA',
  vatReverseValue: 'Taxare inversă',
  codeAdd: 'Adăugați un cod de reducere',
  codePlaceholder: 'Cod de reducere',
  codeApply: 'Aplicați',
  codeChecking: 'Se verifică…',
  codeNotFound: 'Nu am găsit acest cod. Verificați ortografia și încercați din nou.',
  codeRemove: 'Eliminați codul',
  codeForMonths: 'Reducere de {percent} timp de {months} luni',
  codeForOneMonth: 'Reducere de {percent} timp de 1 lună',
  codeForGood: 'Reducere de {percent} permanent',
  codeUntil: 'Reducere de {percent} până la {date}',
  codeCarries: 'Reducere de {percent} până la {date}, se transferă',
  codeCarriesForGood: 'Reducere de {percent} permanent, se transferă',
  ctaSwitch: 'Treceți la {credits} credite',
  ctaUseCode: 'Folosiți codul pentru acest plan',
  ctaCurrent: 'Planul dumneavoastră actual',
  ctaCheckout: 'Continuați la plată',
  ctaSaving: 'Se salvează…',
  ctaOpening: 'Se deschide plata…',
  sideFoot: 'Facturare lunară cu cardul',
  back: 'Toate planurile',
  timingTitle: 'Când să înceapă noul plan?',
  timingGroup: 'Când începe noul plan',
  summaryInclVat: '{price} pe lună cu TVA',
  summaryExclVat: '{price} pe lună fără TVA',
  summaryOff: 'Reducere de {percent}',
  nowTitle: 'Începeți acum',
  renewalTitle: 'La reînnoire, {date}',
  today: 'Astăzi',
  fromThenOn: 'După aceea',
  onDate: 'Pe {date}',
  codeNote: '(reducere de {percent})',
  codeNoteUntil: '(reducere de {percent} până la {date})',
  nowToday: 'Noul plan începe. Plătiți {price}{codeNote} și primiți imediat {credits}.',
  nowTodayOnTop:
    'Noul plan începe. Plătiți {price}{codeNote} și primiți imediat {credits}, pe lângă cele {balance} credite pe care le aveți ({total} în total).',
  nowThen:
    '{credits} și {price} în fiecare lună după aceea. Planul actual se încheie astăzi și nu mai este facturat.',
  nowThenUntil:
    '{credits} și {price} în fiecare lună până la {date}, apoi {fullPrice}. Planul actual se încheie astăzi și nu mai este facturat.',
  renewalToday:
    'Nu se schimbă nimic. Păstrați planul actual și cele {balance} credite și nu plătiți nimic.',
  renewalTodayPaused:
    'Nu se schimbă nimic. Păstrați planul actual și cele {balance} credite și nu plătiți nimic. Utilizarea rămâne în pauză până atunci.',
  renewalThen:
    'Noul plan începe. Plătiți {price}{codeNote} și primiți {credits} atunci, nu mai devreme.',
  footSe: 'Sumele includ TVA de 25%. Debităm cardul de pe pagina Facturare.',
  footUnknown: 'Sumele includ TVA suedez de 25%. TVA se adaugă la plată, acolo unde se aplică.',
  footReverse:
    'Sumele nu includ TVA, care se aplică prin taxare inversă. Debităm cardul de pe pagina Facturare.',
  cancel: 'Anulați',
  confirmNow: 'Plătiți {price} și schimbați',
  confirmRenewal: 'Programați trecerea la {credits} credite',
  paying: 'Se plătește…',
  scheduling: 'Se programează…',
  toastSwitched: 'Noul plan este activ. Au fost adăugate {credits} credite la sold.',
  toastScheduled: 'Noul plan începe pe {date}. Până atunci nu se schimbă nimic.',
  toastCode: 'Reducerea de {percent} se aplică de la {date}, timp de {months} luni.',
  toastCodeOneMonth: 'Reducerea de {percent} se aplică de la {date}, timp de 1 lună.',
  toastCodeForGood: 'Reducerea de {percent} se aplică de la {date}.',
  errorDeclined:
    'Cardul dumneavoastră a fost refuzat. Actualizați-l în Metodă de plată și facturi, apoi încercați din nou.',
  errorDiscountActive: 'O reducere este deja activă. Se transferă la noul plan.',
  errorComplimentary:
    'Planul dumneavoastră este gratuit cu un cod, așa că un plan nou poate începe doar la reînnoire.',
  errorCodeNotAccepted: 'Acest cod nu poate fi folosit pentru acest plan.',
  codeAlreadyUsed: 'Compania dumneavoastră a folosit deja acest cod.',
  errorGeneric: 'Nu am putut schimba planul. Încercați din nou.',
  errorEditing:
    'Finalizați sau anulați editarea întrebării anterioare înainte de a deschide plata.',
  errorAttachments:
    'Ciorna are fișiere atașate. Eliminați-le înainte de a deschide plata. Textul întrebării este salvat și puteți atașa din nou fișierele după aceea.',
  errorStorage:
    'Browserul nu a putut salva ciorna. Copiați întrebarea într-un loc sigur și permiteți stocarea sesiunii înainte de a deschide plata.',
  planPerMonth: '{credits} credite / lună',
  creditsCount: '{count} credite',
  billingTitle: 'Facturare',
  billingIntro: 'Planul, creditele și plățile companiei dumneavoastră.',
  planLabel: 'Plan',
  noPlan: 'Niciun plan',
  planPaymentFailed: 'Plata a eșuat',
  planEndedOn: 'S-a încheiat pe {date}',
  planEnded: 'Încheiat',
  codeNotPercent: 'Acest cod scade o sumă fixă. Aici funcționează doar codurile procentuale.',
  balanceLabel: 'Credite rămase',
  nextLabel: 'Urmează',
  renewsWith: '+{credits} credite pe {date}',
  switchesTo: 'Trece la {credits} credite / lună pe {date}',
  endsOn: 'Se încheie pe {date}',
  discountLabel: 'Reducere',
  creditsNeverExpire: 'Creditele nu expiră niciodată.',
  paymentFailed: 'Ultima plată nu a reușit. Actualizați cardul în Metodă de plată și facturi.',
  seeUsage: 'Vedeți utilizarea',
  changePlan: 'Schimbați planul',
  choosePlan: 'Alegeți un plan',
  portal: 'Metodă de plată și facturi',
  opening: 'Se deschide…',
  readOnly: 'Proprietarul companiei gestionează planul și plățile.',
  nativeOnly: 'Planurile și plățile se gestionează pe web.',
  loading: 'Se încarcă facturarea…',
  retry: 'Încercați din nou',
  unavailable:
    'Facturarea este temporar indisponibilă. Încercați din nou. Istoricul și setările sunt în continuare disponibile.',
  portalError: 'Setările de plată nu au putut fi deschise. Încercați din nou.',
  returnPending: 'Se așteaptă confirmarea plății. Creditele apar după ce plata este confirmată.',
  returnDelayed:
    'Confirmarea plății durează mai mult decât de obicei. Verificați din nou în curând.',
  returnCanceled: 'Plata a fost anulată. Nu s-a debitat nimic.',
  pausedPlanTitle: 'Alegeți un plan pentru a continua',
  pausedPlanOwner:
    'Poneva funcționează cu planuri lunare de credite. Alegeți un plan pentru a continua să puneți întrebări.',
  pausedPlanMember:
    'Compania nu are un plan activ. Proprietarul companiei alege unul în Setări, la Utilizare.',
  pausedPastDue:
    'Ultima plată nu a reușit, așa că planul este în pauză. Actualizați cardul în Metodă de plată și facturi.',
  pausedPastDueMember:
    'Ultima plată a companiei nu a reușit, așa că planul este în pauză. Proprietarul companiei poate actualiza cardul.',
  pausedCreditsTitle: 'Ați folosit toate creditele',
  pausedCreditsBody:
    'Treceți acum la un plan superior sau așteptați până la {date}, ora {time}, când planul adaugă {credits} credite.',
  pausedCreditsMember:
    'Cereți proprietarului companiei să treacă la un plan superior sau așteptați până la {date}, ora {time}, când planul adaugă {credits} credite.',
  pausedCreditsNoRenewal: 'Alegeți un plan mai mare pentru a continua.',
  pausedLimitTitle: 'Ați atins limita lunară',
  pausedLimitBody:
    'Ați folosit limita lunară de {limit} credite. Aceasta se resetează pe {date} sau când proprietarul companiei o mărește.',
  pausedLimitNoDate:
    'Ați folosit limita lunară de {limit} credite. Proprietarul companiei o poate mări.',
  upgradeNow: 'Treceți la un plan superior',
  limitNotice: 'Mai aveți {left} din cele {limit} credite lunare. Limita se resetează pe {date}.',
  limitNoticeNoDate: 'Mai aveți {left} din cele {limit} credite lunare.',
};

const ga: Copy = {
  dialogTitle: 'Athraigh an plean',
  dialogTitleNew: 'Roghnaigh plean',
  dialogSub:
    'Gach mí, cuireann do phlean a chuid creidmheasanna leis an iarmhéid a roinneann an fhoireann ar fad. Coinnítear creidmheasanna nár úsáideadh agus ní théann siad in éag riamh, agus is féidir leat iad a úsáid fad is atá plean gníomhach.',
  close: 'Dún',
  kickerUpgrade: 'Uasghrádú',
  kickerDowngrade: 'Íosghrádú',
  currentPlanTag: 'Plean reatha',
  currentPin: 'Reatha',
  creditsEveryMonth: 'creidmheas gach mí',
  captionCurrent: 'Do phlean reatha.',
  captionMore: '{count} creidmheas níos mó gach mí ná do phlean reatha.',
  captionFewer: '{count} creidmheas níos lú gach mí ná do phlean reatha.',
  captionRecent: 'D’úsáid d’fhoireann {count} creidmheas le 30 lá anuas.',
  scaleEnd: '{price} sa mhí',
  sliderLabel: 'Plean',
  sliderValue: '{credits} creidmheas sa mhí, {price} gan CBL',
  priceLabel: 'Praghas sa mhí',
  exclVat: 'gan CBL',
  exclVatForMonths: 'gan CBL, ar feadh {months} mí',
  exclVatForOneMonth: 'gan CBL, ar feadh mí amháin',
  exclVatUntil: 'gan CBL, go dtí {date}',
  perCreditLabel: 'Praghas in aghaidh an chreidmheasa',
  volumeLabel: 'Lascaine mórchainníochta',
  volumeNone: 'Ceann ar bith',
  volumeNote: 'i gcomparáid le {price} in aghaidh an chreidmheasa ar an bplean is lú',
  volumeBase: 'Socraíonn an plean is lú an bunphraghas in aghaidh an chreidmheasa',
  vatLabel: 'CBL 25% san áireamh',
  perMonth: 'sa mhí',
  vatReverseLabel: 'CBL',
  vatReverseValue: 'Aisiompú muirir',
  codeAdd: 'Cuir cód lascaine leis',
  codePlaceholder: 'Cód lascaine',
  codeApply: 'Cuir i bhfeidhm',
  codeChecking: 'Á sheiceáil…',
  codeNotFound: 'Níor aimsíomar an cód sin. Seiceáil an litriú agus bain triail eile as.',
  codeRemove: 'Bain an cód',
  codeForMonths: '{percent} lascaine ar feadh {months} mí',
  codeForOneMonth: '{percent} lascaine ar feadh mí amháin',
  codeForGood: '{percent} lascaine go buan',
  codeUntil: '{percent} lascaine go dtí {date}',
  codeCarries: '{percent} lascaine go dtí {date}, aistrítear é',
  codeCarriesForGood: '{percent} lascaine go buan, aistrítear é',
  ctaSwitch: 'Athraigh go {credits} creidmheas',
  ctaUseCode: 'Úsáid an cód ar an bplean seo',
  ctaCurrent: 'Do phlean reatha',
  ctaCheckout: 'Ar aghaidh chuig an íocaíocht',
  ctaSaving: 'Á shábháil…',
  ctaOpening: 'An íocaíocht á hoscailt…',
  sideFoot: 'Billeáil mhíosúil le cárta',
  back: 'Gach plean',
  timingTitle: 'Cathain ba chóir don phlean nua tosú?',
  timingGroup: 'Nuair a thosaíonn an plean nua',
  summaryInclVat: '{price} sa mhí, CBL san áireamh',
  summaryExclVat: '{price} sa mhí, gan CBL',
  summaryOff: '{percent} lascaine',
  nowTitle: 'Tosaigh anois',
  renewalTitle: 'Ag an athnuachan, {date}',
  today: 'Inniu',
  fromThenOn: 'Ina dhiaidh sin',
  onDate: 'Ar {date}',
  codeNote: '({percent} lascaine)',
  codeNoteUntil: '({percent} lascaine go dtí {date})',
  nowToday:
    'Tosaíonn an plean nua. Íocann tú {price}{codeNote} agus faigheann tú {credits} láithreach.',
  nowTodayOnTop:
    'Tosaíonn an plean nua. Íocann tú {price}{codeNote} agus faigheann tú {credits} láithreach, sa bhreis ar an {balance} atá agat ({total} san iomlán).',
  nowThen:
    '{credits} agus {price} gach mí ina dhiaidh sin. Críochnaíonn do phlean reatha inniu agus ní ghearrfar táille air arís.',
  nowThenUntil:
    '{credits} agus {price} gach mí go dtí {date}, ansin {fullPrice}. Críochnaíonn do phlean reatha inniu agus ní ghearrfar táille air arís.',
  renewalToday:
    'Ní athraíonn aon rud. Coinníonn tú do phlean reatha agus do {balance} creidmheas, agus ní íocann tú faic.',
  renewalTodayPaused:
    'Ní athraíonn aon rud. Coinníonn tú do phlean reatha agus do {balance} creidmheas, agus ní íocann tú faic. Fanann an úsáid ar sos go dtí sin.',
  renewalThen:
    'Tosaíonn an plean nua. Íocann tú {price}{codeNote} agus faigheann tú {credits} an uair sin, ní roimhe.',
  footSe: 'Tá CBL 25% san áireamh sna suimeanna. Gearraimid an cárta ar do leathanach Billeála.',
  footUnknown:
    'Tá CBL na Sualainne 25% san áireamh sna suimeanna. Cuirtear CBL leis ag an tseiceáil amach nuair is infheidhme.',
  footReverse:
    'Níl CBL san áireamh sna suimeanna, mar go mbaineann aisiompú muirir leis. Gearraimid an cárta ar do leathanach Billeála.',
  cancel: 'Cealaigh',
  confirmNow: 'Íoc {price} agus athraigh',
  confirmRenewal: 'Sceidealaigh an t-athrú go {credits} creidmheas',
  paying: 'Ag íoc…',
  scheduling: 'Á sceidealú…',
  toastSwitched: 'Tá do phlean nua gníomhach. Cuireadh {credits} creidmheas le d’iarmhéid.',
  toastScheduled: 'Tosóidh do phlean nua ar {date}. Ní athraíonn aon rud go dtí sin.',
  toastCode: 'Tá {percent} lascaine i bhfeidhm ó {date}, ar feadh {months} mí.',
  toastCodeOneMonth: 'Tá {percent} lascaine i bhfeidhm ó {date}, ar feadh mí amháin.',
  toastCodeForGood: 'Tá {percent} lascaine i bhfeidhm ó {date}.',
  errorDeclined:
    'Diúltaíodh do chárta. Nuashonraigh é faoi Modh íocaíochta agus sonraisc, agus bain triail eile as.',
  errorDiscountActive: 'Tá lascaine gníomhach cheana. Aistrítear chuig an bplean nua é.',
  errorComplimentary:
    'Tá do phlean saor in aisce le cód, mar sin ní féidir le plean nua tosú ach ag an athnuachan.',
  errorCodeNotAccepted: 'Ní féidir an cód sin a úsáid don phlean seo.',
  codeAlreadyUsed: 'D’úsáid do chuideachta an cód seo cheana.',
  errorGeneric: 'Níorbh fhéidir linn an plean a athrú. Bain triail eile as.',
  errorEditing:
    'Críochnaigh nó cealaigh an eagarthóireacht ar do cheist roimhe seo sula n-osclaíonn tú an íocaíocht.',
  errorAttachments:
    'Tá comhaid ceangailte le do dhréacht. Bain iad sula n-osclaíonn tú an íocaíocht. Sábháiltear téacs do cheiste, agus is féidir leat na comhaid a cheangal arís ina dhiaidh sin.',
  errorStorage:
    'Níorbh fhéidir le do bhrabhsálaí do dhréacht a shábháil. Cóipeáil do cheist áit éigin sábháilte agus ceadaigh stóráil seisiúin sula n-osclaíonn tú an íocaíocht.',
  planPerMonth: '{credits} creidmheas / mí',
  creditsCount: '{count} creidmheas',
  billingTitle: 'Billeáil',
  billingIntro: 'Plean, creidmheasanna agus íocaíocht do chuideachta.',
  planLabel: 'Plean',
  noPlan: 'Gan phlean',
  planPaymentFailed: 'Theip ar an íocaíocht',
  planEndedOn: 'Críochnaithe ar {date}',
  planEnded: 'Críochnaithe',
  codeNotPercent: 'Baineann an cód seo suim sheasta. Ní oibríonn ach cóid chéatadáin anseo.',
  balanceLabel: 'Creidmheasanna fágtha',
  nextLabel: 'Ag teacht',
  renewsWith: '+{credits} creidmheas ar {date}',
  switchesTo: 'Athraíonn go {credits} creidmheas / mí ar {date}',
  endsOn: 'Críochnaíonn ar {date}',
  discountLabel: 'Lascaine',
  creditsNeverExpire: 'Ní théann creidmheasanna in éag riamh.',
  paymentFailed:
    'Níor éirigh le d’íocaíocht dheireanach. Nuashonraigh an cárta faoi Modh íocaíochta agus sonraisc.',
  seeUsage: 'Féach ar an úsáid',
  changePlan: 'Athraigh an plean',
  choosePlan: 'Roghnaigh plean',
  portal: 'Modh íocaíochta agus sonraisc',
  opening: 'Á oscailt…',
  readOnly: 'Is é úinéir na cuideachta a bhainistíonn an plean agus an íocaíocht.',
  nativeOnly: 'Bainistítear pleananna agus íocaíocht ar an ngréasán.',
  loading: 'Billeáil á lódáil…',
  retry: 'Bain triail eile as',
  unavailable:
    'Níl an bhilleáil ar fáil faoi láthair. Bain triail eile as. Tá do stair agus do shocruithe fós ar fáil.',
  portalError: 'Níorbh fhéidir na socruithe íocaíochta a oscailt. Bain triail eile as.',
  returnPending:
    'Ag fanacht le deimhniú íocaíochta. Feicfear do chreidmheasanna nuair a dheimhneofar an íocaíocht.',
  returnDelayed:
    'Tá deimhniú na híocaíochta ag tógáil níos faide ná mar a bhíothas ag súil. Seiceáil arís gan mhoill.',
  returnCanceled: 'Cealaíodh an tseiceáil amach. Níor gearradh aon rud.',
  pausedPlanTitle: 'Roghnaigh plean chun leanúint ar aghaidh',
  pausedPlanOwner:
    'Oibríonn Poneva ar phleananna creidmheasa míosúla. Roghnaigh plean chun leanúint ort ag cur ceisteanna.',
  pausedPlanMember:
    'Níl plean gníomhach ag do chuideachta. Roghnaíonn úinéir na cuideachta ceann i Socruithe faoi Úsáid.',
  pausedPastDue:
    'Níor éirigh le d’íocaíocht dheireanach, mar sin tá an plean ar sos. Nuashonraigh an cárta faoi Modh íocaíochta agus sonraisc.',
  pausedPastDueMember:
    'Níor éirigh le híocaíocht dheireanach na cuideachta, mar sin tá an plean ar sos. Is féidir le húinéir na cuideachta an cárta a nuashonrú.',
  pausedCreditsTitle: 'D’úsáid tú do chreidmheasanna ar fad',
  pausedCreditsBody:
    'Uasghrádaigh anois, nó fan go dtí {date} ag {time} nuair a chuireann do phlean {credits} creidmheas leis.',
  pausedCreditsMember:
    'Iarr ar úinéir na cuideachta uasghrádú, nó fan go dtí {date} ag {time} nuair a chuireann do phlean {credits} creidmheas leis.',
  pausedCreditsNoRenewal: 'Roghnaigh plean níos mó chun leanúint ar aghaidh.',
  pausedLimitTitle: 'Tá do theorainn mhíosúil bainte amach agat',
  pausedLimitBody:
    'D’úsáid tú do theorainn mhíosúil de {limit} creidmheas. Athshocraítear í ar {date} nó nuair a ardaíonn úinéir na cuideachta í.',
  pausedLimitNoDate:
    'D’úsáid tú do theorainn mhíosúil de {limit} creidmheas. Is féidir le húinéir na cuideachta í a ardú.',
  upgradeNow: 'Uasghrádaigh anois',
  limitNotice:
    '{left} de do {limit} creidmheas míosúil fágtha. Athshocraítear do theorainn ar {date}.',
  limitNoticeNoDate: '{left} de do {limit} creidmheas míosúil fágtha.',
};

const mt: Copy = {
  dialogTitle: 'Ibdel il-pjan',
  dialogTitleNew: 'Agħżel pjan',
  dialogSub:
    'Kull xahar il-pjan tiegħek iżid il-krediti tiegħu mal-bilanċ li jaqsam it-tim kollu. Il-krediti mhux użati jinżammu u qatt ma jiskadu, u tista’ tużahom sakemm ikun hemm pjan attiv.',
  close: 'Agħlaq',
  kickerUpgrade: 'Pjan ogħla',
  kickerDowngrade: 'Pjan aktar baxx',
  currentPlanTag: 'Pjan attwali',
  currentPin: 'Attwali',
  creditsEveryMonth: 'krediti kull xahar',
  captionCurrent: 'Il-pjan attwali tiegħek.',
  captionMore: '{count} krediti aktar kull xahar mill-pjan attwali tiegħek.',
  captionFewer: '{count} krediti inqas kull xahar mill-pjan attwali tiegħek.',
  captionRecent: 'It-tim tiegħek uża {count} krediti fl-aħħar 30 jum.',
  scaleEnd: '{price} fix-xahar',
  sliderLabel: 'Pjan',
  sliderValue: '{credits} krediti fix-xahar, {price} mingħajr VAT',
  priceLabel: 'Prezz fix-xahar',
  exclVat: 'mingħajr VAT',
  exclVatForMonths: 'mingħajr VAT, għal {months} xhur',
  exclVatForOneMonth: 'mingħajr VAT, għal xahar',
  exclVatUntil: 'mingħajr VAT, sa {date}',
  perCreditLabel: 'Prezz għal kull kreditu',
  volumeLabel: 'Skont fuq il-volum',
  volumeNone: 'Xejn',
  volumeNote: 'meta mqabbel ma’ {price} għal kull kreditu tal-iżgħar pjan',
  volumeBase: 'L-iżgħar pjan jistabbilixxi l-prezz bażiku għal kull kreditu',
  vatLabel: 'Inkluż 25% VAT',
  perMonth: 'fix-xahar',
  vatReverseLabel: 'VAT',
  vatReverseValue: 'Ħlas b’lura (reverse charge)',
  codeAdd: 'Żid kodiċi ta’ skont',
  codePlaceholder: 'Kodiċi ta’ skont',
  codeApply: 'Applika',
  codeChecking: 'Qed niċċekkjaw…',
  codeNotFound: 'Ma sibniex dak il-kodiċi. Iċċekkja l-ortografija u erġa’ pprova.',
  codeRemove: 'Neħħi l-kodiċi',
  codeForMonths: '{percent} skont għal {months} xhur',
  codeForOneMonth: '{percent} skont għal xahar',
  codeForGood: '{percent} skont għal dejjem',
  codeUntil: '{percent} skont sa {date}',
  codeCarries: '{percent} skont sa {date}, jibqa’ japplika',
  codeCarriesForGood: '{percent} skont għal dejjem, jibqa’ japplika',
  ctaSwitch: 'Aqleb għal {credits} krediti',
  ctaUseCode: 'Uża l-kodiċi fuq dan il-pjan',
  ctaCurrent: 'Il-pjan attwali tiegħek',
  ctaCheckout: 'Kompli għall-ħlas',
  ctaSaving: 'Qed nissejvjaw…',
  ctaOpening: 'Qed niftħu l-ħlas…',
  sideFoot: 'Imħallas kull xahar bil-kard',
  back: 'Il-pjanijiet kollha',
  timingTitle: 'Meta għandu jibda l-pjan il-ġdid?',
  timingGroup: 'Meta jibda l-pjan il-ġdid',
  summaryInclVat: '{price} fix-xahar inkluż VAT',
  summaryExclVat: '{price} fix-xahar mingħajr VAT',
  summaryOff: '{percent} skont',
  nowTitle: 'Ibda issa',
  renewalTitle: 'Mat-tiġdid, {date}',
  today: 'Illum',
  fromThenOn: 'Minn hemm ’il quddiem',
  onDate: 'Nhar {date}',
  codeNote: '({percent} skont)',
  codeNoteUntil: '({percent} skont sa {date})',
  nowToday: 'Il-pjan il-ġdid jibda. Tħallas {price}{codeNote} u tieħu {credits} minnufih.',
  nowTodayOnTop:
    'Il-pjan il-ġdid jibda. Tħallas {price}{codeNote} u tieħu {credits} minnufih, minbarra l-{balance} li diġà għandek ({total} b’kollox).',
  nowThen:
    '{credits} u {price} kull xahar wara dan. Il-pjan attwali tiegħek jispiċċa llum u ma jiġix iċċarġjat aktar.',
  nowThenUntil:
    '{credits} u {price} kull xahar sa {date}, imbagħad {fullPrice}. Il-pjan attwali tiegħek jispiċċa llum u ma jiġix iċċarġjat aktar.',
  renewalToday:
    'Ma jinbidel xejn. Iżżomm il-pjan attwali tiegħek u l-{balance} krediti tiegħek, u ma tħallas xejn.',
  renewalTodayPaused:
    'Ma jinbidel xejn. Iżżomm il-pjan attwali tiegħek u l-{balance} krediti tiegħek, u ma tħallas xejn. L-użu jibqa’ wieqaf sa dakinhar.',
  renewalThen:
    'Il-pjan il-ġdid jibda. Tħallas {price}{codeNote} u tieħu {credits} dakinhar, mhux qabel.',
  footSe: 'L-ammonti jinkludu 25% VAT. Niċċarġjaw il-kard fil-paġna tal-Ħlas tiegħek.',
  footUnknown: 'L-ammonti jinkludu 25% VAT Żvediż. Il-VAT jiżdied waqt il-ħlas fejn japplika.',
  footReverse:
    'L-ammonti ma jinkludux VAT, li jiġi ttrattat b’reverse charge. Niċċarġjaw il-kard fil-paġna tal-Ħlas tiegħek.',
  cancel: 'Ikkanċella',
  confirmNow: 'Ħallas {price} u aqleb',
  confirmRenewal: 'Skeda l-qlib għal {credits} krediti',
  paying: 'Qed inħallsu…',
  scheduling: 'Qed nisskedaw…',
  toastSwitched: 'Il-pjan il-ġdid tiegħek huwa attiv. Żdiedu {credits} krediti mal-bilanċ tiegħek.',
  toastScheduled: 'Il-pjan il-ġdid tiegħek jibda nhar {date}. Ma jinbidel xejn sa dakinhar.',
  toastCode: '{percent} skont japplika minn {date}, għal {months} xhur.',
  toastCodeOneMonth: '{percent} skont japplika minn {date}, għal xahar.',
  toastCodeForGood: '{percent} skont japplika minn {date}.',
  errorDeclined:
    'Il-kard tiegħek ġiet miċħuda. Aġġornaha taħt Metodu ta’ ħlas u fatturi, imbagħad erġa’ pprova.',
  errorDiscountActive: 'Diġà hemm skont attiv. Jgħaddi għall-pjan il-ġdid.',
  errorComplimentary:
    'Il-pjan tiegħek huwa b’xejn b’kodiċi, għalhekk pjan ġdid jista’ jibda biss mat-tiġdid.',
  errorCodeNotAccepted: 'Dak il-kodiċi ma jistax jintuża għal dan il-pjan.',
  codeAlreadyUsed: 'Il-kumpanija tiegħek diġà użat dan il-kodiċi.',
  errorGeneric: 'Ma stajniex nibdlu l-pjan. Erġa’ pprova.',
  errorEditing:
    'Lesti jew ikkanċella l-editjar tal-mistoqsija preċedenti tiegħek qabel tiftaħ il-ħlas.',
  errorAttachments:
    'L-abbozz tiegħek għandu fajls mehmuża. Neħħihom qabel tiftaħ il-ħlas. It-test tal-mistoqsija tiegħek jinżamm, u tista’ terġa’ tehmeż il-fajls wara.',
  errorStorage:
    'Il-browser tiegħek ma setax jissejvja l-abbozz. Ikkopja l-mistoqsija tiegħek f’post sigur u ippermetti l-ħażna tas-sessjoni qabel tiftaħ il-ħlas.',
  planPerMonth: '{credits} krediti / xahar',
  creditsCount: '{count} krediti',
  billingTitle: 'Ħlas',
  billingIntro: 'Il-pjan, il-krediti u l-ħlas tal-kumpanija tiegħek.',
  planLabel: 'Pjan',
  noPlan: 'L-ebda pjan',
  planPaymentFailed: 'Il-ħlas ma rnexxiex',
  planEndedOn: 'Intemm nhar {date}',
  planEnded: 'Intemm',
  codeNotPercent: 'Dan il-kodiċi jnaqqas ammont fiss. Hawn jaħdmu biss kodiċijiet ta’ perċentwal.',
  balanceLabel: 'Krediti li fadal',
  nextLabel: 'Li jmiss',
  renewsWith: '+{credits} krediti nhar {date}',
  switchesTo: 'Jaqleb għal {credits} krediti / xahar nhar {date}',
  endsOn: 'Jispiċċa nhar {date}',
  discountLabel: 'Skont',
  creditsNeverExpire: 'Il-krediti qatt ma jiskadu.',
  paymentFailed: 'L-aħħar ħlas tiegħek ma għaddiex. Aġġorna l-kard taħt Metodu ta’ ħlas u fatturi.',
  seeUsage: 'Ara l-użu',
  changePlan: 'Ibdel il-pjan',
  choosePlan: 'Agħżel pjan',
  portal: 'Metodu ta’ ħlas u fatturi',
  opening: 'Qed jinfetaħ…',
  readOnly: 'Is-sid tal-kumpanija jimmaniġġja l-pjan u l-ħlas.',
  nativeOnly: 'Il-pjanijiet u l-ħlas jiġu mmaniġġjati fuq il-web.',
  loading: 'Qed jillowdja l-ħlas…',
  retry: 'Erġa’ pprova',
  unavailable:
    'Il-ħlas mhux disponibbli temporanjament. Erġa’ pprova. L-istorja u l-issettjar tiegħek għadhom disponibbli.',
  portalError: 'L-issettjar tal-ħlas ma setax jinfetaħ. Erġa’ pprova.',
  returnPending:
    'Qed nistennew il-konferma tal-ħlas. Il-krediti tiegħek jidhru ladarba l-ħlas jiġi kkonfermat.',
  returnDelayed:
    'Il-konferma tal-ħlas qed tieħu aktar milli mistenni. Iċċekkja mill-ġdid ftit wara.',
  returnCanceled: 'Il-ħlas ġie kkanċellat. Ma ġie ċċarġjat xejn.',
  pausedPlanTitle: 'Agħżel pjan biex tkompli',
  pausedPlanOwner:
    'Poneva jaħdem fuq pjanijiet ta’ krediti kull xahar. Agħżel pjan biex tkompli tagħmel mistoqsijiet.',
  pausedPlanMember:
    'Il-kumpanija tiegħek m’għandhiex pjan attiv. Is-sid tal-kumpanija jagħżel wieħed f’Issettjar taħt Użu.',
  pausedPastDue:
    'L-aħħar ħlas tiegħek ma għaddiex, għalhekk il-pjan huwa sospiż. Aġġorna l-kard taħt Metodu ta’ ħlas u fatturi.',
  pausedPastDueMember:
    'L-aħħar ħlas tal-kumpanija ma għaddiex, għalhekk il-pjan huwa sospiż. Is-sid tal-kumpanija jista’ jaġġorna l-kard.',
  pausedCreditsTitle: 'Użajt il-krediti kollha tiegħek',
  pausedCreditsBody:
    'Aqleb għal pjan ogħla issa, jew stenna sa {date} fil-{time} meta l-pjan tiegħek iżid {credits} krediti.',
  pausedCreditsMember:
    'Staqsi lis-sid tal-kumpanija biex jaqleb għal pjan ogħla, jew stenna sa {date} fil-{time} meta l-pjan tiegħek iżid {credits} krediti.',
  pausedCreditsNoRenewal: 'Agħżel pjan ikbar biex tkompli.',
  pausedLimitTitle: 'Wasalt għal-limitu ta’ kull xahar tiegħek',
  pausedLimitBody:
    'Użajt il-limitu ta’ kull xahar tiegħek ta’ {limit} krediti. Jerġa’ jibda nhar {date} jew meta s-sid tal-kumpanija jgħollih.',
  pausedLimitNoDate:
    'Użajt il-limitu ta’ kull xahar tiegħek ta’ {limit} krediti. Is-sid tal-kumpanija jista’ jgħollih.',
  upgradeNow: 'Aqleb għal pjan ogħla issa',
  limitNotice:
    'Fadallek {left} minn {limit} krediti ta’ kull xahar. Il-limitu tiegħek jerġa’ jibda nhar {date}.',
  limitNoticeNoDate: 'Fadallek {left} minn {limit} krediti ta’ kull xahar.',
};

const billingCopy: NamespaceCatalog<BillingCopyKey> = {
  bg,
  hr,
  cs,
  da,
  nl,
  en,
  et,
  fi,
  fr,
  de,
  el,
  hu,
  ga,
  it,
  lv,
  lt,
  mt,
  pl,
  pt,
  ro,
  sk,
  sl,
  es,
  sv,
};
export default billingCopy;
